import React, { useState, useRef } from 'react';
import { 
  ArrowLeft, 
  TrendingUp, 
  Target, 
  CheckCircle, 
  AlertTriangle, 
  Lightbulb,
  BarChart3,
  Download,
  Zap,
  Star,
  FileText,
  Quote,
  Eye,
  ThumbsUp,
  MessageCircle,
  Award,
  Play,
  ChevronUp
} from 'lucide-react';
import { toast } from 'react-hot-toast';
import AnalysisTimeline from './AnalysisTimeline';
import AdvancedAnalysisResults from './AdvancedAnalysisResults';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';


const ReviewResults = ({ data, onNewReview }) => {
  const [activeTab, setActiveTab] = useState('article');
  const [isGeneratingPDF, setIsGeneratingPDF] = useState(false);
  const reportRef = useRef(null);
  const articleRef = useRef(null);
  
  // Add comprehensive null safety checks
  if (!data || !data.review) {
    return (
      <div className="max-w-4xl mx-auto">
        <div className="card text-center">
          <div className="mb-4">
            <AlertTriangle className="w-16 h-16 text-yellow-500 mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-gray-900 mb-2">No Analysis Data Available</h2>
            <p className="text-gray-600 mb-6">
              The review data is missing or incomplete. Please try analyzing your video again.
            </p>
            <button
              onClick={onNewReview}
              className="btn-primary"
            >
              Start New Analysis
            </button>
          </div>
        </div>
      </div>
    );
  }
  
  const { review, platform, platformInfo, generatedAt } = data;
  
  // Ensure we have valid criteria data
  const criteria = review?.criteria || review?.videoAnalysis?.criteria || [];

  const getScoreColor = (score) => {
    if (score >= 8) return 'score-excellent'; // Green
    if (score >= 5) return 'score-average';   // Amber
    return 'score-poor';                      // Red
  };
  

  // Ensure we have valid criteria data before creating chart data
  const chartData = Array.isArray(criteria) ? criteria.map((criterion, index) => ({
    name: criterion?.name?.split(' ').slice(0, 2).join(' ') || `Criterion ${index + 1}`,
    score: criterion?.score || 0,
    fullName: criterion?.name || `Criterion ${index + 1}`,
    fill: getScoreColor(criterion?.score || 0) === 'score-excellent' ? '#10b981' : 
          getScoreColor(criterion?.score || 0) === 'score-average' ? '#f59e0b' : '#ef4444'
  })) : [];

  // Check if advanced analysis is available
  const isAdvancedAnalysis = review.visualAnalysis || review.contentAnalysis;
  
  
  // Generate platform-specific overview heading
  const generateOverviewHeading = () => {
    return 'Best Practices';
  };

  // AIDA Analysis Logic
  const getAIDAAnalysis = () => {
    const criteria = review.criteria || review.videoAnalysis?.criteria || [];
    
    // Attention - Hook, opening, visual impact
    const attentionCriteria = criteria.filter(c => 
      c.name.toLowerCase().includes('hook') ||
      c.name.toLowerCase().includes('opening') ||
      c.name.toLowerCase().includes('attention') ||
      c.name.toLowerCase().includes('visual') ||
      c.name.toLowerCase().includes('capture') ||
      c.name.toLowerCase().includes('grab')
    );
    
    // Interest - Engagement, curiosity, relevance
    const interestCriteria = criteria.filter(c => 
      c.name.toLowerCase().includes('engagement') ||
      c.name.toLowerCase().includes('curiosity') ||
      c.name.toLowerCase().includes('relevance') ||
      c.name.toLowerCase().includes('audience') ||
      c.name.toLowerCase().includes('connection') ||
      c.name.toLowerCase().includes('relatable')
    );
    
    // Desire - Benefits, emotions, proof, social proof
    const desireCriteria = criteria.filter(c => 
      c.name.toLowerCase().includes('benefit') ||
      c.name.toLowerCase().includes('emotion') ||
      c.name.toLowerCase().includes('proof') ||
      c.name.toLowerCase().includes('testimonial') ||
      c.name.toLowerCase().includes('transformation') ||
      c.name.toLowerCase().includes('value') ||
      c.name.toLowerCase().includes('outcome')
    );
    
    // Action - CTA, clear direction, urgency
    const actionCriteria = criteria.filter(c => 
      c.name.toLowerCase().includes('call') ||
      c.name.toLowerCase().includes('action') ||
      c.name.toLowerCase().includes('cta') ||
      c.name.toLowerCase().includes('click') ||
      c.name.toLowerCase().includes('buy') ||
      c.name.toLowerCase().includes('sign') ||
      c.name.toLowerCase().includes('download') ||
      c.name.toLowerCase().includes('urgency')
    );
    
    // Calculate scores for each AIDA element
    const attentionScore = attentionCriteria.length > 0 
      ? Math.round((attentionCriteria.reduce((sum, c) => sum + c.score, 0) / attentionCriteria.length) * 10) / 10
      : 1.0;
    
    const interestScore = interestCriteria.length > 0 
      ? Math.round((interestCriteria.reduce((sum, c) => sum + c.score, 0) / interestCriteria.length) * 10) / 10
      : 1.0;
    
    const desireScore = desireCriteria.length > 0 
      ? Math.round((desireCriteria.reduce((sum, c) => sum + c.score, 0) / desireCriteria.length) * 10) / 10
      : 1.0;
    
    const actionScore = actionCriteria.length > 0 
      ? Math.round((actionCriteria.reduce((sum, c) => sum + c.score, 0) / actionCriteria.length) * 10) / 10
      : 1.0;
    
    const overallAIDAScore = Math.round(((attentionScore + interestScore + desireScore + actionScore) / 4) * 10) / 10;
    
    return {
      attention: { score: attentionScore, criteria: attentionCriteria },
      interest: { score: interestScore, criteria: interestCriteria },
      desire: { score: desireScore, criteria: desireCriteria },
      action: { score: actionScore, criteria: actionCriteria },
      overall: overallAIDAScore
    };
  };

  // Enhanced PAS Analysis Logic
  const getPASAnalysis = () => {
    const criteria = review.criteria || review.videoAnalysis?.criteria || [];
    
    // Enhanced Problem - Pain points, challenges, struggles with comprehensive matching
    const problemCriteria = criteria.filter(c => {
      const name = c.name.toLowerCase();
      const description = (c.description || '').toLowerCase();
      return name.includes('problem') ||
             name.includes('pain') ||
             name.includes('challenge') ||
             name.includes('struggle') ||
             name.includes('issue') ||
             name.includes('difficulty') ||
             name.includes('frustration') ||
             name.includes('worry') ||
             name.includes('concern') ||
             name.includes('trouble') ||
             name.includes('obstacle') ||
             name.includes('barrier') ||
             name.includes('hurdle') ||
             name.includes('burden') ||
             name.includes('stress') ||
             name.includes('anxiety') ||
             name.includes('fear') ||
             name.includes('doubt') ||
             name.includes('uncertainty') ||
             name.includes('confusion') ||
             name.includes('overwhelm') ||
             name.includes('stuck') ||
             name.includes('limited') ||
             name.includes('restricted') ||
             name.includes('inadequate') ||
             name.includes('insufficient') ||
             name.includes('poor') ||
             name.includes('bad') ||
             name.includes('wrong') ||
             name.includes('broken') ||
             name.includes('failing') ||
             name.includes('losing') ||
             name.includes('declining') ||
             name.includes('deteriorating') ||
             name.includes('suffering') ||
             name.includes('hurting') ||
             name.includes('damaging') ||
             name.includes('harming') ||
             description.includes('problem') ||
             description.includes('pain') ||
             description.includes('challenge') ||
             description.includes('struggle') ||
             description.includes('difficulty');
    });
    
    // Enhanced Agitate - Urgency, consequences, cost of inaction with comprehensive matching
    const agitateCriteria = criteria.filter(c => {
      const name = c.name.toLowerCase();
      const description = (c.description || '').toLowerCase();
      return name.includes('urgency') ||
             name.includes('consequence') ||
             name.includes('cost') ||
             name.includes('risk') ||
             name.includes('fear') ||
             name.includes('anxiety') ||
             name.includes('pressure') ||
             name.includes('stakes') ||
             name.includes('impact') ||
             name.includes('threat') ||
             name.includes('danger') ||
             name.includes('harm') ||
             name.includes('damage') ||
             name.includes('loss') ||
             name.includes('failure') ||
             name.includes('crisis') ||
             name.includes('emergency') ||
             name.includes('critical') ||
             name.includes('urgent') ||
             name.includes('immediate') ||
             name.includes('now') ||
             name.includes('today') ||
             name.includes('deadline') ||
             name.includes('time') ||
             name.includes('limited') ||
             name.includes('running') ||
             name.includes('wasting') ||
             name.includes('missing') ||
             name.includes('losing') ||
             name.includes('regret') ||
             name.includes('disappointment') ||
             name.includes('frustration') ||
             name.includes('anger') ||
             name.includes('worry') ||
             name.includes('stress') ||
             name.includes('tension') ||
             name.includes('burden') ||
             name.includes('weight') ||
             name.includes('pressure') ||
             name.includes('strain') ||
             name.includes('exhaustion') ||
             name.includes('burnout') ||
             name.includes('overwhelm') ||
             name.includes('desperate') ||
             name.includes('hopeless') ||
             name.includes('helpless') ||
             name.includes('trapped') ||
             name.includes('stuck') ||
             description.includes('urgency') ||
             description.includes('consequence') ||
             description.includes('risk') ||
             description.includes('fear') ||
             description.includes('pressure');
    });
    
    // Enhanced Solution - Product/service as the fix, benefits, results with comprehensive matching
    const solutionCriteria = criteria.filter(c => {
      const name = c.name.toLowerCase();
      const description = (c.description || '').toLowerCase();
      return name.includes('solution') ||
             name.includes('fix') ||
             name.includes('benefit') ||
             name.includes('result') ||
             name.includes('outcome') ||
             name.includes('improvement') ||
             name.includes('transformation') ||
             name.includes('success') ||
             name.includes('relief') ||
             name.includes('help') ||
             name.includes('resolve') ||
             name.includes('cure') ||
             name.includes('heal') ||
             name.includes('repair') ||
             name.includes('solve') ||
             name.includes('address') ||
             name.includes('tackle') ||
             name.includes('overcome') ||
             name.includes('beat') ||
             name.includes('defeat') ||
             name.includes('conquer') ||
             name.includes('master') ||
             name.includes('excel') ||
             name.includes('thrive') ||
             name.includes('prosper') ||
             name.includes('flourish') ||
             name.includes('achieve') ||
             name.includes('accomplish') ||
             name.includes('attain') ||
             name.includes('reach') ||
             name.includes('gain') ||
             name.includes('earn') ||
             name.includes('win') ||
             name.includes('victory') ||
             name.includes('triumph') ||
             name.includes('breakthrough') ||
             name.includes('discovery') ||
             name.includes('innovation') ||
             name.includes('advancement') ||
             name.includes('progress') ||
             name.includes('development') ||
             name.includes('growth') ||
             name.includes('evolution') ||
             name.includes('upgrade') ||
             name.includes('enhancement') ||
             name.includes('optimization') ||
             name.includes('efficiency') ||
             name.includes('productivity') ||
             name.includes('performance') ||
             name.includes('quality') ||
             name.includes('excellence') ||
             name.includes('superiority') ||
             name.includes('advantage') ||
             name.includes('edge') ||
             name.includes('power') ||
             name.includes('strength') ||
             name.includes('capability') ||
             name.includes('ability') ||
             name.includes('skill') ||
             name.includes('talent') ||
             name.includes('expertise') ||
             name.includes('knowledge') ||
             name.includes('wisdom') ||
             name.includes('insight') ||
             name.includes('understanding') ||
             name.includes('clarity') ||
             name.includes('confidence') ||
             name.includes('certainty') ||
             name.includes('security') ||
             name.includes('safety') ||
             name.includes('protection') ||
             name.includes('defense') ||
             name.includes('shield') ||
             name.includes('guard') ||
             name.includes('support') ||
             name.includes('assistance') ||
             name.includes('aid') ||
             name.includes('guidance') ||
             name.includes('direction') ||
             name.includes('leadership') ||
             name.includes('mentorship') ||
             name.includes('coaching') ||
             name.includes('training') ||
             name.includes('education') ||
             name.includes('learning') ||
             name.includes('teaching') ||
             name.includes('instruction') ||
             name.includes('method') ||
             name.includes('approach') ||
             name.includes('strategy') ||
             name.includes('technique') ||
             name.includes('system') ||
             name.includes('process') ||
             name.includes('procedure') ||
             name.includes('formula') ||
             name.includes('recipe') ||
             name.includes('blueprint') ||
             name.includes('plan') ||
             name.includes('program') ||
             name.includes('course') ||
             name.includes('tool') ||
             name.includes('resource') ||
             name.includes('platform') ||
             name.includes('service') ||
             name.includes('product') ||
             name.includes('device') ||
             name.includes('technology') ||
             name.includes('software') ||
             name.includes('app') ||
             description.includes('solution') ||
             description.includes('benefit') ||
             description.includes('result') ||
             description.includes('improvement') ||
             description.includes('help') ||
             description.includes('fix');
    });
    
    // Enhanced scoring with weighted calculations and bonus points
    const problemScore = problemCriteria.length > 0 
      ? Math.min(10, Math.round((problemCriteria.reduce((sum, c) => sum + c.score, 0) / problemCriteria.length) * 10) / 10 + 
        (problemCriteria.length >= 3 ? 1 : 0)) // Bonus for having multiple problem elements
      : 1.0;
    
    const agitateScore = agitateCriteria.length > 0 
      ? Math.min(10, Math.round((agitateCriteria.reduce((sum, c) => sum + c.score, 0) / agitateCriteria.length) * 10) / 10 + 
        (agitateCriteria.length >= 3 ? 1 : 0)) // Bonus for having multiple agitation elements
      : 1.0;
    
    const solutionScore = solutionCriteria.length > 0 
      ? Math.min(10, Math.round((solutionCriteria.reduce((sum, c) => sum + c.score, 0) / solutionCriteria.length) * 10) / 10 + 
        (solutionCriteria.length >= 3 ? 1 : 0)) // Bonus for having multiple solution elements
      : 1.0;
    
    // Enhanced overall score calculation with persuasion flow consideration
    const overallPASScore = Math.round(((problemScore + agitateScore + solutionScore) / 3) * 10) / 10;
    
    // Calculate persuasion flow quality (how well the problem leads to agitation and then to solution)
    const persuasionFlow = problemScore >= 6 && agitateScore >= 6 && solutionScore >= 6 ? 'excellent' :
                         problemScore >= 4 && agitateScore >= 4 && solutionScore >= 4 ? 'good' : 'needs_improvement';
    
    // Determine PAS balance (how well the elements work together)
    const scoreVariance = Math.max(problemScore, agitateScore, solutionScore) - 
                         Math.min(problemScore, agitateScore, solutionScore);
    const isBalanced = scoreVariance <= 3; // Balanced if scores are within 3 points of each other
    
    // Check for strong emotional escalation from problem to agitation
    const hasStrongEscalation = agitateScore > problemScore && agitateScore >= 6;
    
    // Check for clear solution positioning
    const hasClearSolution = solutionScore >= 7 && (solutionCriteria.some(c => 
      c.name.toLowerCase().includes('product') || 
      c.name.toLowerCase().includes('service') || 
      c.name.toLowerCase().includes('solution')
    ));
    
    return {
      problem: { 
        score: problemScore, 
        criteria: problemCriteria,
        strength: problemScore >= 8 ? 'strong' : problemScore >= 5 ? 'moderate' : 'weak',
        description: problemScore >= 8 ? 'Excellent problem identification and pain point presentation' :
                    problemScore >= 5 ? 'Adequate problem awareness with room for improvement' :
                    'Needs stronger problem identification and pain point development'
      },
      agitate: { 
        score: agitateScore, 
        criteria: agitateCriteria,
        strength: agitateScore >= 8 ? 'strong' : agitateScore >= 5 ? 'moderate' : 'weak',
        description: agitateScore >= 8 ? 'Strong emotional agitation and urgency creation' :
                    agitateScore >= 5 ? 'Some urgency and consequences shown' :
                    'Needs stronger agitation and urgency development'
      },
      solution: { 
        score: solutionScore, 
        criteria: solutionCriteria,
        strength: solutionScore >= 8 ? 'strong' : solutionScore >= 5 ? 'moderate' : 'weak',
        description: solutionScore >= 8 ? 'Clear solution positioning with strong benefits and results' :
                    solutionScore >= 5 ? 'Adequate solution elements with some benefit focus' :
                    'Needs stronger solution positioning and benefit demonstration'
      },
      overall: overallPASScore,
      persuasionFlow,
      isBalanced,
      hasStrongEscalation,
      hasClearSolution,
      totalCriteria: problemCriteria.length + agitateCriteria.length + solutionCriteria.length,
      recommendations: generatePASRecommendations(problemScore, agitateScore, solutionScore, isBalanced, persuasionFlow, hasStrongEscalation, hasClearSolution)
    };
  };

  // Generate specific recommendations for PAS structure
  const generatePASRecommendations = (problemScore, agitateScore, solutionScore, isBalanced, persuasionFlow, hasStrongEscalation, hasClearSolution) => {
    const recommendations = [];
    
    if (problemScore < 6) {
      recommendations.push({
        type: 'problem',
        priority: 'high',
        title: 'Strengthen Problem Identification',
        description: 'Create a compelling and relatable problem that your target audience can immediately connect with.',
        tips: [
          'Identify specific pain points your audience experiences daily',
          'Show the emotional impact of the problem',
          'Use relatable scenarios and real-world examples',
          'Highlight how the problem affects their life/work',
          'Make the problem personal and urgent'
        ]
      });
    }
    
    if (agitateScore < 6) {
      recommendations.push({
        type: 'agitate',
        priority: 'high',
        title: 'Intensify Agitation and Urgency',
        description: 'Amplify the consequences and costs of not solving the problem to create compelling urgency.',
        tips: [
          'Show what happens if they don\'t take action',
          'Highlight the cost of inaction (time, money, opportunity)',
          'Create emotional urgency and pressure',
          'Use fear of missing out (FOMO) techniques',
          'Show the negative consequences escalating'
        ]
      });
    }
    
    if (solutionScore < 6) {
      recommendations.push({
        type: 'solution',
        priority: 'high',
        title: 'Enhance Solution Positioning',
        description: 'Clearly position your product or service as the natural and effective solution to the problem.',
        tips: [
          'Show how your product/service directly solves the problem',
          'Demonstrate specific benefits and results',
          'Provide proof and evidence of effectiveness',
          'Position as the logical next step',
          'Create a sense of relief and hope'
        ]
      });
    }
    
    if (!hasStrongEscalation) {
      recommendations.push({
        type: 'escalation',
        priority: 'medium',
        title: 'Create Stronger Emotional Escalation',
        description: 'Build emotional intensity from problem identification to agitation to create compelling urgency.',
        tips: [
          'Make agitation stronger than problem presentation',
          'Use emotional language and imagery',
          'Create tension and discomfort',
          'Show problems getting worse over time',
          'Build to a crisis point that demands action'
        ]
      });
    }
    
    if (!hasClearSolution) {
      recommendations.push({
        type: 'solution_clarity',
        priority: 'medium',
        title: 'Improve Solution Clarity',
        description: 'Make your product or service the clear and obvious solution to the agitated problem.',
        tips: [
          'Directly connect solution to the problem',
          'Show immediate relief and benefits',
          'Demonstrate ease of implementation',
          'Provide clear next steps',
          'Build confidence in the solution'
        ]
      });
    }
    
    if (!isBalanced) {
      recommendations.push({
        type: 'balance',
        priority: 'medium',
        title: 'Balance PAS Elements',
        description: 'Ensure all three PAS elements (Problem, Agitate, Solution) are well-developed and work together.',
        tips: [
          'Strengthen the weakest element',
          'Ensure smooth progression between elements',
          'Maintain consistent emotional tone',
          'Create logical flow from problem to solution',
          'Balance emotional and logical appeals'
        ]
      });
    }
    
    if (persuasionFlow === 'needs_improvement') {
      recommendations.push({
        type: 'flow',
        priority: 'medium',
        title: 'Improve Persuasion Flow',
        description: 'Create a logical and emotionally compelling progression from problem through agitation to solution.',
        tips: [
          'Ensure each element builds on the previous one',
          'Create cause-and-effect relationships',
          'Maintain emotional momentum throughout',
          'Show clear progression and resolution',
          'Build to a satisfying conclusion'
        ]
      });
    }
    
    return recommendations;
  };

  // Enhanced BAB Analysis Logic
  const getBABAnalysis = () => {
    const criteria = review.criteria || review.videoAnalysis?.criteria || [];
    
    // Enhanced Before - Struggles, inefficiencies, current state with comprehensive matching
    const beforeCriteria = criteria.filter(c => {
      const name = c.name.toLowerCase();
      const description = (c.description || '').toLowerCase();
      return name.includes('before') ||
             name.includes('struggle') ||
             name.includes('inefficiency') ||
             name.includes('current') ||
             name.includes('existing') ||
             name.includes('old') ||
             name.includes('past') ||
             name.includes('problem') ||
             name.includes('pain') ||
             name.includes('difficulty') ||
             name.includes('frustration') ||
             name.includes('challenge') ||
             name.includes('issue') ||
             name.includes('concern') ||
             name.includes('worry') ||
             name.includes('anxiety') ||
             name.includes('stress') ||
             name.includes('burden') ||
             name.includes('hardship') ||
             name.includes('struggle') ||
             name.includes('suffering') ||
             name.includes('discomfort') ||
             name.includes('inconvenience') ||
             name.includes('limitation') ||
             name.includes('barrier') ||
             name.includes('obstacle') ||
             name.includes('weakness') ||
             name.includes('flaw') ||
             name.includes('drawback') ||
             name.includes('downside') ||
             name.includes('negative') ||
             name.includes('bad') ||
             name.includes('poor') ||
             name.includes('lacking') ||
             name.includes('missing') ||
             description.includes('struggle') ||
             description.includes('problem') ||
             description.includes('difficulty') ||
             description.includes('pain') ||
             description.includes('current') ||
             description.includes('before');
    });
    
    // Enhanced After - Benefits, relief, happiness, desired state with comprehensive matching
    const afterCriteria = criteria.filter(c => {
      const name = c.name.toLowerCase();
      const description = (c.description || '').toLowerCase();
      return name.includes('after') ||
             name.includes('benefit') ||
             name.includes('relief') ||
             name.includes('happiness') ||
             name.includes('success') ||
             name.includes('improvement') ||
             name.includes('transformation') ||
             name.includes('result') ||
             name.includes('outcome') ||
             name.includes('future') ||
             name.includes('new') ||
             name.includes('better') ||
             name.includes('enhanced') ||
             name.includes('improved') ||
             name.includes('upgraded') ||
             name.includes('optimized') ||
             name.includes('enhanced') ||
             name.includes('superior') ||
             name.includes('advanced') ||
             name.includes('perfect') ||
             name.includes('ideal') ||
             name.includes('desired') ||
             name.includes('wanted') ||
             name.includes('goal') ||
             name.includes('target') ||
             name.includes('achievement') ||
             name.includes('accomplishment') ||
             name.includes('victory') ||
             name.includes('triumph') ||
             name.includes('breakthrough') ||
             name.includes('solution') ||
             name.includes('resolution') ||
             name.includes('healing') ||
             name.includes('recovery') ||
             name.includes('growth') ||
             name.includes('progress') ||
             name.includes('development') ||
             name.includes('evolution') ||
             name.includes('change') ||
             name.includes('metamorphosis') ||
             description.includes('benefit') ||
             description.includes('improvement') ||
             description.includes('transformation') ||
             description.includes('success') ||
             description.includes('after') ||
             description.includes('future');
    });
    
    // Enhanced Bridge - Product/service as the connection, how it works with comprehensive matching
    const bridgeCriteria = criteria.filter(c => {
      const name = c.name.toLowerCase();
      const description = (c.description || '').toLowerCase();
      return name.includes('bridge') ||
             name.includes('solution') ||
             name.includes('product') ||
             name.includes('service') ||
             name.includes('method') ||
             name.includes('process') ||
             name.includes('how') ||
             name.includes('way') ||
             name.includes('step') ||
             name.includes('journey') ||
             name.includes('path') ||
             name.includes('connection') ||
             name.includes('link') ||
             name.includes('tool') ||
             name.includes('technology') ||
             name.includes('system') ||
             name.includes('approach') ||
             name.includes('strategy') ||
             name.includes('technique') ||
             name.includes('formula') ||
             name.includes('recipe') ||
             name.includes('guide') ||
             name.includes('manual') ||
             name.includes('program') ||
             name.includes('course') ||
             name.includes('training') ||
             name.includes('education') ||
             name.includes('learning') ||
             name.includes('teaching') ||
             name.includes('instruction') ||
             name.includes('coaching') ||
             name.includes('mentoring') ||
             name.includes('support') ||
             name.includes('help') ||
             name.includes('assistance') ||
             name.includes('aid') ||
             name.includes('enabler') ||
             name.includes('facilitator') ||
             name.includes('catalyst') ||
             name.includes('driver') ||
             name.includes('mechanism') ||
             name.includes('means') ||
             name.includes('medium') ||
             name.includes('channel') ||
             name.includes('platform') ||
             name.includes('software') ||
             name.includes('app') ||
             name.includes('device') ||
             name.includes('equipment') ||
             description.includes('solution') ||
             description.includes('product') ||
             description.includes('service') ||
             description.includes('method') ||
             description.includes('bridge') ||
             description.includes('how');
    });
    
    // Enhanced scoring with weighted calculations and bonus points
    const beforeScore = beforeCriteria.length > 0 
      ? Math.min(10, Math.round((beforeCriteria.reduce((sum, c) => sum + c.score, 0) / beforeCriteria.length) * 10) / 10 + 
        (beforeCriteria.length >= 3 ? 1 : 0)) // Bonus for having multiple "before" elements
      : 1.0;
    
    const afterScore = afterCriteria.length > 0 
      ? Math.min(10, Math.round((afterCriteria.reduce((sum, c) => sum + c.score, 0) / afterCriteria.length) * 10) / 10 + 
        (afterCriteria.length >= 3 ? 1 : 0)) // Bonus for having multiple "after" elements
      : 1.0;
    
    const bridgeScore = bridgeCriteria.length > 0 
      ? Math.min(10, Math.round((bridgeCriteria.reduce((sum, c) => sum + c.score, 0) / bridgeCriteria.length) * 10) / 10 + 
        (bridgeCriteria.length >= 2 ? 1 : 0)) // Bonus for having multiple "bridge" elements
      : 1.0;
    
    // Enhanced overall score calculation with transformation flow consideration
    const overallBABScore = Math.round(((beforeScore + afterScore + bridgeScore) / 3) * 10) / 10;
    
    // Calculate transformation flow quality
    const transformationFlow = beforeScore < afterScore && bridgeScore >= Math.min(beforeScore, afterScore) ? 'excellent' :
                              beforeScore <= afterScore && bridgeScore >= 5 ? 'good' : 'needs_improvement';
    
    // Determine BAB balance (how well the elements work together)
    const scoreVariance = Math.max(beforeScore, afterScore, bridgeScore) - 
                         Math.min(beforeScore, afterScore, bridgeScore);
    const isBalanced = scoreVariance <= 3; // Balanced if scores are within 3 points of each other
    
    // Check for strong contrast between before and after
    const hasStrongContrast = Math.abs(beforeScore - afterScore) >= 2;
    
    return {
      before: { 
        score: beforeScore, 
        criteria: beforeCriteria,
        strength: beforeScore >= 8 ? 'strong' : beforeScore >= 5 ? 'moderate' : 'weak',
        description: beforeScore >= 8 ? 'Excellent depiction of current struggles and pain points' :
                    beforeScore >= 5 ? 'Adequate current state awareness with room for improvement' :
                    'Needs stronger "before" state description and struggle identification'
      },
      after: { 
        score: afterScore, 
        criteria: afterCriteria,
        strength: afterScore >= 8 ? 'strong' : afterScore >= 5 ? 'moderate' : 'weak',
        description: afterScore >= 8 ? 'Compelling vision of benefits and desired transformation' :
                    afterScore >= 5 ? 'Some future benefits and improvements shown' :
                    'Needs stronger "after" state visualization and benefit presentation'
      },
      bridge: { 
        score: bridgeScore, 
        criteria: bridgeCriteria,
        strength: bridgeScore >= 8 ? 'strong' : bridgeScore >= 5 ? 'moderate' : 'weak',
        description: bridgeScore >= 8 ? 'Clear product/service positioning as the transformation bridge' :
                    bridgeScore >= 5 ? 'Adequate bridge elements with some product focus' :
                    'Needs stronger product/service positioning as the transformation enabler'
      },
      overall: overallBABScore,
      transformationFlow,
      isBalanced,
      hasStrongContrast,
      totalCriteria: beforeCriteria.length + afterCriteria.length + bridgeCriteria.length,
      recommendations: generateBABRecommendations(beforeScore, afterScore, bridgeScore, isBalanced, transformationFlow, hasStrongContrast)
    };
  };

  // Generate specific recommendations for BAB structure
  const generateBABRecommendations = (beforeScore, afterScore, bridgeScore, isBalanced, transformationFlow, hasStrongContrast) => {
    const recommendations = [];
    
    if (beforeScore < 6) {
      recommendations.push({
        type: 'before',
        priority: 'high',
        title: 'Strengthen the "Before" State',
        description: 'Create a compelling depiction of current struggles, pain points, and inefficiencies that your audience can relate to.',
        tips: [
          'Identify specific pain points your audience experiences',
          'Show the emotional impact of current problems',
          'Use relatable scenarios and situations',
          'Highlight inefficiencies and frustrations',
          'Create urgency around solving these problems'
        ]
      });
    }
    
    if (afterScore < 6) {
      recommendations.push({
        type: 'after',
        priority: 'high',
        title: 'Enhance the "After" Vision',
        description: 'Paint a compelling picture of the desired future state with clear benefits and transformations.',
        tips: [
          'Show specific benefits and improvements',
          'Visualize the transformation clearly',
          'Highlight emotional rewards and satisfaction',
          'Demonstrate success stories or outcomes',
          'Create aspirational and achievable goals'
        ]
      });
    }
    
    if (bridgeScore < 6) {
      recommendations.push({
        type: 'bridge',
        priority: 'high',
        title: 'Position Your Product/Service as the Bridge',
        description: 'Clearly establish your product or service as the connection between current problems and desired outcomes.',
        tips: [
          'Show how your product/service solves the problem',
          'Demonstrate the transformation process',
          'Explain the method or approach clearly',
          'Position as the logical solution',
          'Build trust and credibility'
        ]
      });
    }
    
    if (!hasStrongContrast) {
      recommendations.push({
        type: 'contrast',
        priority: 'medium',
        title: 'Create Stronger Before-After Contrast',
        description: 'Enhance the contrast between current struggles and desired outcomes to make the transformation more compelling.',
        tips: [
          'Amplify the "before" pain points',
          'Enhance the "after" benefits',
          'Create stark visual or emotional contrasts',
          'Use before/after comparisons',
          'Show dramatic improvements'
        ]
      });
    }
    
    if (!isBalanced) {
      recommendations.push({
        type: 'balance',
        priority: 'medium',
        title: 'Balance BAB Elements',
        description: 'Ensure all three BAB elements (Before, After, Bridge) are well-developed and balanced.',
        tips: [
          'Strengthen the weakest element',
          'Ensure equal attention to all three parts',
          'Create smooth transitions between elements',
          'Maintain consistent messaging',
          'Balance emotional and logical appeals'
        ]
      });
    }
    
    if (transformationFlow === 'needs_improvement') {
      recommendations.push({
        type: 'flow',
        priority: 'medium',
        title: 'Improve Transformation Flow',
        description: 'Create a logical progression from current problems through your solution to desired outcomes.',
        tips: [
          'Ensure "after" is better than "before"',
          'Make the bridge connect naturally',
          'Create logical cause-and-effect relationships',
          'Show clear transformation pathway',
          'Build momentum toward the solution'
        ]
      });
    }
    
    return recommendations;
  };

  // HRR Analysis Logic
  const getHRRAnalysis = () => {
    const criteria = review.criteria || review.videoAnalysis?.criteria || [];
    
    // Hook - Attention-grabbing opening, first 3-5 seconds
    const hookCriteria = criteria.filter(c => 
      c.name.toLowerCase().includes('hook') ||
      c.name.toLowerCase().includes('opening') ||
      c.name.toLowerCase().includes('attention') ||
      c.name.toLowerCase().includes('grab') ||
      c.name.toLowerCase().includes('capture') ||
      c.name.toLowerCase().includes('start') ||
      c.name.toLowerCase().includes('beginning') ||
      c.name.toLowerCase().includes('first') ||
      c.name.toLowerCase().includes('intro') ||
      c.name.toLowerCase().includes('lead')
    );
    
    // Relate - Familiar scenarios, shared struggles, connection
    const relateCriteria = criteria.filter(c => 
      c.name.toLowerCase().includes('relate') ||
      c.name.toLowerCase().includes('familiar') ||
      c.name.toLowerCase().includes('shared') ||
      c.name.toLowerCase().includes('struggle') ||
      c.name.toLowerCase().includes('connection') ||
      c.name.toLowerCase().includes('audience') ||
      c.name.toLowerCase().includes('relatable') ||
      c.name.toLowerCase().includes('common') ||
      c.name.toLowerCase().includes('universal') ||
      c.name.toLowerCase().includes('everyday') ||
      c.name.toLowerCase().includes('experience') ||
      c.name.toLowerCase().includes('situation')
    );
    
    // Resolution - Solution, payoff, outcome, result
    const resolutionCriteria = criteria.filter(c => 
      c.name.toLowerCase().includes('resolution') ||
      c.name.toLowerCase().includes('solution') ||
      c.name.toLowerCase().includes('payoff') ||
      c.name.toLowerCase().includes('outcome') ||
      c.name.toLowerCase().includes('result') ||
      c.name.toLowerCase().includes('ending') ||
      c.name.toLowerCase().includes('conclusion') ||
      c.name.toLowerCase().includes('final') ||
      c.name.toLowerCase().includes('deliver') ||
      c.name.toLowerCase().includes('answer') ||
      c.name.toLowerCase().includes('fix') ||
      c.name.toLowerCase().includes('resolve')
    );
    
    // Calculate scores for each HRR element
    const hookScore = hookCriteria.length > 0 
      ? Math.round((hookCriteria.reduce((sum, c) => sum + c.score, 0) / hookCriteria.length) * 10) / 10
      : 1.0;
    
    const relateScore = relateCriteria.length > 0 
      ? Math.round((relateCriteria.reduce((sum, c) => sum + c.score, 0) / relateCriteria.length) * 10) / 10
      : 1.0;
    
    const resolutionScore = resolutionCriteria.length > 0 
      ? Math.round((resolutionCriteria.reduce((sum, c) => sum + c.score, 0) / resolutionCriteria.length) * 10) / 10
      : 1.0;
    
    const overallHRRScore = Math.round(((hookScore + relateScore + resolutionScore) / 3) * 10) / 10;
    
    return {
      hook: { score: hookScore, criteria: hookCriteria },
      relate: { score: relateScore, criteria: relateCriteria },
      resolution: { score: resolutionScore, criteria: resolutionCriteria },
      overall: overallHRRScore
    };
  };

  // Story Arc Analysis Logic - Updated to use structured data
  const getStoryArcAnalysis = () => {
    const storyArcData = review.storyArcAnalysis || review.videoAnalysis?.storyArcAnalysis || {};
    const criteria = review.criteria || review.videoAnalysis?.criteria || [];
    
    // Helper function to extract score from analysis text
    const extractScoreFromText = (text) => {
      if (!text) return 5.0; // Default score if no analysis
      
      // Look for score patterns like "8/10", "7.5/10", "excellent (9)", "good (7)", etc.
      const scoreMatch = text.match(/(\d+(?:\.\d+)?)\/10|(\d+(?:\.\d+)?)\/10|excellent\s*\((\d+(?:\.\d+)?)\)|good\s*\((\d+(?:\.\d+)?)\)|poor\s*\((\d+(?:\.\d+)?)\)|weak\s*\((\d+(?:\.\d+)?)\)|strong\s*\((\d+(?:\.\d+)?)\)/i);
      if (scoreMatch) {
        return parseFloat(scoreMatch[1] || scoreMatch[2] || scoreMatch[3] || scoreMatch[4] || scoreMatch[5] || scoreMatch[6] || scoreMatch[7]);
      }
      
      // Fallback: analyze sentiment and assign score
      const lowerText = text.toLowerCase();
      if (lowerText.includes('excellent') || lowerText.includes('outstanding') || lowerText.includes('exceptional')) return 9.0;
      if (lowerText.includes('very good') || lowerText.includes('strong') || lowerText.includes('effective')) return 8.0;
      if (lowerText.includes('good') || lowerText.includes('solid') || lowerText.includes('decent')) return 7.0;
      if (lowerText.includes('average') || lowerText.includes('adequate') || lowerText.includes('moderate')) return 6.0;
      if (lowerText.includes('weak') || lowerText.includes('poor') || lowerText.includes('lacking')) return 4.0;
      if (lowerText.includes('very poor') || lowerText.includes('terrible') || lowerText.includes('inadequate')) return 2.0;
      
      return 5.0; // Default neutral score
    };
    
    // Map structured story arc data to our 5-point structure
    const hookScore = extractScoreFromText(storyArcData.hookEffectiveness);
    const problemScore = extractScoreFromText(storyArcData.problemIdentification);
    const tensionScore = extractScoreFromText(storyArcData.tensionBuilding);
    const climaxScore = extractScoreFromText(storyArcData.climaxMoment);
    const resolutionScore = extractScoreFromText(storyArcData.resolution);
    
    // Fallback to criteria filtering if structured data is not available
    let fallbackHookScore = hookScore;
    let fallbackProblemScore = problemScore;
    let fallbackTensionScore = tensionScore;
    let fallbackClimaxScore = climaxScore;
    let fallbackResolutionScore = resolutionScore;
    
    if (!storyArcData.hookEffectiveness) {
      const hookCriteria = criteria.filter(c => 
        c.name.toLowerCase().includes('hook') ||
        c.name.toLowerCase().includes('opening') ||
        c.name.toLowerCase().includes('attention') ||
        c.name.toLowerCase().includes('grab') ||
        c.name.toLowerCase().includes('capture') ||
        c.name.toLowerCase().includes('start') ||
        c.name.toLowerCase().includes('beginning') ||
        c.name.toLowerCase().includes('first') ||
        c.name.toLowerCase().includes('intro') ||
        c.name.toLowerCase().includes('lead')
      );
      fallbackHookScore = hookCriteria.length > 0 
        ? Math.round((hookCriteria.reduce((sum, c) => sum + c.score, 0) / hookCriteria.length) * 10) / 10
        : 5.0;
    }
    
    if (!storyArcData.problemIdentification) {
      const problemCriteria = criteria.filter(c => 
        c.name.toLowerCase().includes('problem') ||
        c.name.toLowerCase().includes('conflict') ||
        c.name.toLowerCase().includes('challenge') ||
        c.name.toLowerCase().includes('pain') ||
        c.name.toLowerCase().includes('struggle') ||
        c.name.toLowerCase().includes('issue') ||
        c.name.toLowerCase().includes('obstacle') ||
        c.name.toLowerCase().includes('difficulty') ||
        c.name.toLowerCase().includes('barrier') ||
        c.name.toLowerCase().includes('tension')
      );
      fallbackProblemScore = problemCriteria.length > 0 
        ? Math.round((problemCriteria.reduce((sum, c) => sum + c.score, 0) / problemCriteria.length) * 10) / 10
        : 5.0;
    }
    
    if (!storyArcData.tensionBuilding) {
      const tensionCriteria = criteria.filter(c => 
        c.name.toLowerCase().includes('tension') ||
        c.name.toLowerCase().includes('build') ||
        c.name.toLowerCase().includes('rising') ||
        c.name.toLowerCase().includes('escalate') ||
        c.name.toLowerCase().includes('intensify') ||
        c.name.toLowerCase().includes('mounting') ||
        c.name.toLowerCase().includes('pressure') ||
        c.name.toLowerCase().includes('suspense') ||
        c.name.toLowerCase().includes('anticipation') ||
        c.name.toLowerCase().includes('excitement')
      );
      fallbackTensionScore = tensionCriteria.length > 0 
        ? Math.round((tensionCriteria.reduce((sum, c) => sum + c.score, 0) / tensionCriteria.length) * 10) / 10
        : 5.0;
    }
    
    if (!storyArcData.climaxMoment) {
      const climaxCriteria = criteria.filter(c => 
        c.name.toLowerCase().includes('climax') ||
        c.name.toLowerCase().includes('peak') ||
        c.name.toLowerCase().includes('crescendo') ||
        c.name.toLowerCase().includes('turning') ||
        c.name.toLowerCase().includes('pivot') ||
        c.name.toLowerCase().includes('revelation') ||
        c.name.toLowerCase().includes('breakthrough') ||
        c.name.toLowerCase().includes('moment') ||
        c.name.toLowerCase().includes('culmination') ||
        c.name.toLowerCase().includes('crisis')
      );
      fallbackClimaxScore = climaxCriteria.length > 0 
        ? Math.round((climaxCriteria.reduce((sum, c) => sum + c.score, 0) / climaxCriteria.length) * 10) / 10
        : 5.0;
    }
    
    if (!storyArcData.resolution) {
      const resolutionCriteria = criteria.filter(c => 
        c.name.toLowerCase().includes('resolution') ||
        c.name.toLowerCase().includes('cta') ||
        c.name.toLowerCase().includes('call') ||
        c.name.toLowerCase().includes('action') ||
        c.name.toLowerCase().includes('ending') ||
        c.name.toLowerCase().includes('conclusion') ||
        c.name.toLowerCase().includes('final') ||
        c.name.toLowerCase().includes('close') ||
        c.name.toLowerCase().includes('wrap') ||
        c.name.toLowerCase().includes('finish')
      );
      fallbackResolutionScore = resolutionCriteria.length > 0 
        ? Math.round((resolutionCriteria.reduce((sum, c) => sum + c.score, 0) / resolutionCriteria.length) * 10) / 10
        : 5.0;
    }
    
    // Use structured data scores or fallback to criteria-based scores
    const finalHookScore = storyArcData.hookEffectiveness ? hookScore : fallbackHookScore;
    const finalProblemScore = storyArcData.problemIdentification ? problemScore : fallbackProblemScore;
    const finalTensionScore = storyArcData.tensionBuilding ? tensionScore : fallbackTensionScore;
    const finalClimaxScore = storyArcData.climaxMoment ? climaxScore : fallbackClimaxScore;
    const finalResolutionScore = storyArcData.resolution ? resolutionScore : fallbackResolutionScore;
    
    const overallStoryArcScore = Math.round(((finalHookScore + finalProblemScore + finalTensionScore + finalClimaxScore + finalResolutionScore) / 5) * 10) / 10;
    
    return {
      hook: { 
        score: finalHookScore, 
        criteria: [], 
        analysis: storyArcData.hookEffectiveness || 'Hook effectiveness analysis not available'
      },
      problem: { 
        score: finalProblemScore, 
        criteria: [], 
        analysis: storyArcData.problemIdentification || 'Problem identification analysis not available'
      },
      tension: { 
        score: finalTensionScore, 
        criteria: [], 
        analysis: storyArcData.tensionBuilding || 'Tension building analysis not available'
      },
      climax: { 
        score: finalClimaxScore, 
        criteria: [], 
        analysis: storyArcData.climaxMoment || 'Climax analysis not available'
      },
      resolution: { 
        score: finalResolutionScore, 
        criteria: [], 
        analysis: storyArcData.resolution || 'Resolution analysis not available'
      },
      overall: overallStoryArcScore
    };
  };

  // Enhanced 3-Act Structure Analysis Logic
  const get3ActAnalysis = () => {
    const criteria = review.criteria || review.videoAnalysis?.criteria || [];
    
    // Enhanced Act 1 (Setup) - Character/context introduction with more comprehensive matching
    const setupCriteria = criteria.filter(c => {
      const name = c.name.toLowerCase();
      const description = (c.description || '').toLowerCase();
      return name.includes('setup') ||
             name.includes('introduction') ||
             name.includes('character') ||
             name.includes('context') ||
             name.includes('establish') ||
             name.includes('introduce') ||
             name.includes('beginning') ||
             name.includes('opening') ||
             name.includes('scene') ||
             name.includes('setting') ||
             name.includes('background') ||
             name.includes('world') ||
             name.includes('foundation') ||
             name.includes('premise') ||
             name.includes('protagonist') ||
             name.includes('audience') ||
             name.includes('viewer') ||
             name.includes('customer') ||
             name.includes('user') ||
             name.includes('persona') ||
             name.includes('identity') ||
             name.includes('brand') ||
             name.includes('company') ||
             description.includes('introduction') ||
             description.includes('setup') ||
             description.includes('character') ||
             description.includes('establish');
    });
    
    // Enhanced Act 2 (Confrontation) - Problem, struggle, obstacle with comprehensive matching
    const confrontationCriteria = criteria.filter(c => {
      const name = c.name.toLowerCase();
      const description = (c.description || '').toLowerCase();
      return name.includes('confrontation') ||
             name.includes('problem') ||
             name.includes('struggle') ||
             name.includes('obstacle') ||
             name.includes('challenge') ||
             name.includes('conflict') ||
             name.includes('tension') ||
             name.includes('crisis') ||
             name.includes('dilemma') ||
             name.includes('difficulty') ||
             name.includes('pain') ||
             name.includes('frustration') ||
             name.includes('battle') ||
             name.includes('middle') ||
             name.includes('development') ||
             name.includes('rising') ||
             name.includes('action') ||
             name.includes('complication') ||
             name.includes('escalation') ||
             name.includes('struggle') ||
             name.includes('resistance') ||
             name.includes('barrier') ||
             name.includes('hurdle') ||
             name.includes('issue') ||
             name.includes('concern') ||
             name.includes('worry') ||
             name.includes('anxiety') ||
             name.includes('stress') ||
             name.includes('burden') ||
             description.includes('problem') ||
             description.includes('conflict') ||
             description.includes('struggle') ||
             description.includes('challenge');
    });
    
    // Enhanced Act 3 (Resolution) - Solution, product/service as enabler with comprehensive matching
    const resolutionCriteria = criteria.filter(c => {
      const name = c.name.toLowerCase();
      const description = (c.description || '').toLowerCase();
      return name.includes('resolution') ||
             name.includes('solution') ||
             name.includes('enabler') ||
             name.includes('product') ||
             name.includes('service') ||
             name.includes('ending') ||
             name.includes('conclusion') ||
             name.includes('outcome') ||
             name.includes('result') ||
             name.includes('answer') ||
             name.includes('fix') ||
             name.includes('resolve') ||
             name.includes('deliver') ||
             name.includes('payoff') ||
             name.includes('climax') ||
             name.includes('peak') ||
             name.includes('turning') ||
             name.includes('transformation') ||
             name.includes('change') ||
             name.includes('improvement') ||
             name.includes('benefit') ||
             name.includes('advantage') ||
             name.includes('value') ||
             name.includes('offer') ||
             name.includes('proposal') ||
             name.includes('call') ||
             name.includes('action') ||
             name.includes('cta') ||
             name.includes('button') ||
             name.includes('purchase') ||
             name.includes('buy') ||
             name.includes('subscribe') ||
             name.includes('sign') ||
             name.includes('download') ||
             description.includes('solution') ||
             description.includes('resolution') ||
             description.includes('product') ||
             description.includes('service') ||
             description.includes('benefit');
    });
    
    // Enhanced scoring with weighted calculations and bonus points
    const setupScore = setupCriteria.length > 0 
      ? Math.min(10, Math.round((setupCriteria.reduce((sum, c) => sum + c.score, 0) / setupCriteria.length) * 10) / 10 + 
        (setupCriteria.length >= 3 ? 1 : 0)) // Bonus for having multiple setup elements
      : 1.0;
    
    const confrontationScore = confrontationCriteria.length > 0 
      ? Math.min(10, Math.round((confrontationCriteria.reduce((sum, c) => sum + c.score, 0) / confrontationCriteria.length) * 10) / 10 + 
        (confrontationCriteria.length >= 3 ? 1 : 0)) // Bonus for having multiple conflict elements
      : 1.0;
    
    const resolutionScore = resolutionCriteria.length > 0 
      ? Math.min(10, Math.round((resolutionCriteria.reduce((sum, c) => sum + c.score, 0) / resolutionCriteria.length) * 10) / 10 + 
        (resolutionCriteria.length >= 2 ? 1 : 0)) // Bonus for having multiple resolution elements
      : 1.0;
    
    // Enhanced overall score calculation with structure balance consideration
    const overall3ActScore = Math.round(((setupScore + confrontationScore + resolutionScore) / 3) * 10) / 10;
    
    // Calculate structure balance (how well the acts flow together)
    const scoreVariance = Math.max(setupScore, confrontationScore, resolutionScore) - 
                         Math.min(setupScore, confrontationScore, resolutionScore);
    const isBalanced = scoreVariance <= 3; // Balanced if scores are within 3 points of each other
    
    // Determine narrative flow quality
    const narrativeFlow = setupScore > confrontationScore && confrontationScore < resolutionScore ? 'excellent' :
                         setupScore <= confrontationScore && confrontationScore >= resolutionScore ? 'needs_improvement' : 'good';
    
    return {
      setup: { 
        score: setupScore, 
        criteria: setupCriteria,
        strength: setupScore >= 8 ? 'strong' : setupScore >= 5 ? 'moderate' : 'weak',
        description: setupScore >= 8 ? 'Excellent character and context introduction' :
                    setupScore >= 5 ? 'Adequate setup with room for improvement' :
                    'Needs stronger character and context establishment'
      },
      confrontation: { 
        score: confrontationScore, 
        criteria: confrontationCriteria,
        strength: confrontationScore >= 8 ? 'strong' : confrontationScore >= 5 ? 'moderate' : 'weak',
        description: confrontationScore >= 8 ? 'Clear and compelling conflict presentation' :
                    confrontationScore >= 5 ? 'Some conflict elements present' :
                    'Needs stronger problem/conflict development'
      },
      resolution: { 
        score: resolutionScore, 
        criteria: resolutionCriteria,
        strength: resolutionScore >= 8 ? 'strong' : resolutionScore >= 5 ? 'moderate' : 'weak',
        description: resolutionScore >= 8 ? 'Strong resolution with clear product positioning' :
                    resolutionScore >= 5 ? 'Adequate resolution with some product focus' :
                    'Needs stronger resolution and product/service positioning'
      },
      overall: overall3ActScore,
      balance: isBalanced,
      flow: narrativeFlow,
      totalCriteria: setupCriteria.length + confrontationCriteria.length + resolutionCriteria.length,
      recommendations: generate3ActRecommendations(setupScore, confrontationScore, resolutionScore, isBalanced, narrativeFlow)
    };
  };

  // Generate specific recommendations for 3-Act structure
  const generate3ActRecommendations = (setupScore, confrontationScore, resolutionScore, isBalanced, narrativeFlow) => {
    const recommendations = [];
    
    if (setupScore < 6) {
      recommendations.push({
        type: 'setup',
        priority: 'high',
        title: 'Strengthen Act 1: Setup',
        description: 'Focus on introducing your main character (customer) and establishing the context clearly in the first 25% of your video.',
        tips: [
          'Introduce the customer persona within the first 3 seconds',
          'Establish the setting and current situation',
          'Create emotional connection with the audience',
          'Set clear expectations for what follows'
        ]
      });
    }
    
    if (confrontationScore < 6) {
      recommendations.push({
        type: 'confrontation',
        priority: 'high',
        title: 'Develop Act 2: Confrontation',
        description: 'Create compelling conflict or problem that your target audience can relate to in the middle 50% of your video.',
        tips: [
          'Present a specific problem your audience faces',
          'Show the emotional impact of the problem',
          'Create tension and urgency',
          'Make the problem relatable and specific'
        ]
      });
    }
    
    if (resolutionScore < 6) {
      recommendations.push({
        type: 'resolution',
        priority: 'high',
        title: 'Enhance Act 3: Resolution',
        description: 'Position your product or service as the clear solution and enabler of transformation in the final 25% of your video.',
        tips: [
          'Present your product/service as the natural solution',
          'Show the transformation or outcome clearly',
          'Include a strong call-to-action',
          'Demonstrate the value proposition'
        ]
      });
    }
    
    if (!isBalanced) {
      recommendations.push({
        type: 'balance',
        priority: 'medium',
        title: 'Improve Act Balance',
        description: 'Ensure all three acts have similar strength and development.',
        tips: [
          'Review the weakest act and strengthen it',
          'Ensure smooth transitions between acts',
          'Maintain consistent pacing throughout',
          'Balance emotional intensity across acts'
        ]
      });
    }
    
    if (narrativeFlow === 'needs_improvement') {
      recommendations.push({
        type: 'flow',
        priority: 'medium',
        title: 'Improve Narrative Flow',
        description: 'Create better progression from setup through conflict to resolution.',
        tips: [
          'Ensure setup leads naturally to confrontation',
          'Make confrontation build toward resolution',
          'Create logical cause-and-effect relationships',
          'Maintain story momentum throughout'
        ]
      });
    }
    
    return recommendations;
  };

  // Hero's Journey Analysis Logic
  const getHeroJourneyAnalysis = () => {
    const criteria = review.criteria || review.videoAnalysis?.criteria || [];
    
    // Hero - The customer as the hero, not the brand
    const heroCriteria = criteria.filter(c => 
      c.name.toLowerCase().includes('hero') ||
      c.name.toLowerCase().includes('customer') ||
      c.name.toLowerCase().includes('audience') ||
      c.name.toLowerCase().includes('character') ||
      c.name.toLowerCase().includes('protagonist') ||
      c.name.toLowerCase().includes('person') ||
      c.name.toLowerCase().includes('individual') ||
      c.name.toLowerCase().includes('user') ||
      c.name.toLowerCase().includes('viewer') ||
      c.name.toLowerCase().includes('relatable') ||
      c.name.toLowerCase().includes('relatable')
    );
    
    // Call to Adventure - Challenge or need they face
    const callToAdventureCriteria = criteria.filter(c => 
      c.name.toLowerCase().includes('call') ||
      c.name.toLowerCase().includes('adventure') ||
      c.name.toLowerCase().includes('challenge') ||
      c.name.toLowerCase().includes('need') ||
      c.name.toLowerCase().includes('problem') ||
      c.name.toLowerCase().includes('struggle') ||
      c.name.toLowerCase().includes('opportunity') ||
      c.name.toLowerCase().includes('journey') ||
      c.name.toLowerCase().includes('quest') ||
      c.name.toLowerCase().includes('mission') ||
      c.name.toLowerCase().includes('goal') ||
      c.name.toLowerCase().includes('desire')
    );
    
    // Mentor/Guide - Brand appears as guide or helper
    const mentorCriteria = criteria.filter(c => 
      c.name.toLowerCase().includes('mentor') ||
      c.name.toLowerCase().includes('guide') ||
      c.name.toLowerCase().includes('helper') ||
      c.name.toLowerCase().includes('support') ||
      c.name.toLowerCase().includes('assistance') ||
      c.name.toLowerCase().includes('solution') ||
      c.name.toLowerCase().includes('product') ||
      c.name.toLowerCase().includes('service') ||
      c.name.toLowerCase().includes('brand') ||
      c.name.toLowerCase().includes('company') ||
      c.name.toLowerCase().includes('tool') ||
      c.name.toLowerCase().includes('resource')
    );
    
    // Transformation - Customer overcomes challenge with product/service
    const transformationCriteria = criteria.filter(c => 
      c.name.toLowerCase().includes('transformation') ||
      c.name.toLowerCase().includes('change') ||
      c.name.toLowerCase().includes('overcome') ||
      c.name.toLowerCase().includes('success') ||
      c.name.toLowerCase().includes('improvement') ||
      c.name.toLowerCase().includes('growth') ||
      c.name.toLowerCase().includes('development') ||
      c.name.toLowerCase().includes('progress') ||
      c.name.toLowerCase().includes('breakthrough') ||
      c.name.toLowerCase().includes('victory') ||
      c.name.toLowerCase().includes('achievement') ||
      c.name.toLowerCase().includes('result')
    );
    
    // Return with Reward - New better version of life shown
    const returnRewardCriteria = criteria.filter(c => 
      c.name.toLowerCase().includes('return') ||
      c.name.toLowerCase().includes('reward') ||
      c.name.toLowerCase().includes('benefit') ||
      c.name.toLowerCase().includes('outcome') ||
      c.name.toLowerCase().includes('result') ||
      c.name.toLowerCase().includes('better') ||
      c.name.toLowerCase().includes('improved') ||
      c.name.toLowerCase().includes('enhanced') ||
      c.name.toLowerCase().includes('new') ||
      c.name.toLowerCase().includes('future') ||
      c.name.toLowerCase().includes('life') ||
      c.name.toLowerCase().includes('lifestyle') ||
      c.name.toLowerCase().includes('happiness') ||
      c.name.toLowerCase().includes('success')
    );
    
    // Calculate scores for each Hero's Journey element
    const heroScore = heroCriteria.length > 0 
      ? Math.round((heroCriteria.reduce((sum, c) => sum + c.score, 0) / heroCriteria.length) * 10) / 10
      : 1.0;
    
    const callToAdventureScore = callToAdventureCriteria.length > 0 
      ? Math.round((callToAdventureCriteria.reduce((sum, c) => sum + c.score, 0) / callToAdventureCriteria.length) * 10) / 10
      : 1.0;
    
    const mentorScore = mentorCriteria.length > 0 
      ? Math.round((mentorCriteria.reduce((sum, c) => sum + c.score, 0) / mentorCriteria.length) * 10) / 10
      : 1.0;
    
    const transformationScore = transformationCriteria.length > 0 
      ? Math.round((transformationCriteria.reduce((sum, c) => sum + c.score, 0) / transformationCriteria.length) * 10) / 10
      : 1.0;
    
    const returnRewardScore = returnRewardCriteria.length > 0 
      ? Math.round((returnRewardCriteria.reduce((sum, c) => sum + c.score, 0) / returnRewardCriteria.length) * 10) / 10
      : 1.0;
    
    const overallHeroScore = Math.round(((heroScore + callToAdventureScore + mentorScore + transformationScore + returnRewardScore) / 5) * 10) / 10;
    
    return {
      hero: { score: heroScore, criteria: heroCriteria },
      callToAdventure: { score: callToAdventureScore, criteria: callToAdventureCriteria },
      mentor: { score: mentorScore, criteria: mentorCriteria },
      transformation: { score: transformationScore, criteria: transformationCriteria },
      returnReward: { score: returnRewardScore, criteria: returnRewardCriteria },
      overall: overallHeroScore
    };
  };

  // Calculate overall score as average of best practices score and top performing framework score
  const bestPracticesScore = criteria.length > 0 
    ? Math.round((criteria.reduce((sum, criterion) => sum + criterion.score, 0) / criteria.length) * 10) / 10
    : 5; // Default fallback score
  
  // Get all framework scores to find the top performer
  const storyArcAnalysis = getStoryArcAnalysis();
  const aidaAnalysis = getAIDAAnalysis();
  const pasAnalysis = getPASAnalysis();
  const babAnalysis = getBABAnalysis();
  const hrrAnalysis = getHRRAnalysis();
  const threeActAnalysis = get3ActAnalysis();
  const heroAnalysis = getHeroJourneyAnalysis();
  
  const frameworks = [
    { name: 'Story Arc', score: storyArcAnalysis.overall },
    { name: 'AIDA', score: aidaAnalysis.overall },
    { name: 'PAS', score: pasAnalysis.overall },
    { name: 'BAB', score: babAnalysis.overall },
    { name: 'HRR', score: hrrAnalysis.overall },
    { name: '3-Act', score: threeActAnalysis.overall },
    { name: "Hero's Journey", score: heroAnalysis.overall }
  ];
  
  const topFramework = frameworks.reduce((best, current) => 
    current.score > best.score ? current : best
  );
  
  const overallScore = Math.round(((bestPracticesScore + topFramework.score) / 2) * 10) / 10;

  // Dynamic AI Content Generation Functions for Advanced Reports
  const generateAdvancedHeadline = () => {
    const score = overallScore;
    const storyArcAnalysis = getStoryArcAnalysis();
    const aidaAnalysis = getAIDAAnalysis();
    const pasAnalysis = getPASAnalysis();
    const babAnalysis = getBABAnalysis();
    const hrrAnalysis = getHRRAnalysis();
    const threeActAnalysis = get3ActAnalysis();
    
    const frameworks = [
      { name: 'Story Arc', score: storyArcAnalysis.overall, focus: 'Narrative Flow' },
      { name: 'AIDA', score: aidaAnalysis.overall, focus: 'Customer Journey' },
      { name: 'PAS', score: pasAnalysis.overall, focus: 'Problem-Solution Fit' },
      { name: 'BAB', score: babAnalysis.overall, focus: 'Transformation Story' },
      { name: 'HRR', score: hrrAnalysis.overall, focus: 'Emotional Connection' },
      { name: '3-Act', score: threeActAnalysis.overall, focus: 'Story Structure' }
    ];
    
    const topFramework = frameworks.reduce((best, current) => 
      current.score > best.score ? current : best
    );
    
    if (score >= 8) {
      if (topFramework.score >= 8) {
        return `The ${topFramework.name} Mastery: How This Video Dominates Through ${topFramework.focus}`;
      } else if (storyArcAnalysis.hook.score >= 8) {
        return "Hook Perfection: The 3-Second Rule That Transforms Viewers Into Customers";
      } else if (aidaAnalysis.overall >= 8) {
        return "Attention to Action: The Complete Customer Journey That Converts";
      } else {
        return "Multi-Framework Excellence: The Strategic Video That Wins on Every Level";
      }
    } else if (score >= 6) {
      return `Unlocking ${topFramework.focus}: Your Path to Video Marketing Dominance`;
    } else if (score >= 4) {
      return `From ${topFramework.name} Insights to Action: Your Video Transformation Blueprint`;
    } else {
      return "The Multi-Framework Roadmap: Transforming Your Video Into a Conversion Machine";
    }
  };

  const generateAdvancedLeadParagraph = () => {
    const score = overallScore;
    const storyArcAnalysis = getStoryArcAnalysis();
    const frameworks = [
      { name: 'AIDA', score: getAIDAAnalysis().overall },
      { name: 'PAS', score: getPASAnalysis().overall },
      { name: 'BAB', score: getBABAnalysis().overall },
      { name: 'HRR', score: getHRRAnalysis().overall },
      { name: '3-Act', score: get3ActAnalysis().overall },
      { name: 'Story Arc', score: storyArcAnalysis.overall }
    ];
    
    const topFramework = frameworks.reduce((best, current) => 
      current.score > best.score ? current : best
    );
    
    const strongFrameworks = frameworks.filter(f => f.score >= 7).length;
    const weakFrameworks = frameworks.filter(f => f.score < 5).length;
    
    if (score >= 8) {
      return `In the sophisticated world of multi-framework video marketing, exceptional content doesn't just perform well—it dominates across multiple strategic dimensions. This comprehensive analysis reveals a video that achieves ${overallScore}/10 overall performance, with ${strongFrameworks} frameworks scoring 7+ and your ${topFramework.name} framework leading at ${topFramework.score.toFixed(1)}/10. Through advanced storytelling and conversion optimization, this content demonstrates mastery of the psychological triggers that drive viewer behavior from first impression to final action.`;
    } else if (score >= 6) {
      return `Multi-framework analysis reveals the strategic depth of your video marketing approach. With an overall score of ${overallScore}/10, this content shows strong performance across ${strongFrameworks} key frameworks, with ${topFramework.name} emerging as your strongest asset at ${topFramework.score.toFixed(1)}/10. While the foundation is solid, strategic refinements in ${weakFrameworks > 0 ? `${weakFrameworks} underperforming framework${weakFrameworks > 1 ? 's' : ''}` : 'key areas'} can unlock exponential growth in viewer engagement and conversion rates.`;
    } else {
      return `Advanced multi-framework analysis uncovers the strategic opportunities hidden within your video content. With an overall score of ${overallScore}/10, this analysis reveals ${topFramework.name} as your strongest framework at ${topFramework.score.toFixed(1)}/10, while identifying critical improvement areas across ${weakFrameworks} frameworks. By systematically addressing these strategic gaps, you can transform this content from underperforming to outstanding, leveraging proven psychological triggers to maximize viewer engagement and drive measurable business results.`;
    }
  };

  const generateFrameworkInsights = (frameworkName, score, analysis) => {
    const insights = {
      AIDA: {
        high: `Exceptional customer journey mastery. Your AIDA framework execution demonstrates sophisticated understanding of the attention-to-action funnel, with strategic touchpoints that guide viewers seamlessly through each stage.`,
        medium: `Solid AIDA fundamentals with strategic optimization opportunities. The customer journey shows competent execution with specific areas for enhancement to maximize conversion potential.`,
        low: `AIDA framework reveals critical gaps in the customer journey. Strategic restructuring of the attention-to-action flow can dramatically improve viewer engagement and conversion rates.`
      },
      PAS: {
        high: `Masterful problem-agitation-solution execution. Your content demonstrates exceptional understanding of pain-point psychology, creating compelling urgency that drives viewer action.`,
        medium: `Competent PAS implementation with enhancement potential. The problem-solution narrative shows solid fundamentals with opportunities to strengthen the agitation phase for maximum impact.`,
        low: `PAS framework analysis reveals significant opportunities for improvement. Strengthening the problem identification and solution presentation can transform viewer response and conversion rates.`
      },
      BAB: {
        high: `Outstanding transformation storytelling. Your before-after-bridge narrative creates compelling emotional journeys that resonate with viewers and drive meaningful engagement.`,
        medium: `Effective transformation narrative with optimization potential. The BAB structure demonstrates solid storytelling principles with opportunities to amplify emotional impact.`,
        low: `BAB framework shows opportunities for transformation narrative enhancement. Strengthening the before-after contrast and bridge elements can significantly improve viewer connection and action.`
      },
      HRR: {
        high: `Exceptional empathy-first engagement. Your hook-relate-resolve approach creates deep emotional connections that foster trust and drive viewer loyalty and action.`,
        medium: `Solid emotional engagement with relationship-building potential. The HRR framework shows competent empathy-driven storytelling with opportunities to deepen viewer connections.`,
        low: `HRR analysis reveals opportunities for emotional engagement enhancement. Strengthening the relate phase can transform viewer perception from passive observer to active participant.`
      },
      '3-Act': {
        high: `Classical storytelling excellence. Your three-act structure demonstrates masterful narrative pacing and dramatic tension that keeps viewers engaged from opening to resolution.`,
        medium: `Competent three-act execution with dramatic enhancement potential. The story structure shows solid fundamentals with opportunities to strengthen tension and pacing.`,
        low: `Three-act analysis reveals structural optimization opportunities. Enhancing the dramatic arc and pacing can significantly improve viewer retention and emotional impact.`
      },
      'Story Arc': {
        high: `Sophisticated narrative mastery. Your five-point story arc demonstrates exceptional storytelling craft with perfect pacing through hook, problem, tension, climax, and resolution.`,
        medium: `Solid narrative structure with enhancement opportunities. The story arc shows competent storytelling with specific areas for dramatic tension and pacing improvements.`,
        low: `Story arc analysis reveals critical narrative optimization opportunities. Strengthening the dramatic progression can transform viewer engagement and emotional investment.`
      }
    };

    const scoreCategory = score >= 8 ? 'high' : score >= 5 ? 'medium' : 'low';
    return insights[frameworkName]?.[scoreCategory] || insights[frameworkName]?.medium || '';
  };

  const generateHookAnalysis = (hookScore, hookCriteria) => {
    if (hookScore >= 8) {
      return `Exceptional hook mastery is evident in your opening moments. With a ${hookScore}/10 score, your content demonstrates sophisticated techniques for immediate attention capture, leveraging psychological triggers that ensure viewers stay engaged beyond the critical first 3 seconds. This level of hook performance typically correlates with 3-5x higher completion rates and significantly improved conversion metrics.`;
    } else if (hookScore >= 6) {
      return `Your hook shows solid fundamentals with a ${hookScore}/10 score, indicating competent attention capture techniques. While the opening effectively introduces your content, strategic enhancements can transform viewer retention and create stronger initial engagement that carries through the entire viewing experience.`;
    } else {
      return `Hook analysis reveals significant opportunities for improvement with a ${hookScore}/10 score. The opening moments are critical for viewer retention—strengthening your hook can dramatically improve completion rates and create the foundation for successful conversion throughout the rest of your content.`;
    }
  };

  const generateAdvancedConclusion = () => {
    const score = overallScore;
    const storyArcAnalysis = getStoryArcAnalysis();
    const frameworks = [
      { name: 'AIDA', score: getAIDAAnalysis().overall },
      { name: 'PAS', score: getPASAnalysis().overall },
      { name: 'BAB', score: getBABAnalysis().overall },
      { name: 'HRR', score: getHRRAnalysis().overall },
      { name: '3-Act', score: get3ActAnalysis().overall },
      { name: 'Story Arc', score: storyArcAnalysis.overall }
    ];
    
    const topFramework = frameworks.reduce((best, current) => 
      current.score > best.score ? current : best
    );
    
    const strongFrameworks = frameworks.filter(f => f.score >= 7);
    const improvementAreas = frameworks.filter(f => f.score < 6);
    
    if (score >= 8) {
      return {
        verdict: "Multi-Framework Mastery Achieved",
        message: `This video represents a masterclass in strategic multi-framework marketing. With an overall score of ${overallScore}/10 and ${strongFrameworks.length} frameworks scoring 7+, your content demonstrates exceptional understanding of the psychological principles that drive viewer behavior. The ${topFramework.name} framework's ${topFramework.score.toFixed(1)}/10 performance indicates mastery-level execution that can serve as a template for scaling your video marketing strategy across all channels.`,
        recommendation: "This content is ready for maximum amplification. Consider creating a content series following the same multi-framework approach, and use this analysis as a benchmark for all future video content to maintain this level of strategic excellence."
      };
    } else if (score >= 6) {
      return {
        verdict: "Strategic Foundation with Growth Acceleration Potential",
        message: `Your video demonstrates solid multi-framework performance with an overall score of ${overallScore}/10. The ${topFramework.name} framework's ${topFramework.score.toFixed(1)}/10 performance provides a strong strategic foundation, while ${improvementAreas.length} framework${improvementAreas.length > 1 ? 's' : ''} present targeted opportunities for enhancement. By focusing on the highest-impact improvements first, you can achieve significant performance gains across all metrics.`,
        recommendation: "Prioritize improvements in the lowest-scoring frameworks while maintaining your strengths. A systematic approach to framework optimization can yield 2-3x improvements in engagement and conversion rates within 30-60 days."
      };
    } else {
      return {
        verdict: "Strategic Transformation Opportunity",
        message: `With an overall score of ${overallScore}/10, this video presents clear opportunities for multi-framework optimization. While ${topFramework.name} shows promise at ${topFramework.score.toFixed(1)}/10, ${improvementAreas.length} framework${improvementAreas.length > 1 ? 's' : ''} require strategic restructuring to unlock the content's full potential. This analysis provides the roadmap for transforming underperforming elements into competitive advantages.`,
        recommendation: "Implement framework improvements systematically, starting with the lowest-scoring areas. Consider A/B testing different approaches to find the optimal combination that resonates most effectively with your target audience."
      };
    }
  };
  
  const tabs = [
    { id: 'overview', label: generateOverviewHeading(), icon: <BarChart3 className="w-4 h-4" /> },
    { id: 'article', label: 'Analysis', icon: <FileText className="w-4 h-4" /> },
    { id: 'branding', label: 'Branding', icon: <Star className="w-4 h-4" /> },
    { id: 'conversion', label: 'Conversion', icon: <TrendingUp className="w-4 h-4" /> },
    { id: 'brief', label: 'Brief', icon: <Lightbulb className="w-4 h-4" /> },
    { id: 'aida', label: 'AIDA', icon: <Target className="w-4 h-4" /> },
    { id: 'pas', label: 'PAS', icon: <Zap className="w-4 h-4" /> },
    { id: 'bab', label: 'BAB', icon: <TrendingUp className="w-4 h-4" /> },
    { id: 'hrr', label: 'HRR', icon: <CheckCircle className="w-4 h-4" /> },
    { id: '3act', label: '3-Act', icon: <AlertTriangle className="w-4 h-4" /> },
    { id: 'hero', label: "Hero", icon: <Star className="w-4 h-4" /> },
    ...(isAdvancedAnalysis ? [{ id: 'advanced', label: 'Advanced Analysis', icon: <BarChart3 className="w-4 h-4" /> }] : [])
  ];

  const handleDownloadReport = async () => {
    // Switch to article tab first if not already there
    const wasOnArticleTab = activeTab === 'article';
    if (!wasOnArticleTab) {
      setActiveTab('article');
      // Wait for tab to render
      await new Promise(resolve => setTimeout(resolve, 100));
    }
    
    if (!articleRef.current) {
      toast.error('Article content not available');
      return;
    }
    
    setIsGeneratingPDF(true);
    
    try {
      // Enhanced html2canvas options for production
      const canvas = await html2canvas(articleRef.current, {
        scale: 1.5, // Reduced scale for better compatibility
        useCORS: true,
        allowTaint: false, // Changed to false for better security
        backgroundColor: '#ffffff',
        logging: false,
        width: articleRef.current.scrollWidth,
        height: articleRef.current.scrollHeight,
        scrollX: 0,
        scrollY: 0,
        windowWidth: window.innerWidth,
        windowHeight: window.innerHeight,
        ignoreElements: (element) => {
          // Ignore video elements that might cause issues
          return element.tagName === 'VIDEO' || element.classList.contains('video-element');
        }
      });
      
      const imgData = canvas.toDataURL('image/png', 0.8); // Reduced quality for better compatibility
      const pdf = new jsPDF('p', 'mm', 'a4');
      const imgWidth = 210;
      const pageHeight = 295;
      const imgHeight = (canvas.height * imgWidth) / canvas.width;
      let heightLeft = imgHeight;
      
      let position = 0;
      
      pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
      heightLeft -= pageHeight;
      
      while (heightLeft >= 0) {
        position = heightLeft - imgHeight;
        pdf.addPage();
        pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
        heightLeft -= pageHeight;
      }
      
      pdf.save(`video-analysis-article-${new Date().toISOString().split('T')[0]}.pdf`);
      toast.success('PDF article generated successfully!');
      
      // Switch back to original tab if we changed it
      if (!wasOnArticleTab) {
        setTimeout(() => setActiveTab('overview'), 500);
      }

    } catch (error) {
      console.error('Error generating PDF:', error);
      toast.error('Failed to generate PDF report');
    } finally {
      setIsGeneratingPDF(false);
    }
  };
  
  // OLD UNUSED PDF FUNCTION - KEEPING FOR REFERENCE
  const handleDownloadReportOLD = async () => {
    setIsGeneratingPDF(true);
    
    try {
      const pdf = new jsPDF('p', 'mm', 'a4');
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      const margin = 20;
      let yPosition = margin;
      let pageNumber = 1;

      // Helper functions
      const checkNewPage = (requiredSpace = 20) => {
        if (yPosition + requiredSpace > pageHeight - margin - 20) {
          pdf.addPage();
          yPosition = margin;
          pageNumber++;
          return true;
        }
        return false;
      };

      const addFooter = () => {
        const footerY = pageHeight - 10;
        pdf.setFontSize(8);
        pdf.setFont('helvetica', 'normal');
        pdf.setTextColor('#6b7280');
        pdf.text(`Page ${pageNumber}`, pageWidth - 30, footerY);
        pdf.text('Generated by ArcRefinery AI', margin, footerY);
      };

      const addText = (text, fontSize = 11, isBold = false, color = '#000000', alignment = 'left') => {
        if (!text) return;
        pdf.setFontSize(fontSize);
        pdf.setFont('helvetica', isBold ? 'bold' : 'normal');
        pdf.setTextColor(color);
        const maxWidth = pageWidth - 2 * margin;
        const lines = pdf.splitTextToSize(text, maxWidth);
        lines.forEach(line => {
          if (checkNewPage(fontSize + 5)) return;
          let xPosition = margin;
          if (alignment === 'center') xPosition = (pageWidth - pdf.getTextWidth(line)) / 2;
          pdf.text(line, xPosition, yPosition);
          yPosition += fontSize * 0.4;
        });
        yPosition += 3;
      };

      const addSectionHeader = (title, color = '#1e40af') => {
        checkNewPage(15);
        yPosition += 5;
        pdf.setFillColor(color);
        pdf.roundedRect(margin, yPosition - 2, pageWidth - 2 * margin, 8, 2, 2, 'F');
        pdf.setFontSize(14);
        pdf.setFont('helvetica', 'bold');
        pdf.setTextColor('#ffffff');
        pdf.text(title, margin + 5, yPosition + 3);
        yPosition += 12;
      };

      const addSubsection = (title) => {
        checkNewPage(10);
        yPosition += 3;
        addText(title, 12, true, '#374151');
        yPosition += 2;
      };

      // Header
      addText('Social Media Video Analysis Report', 18, true, '#1f2937', 'center');
      yPosition += 5;
      addText(`Platform: ${platformInfo?.name || platform || 'Social Media'}`, 12, false, '#6b7280', 'center');
      yPosition += 10;

      // Report metadata
      checkNewPage(25);
      pdf.setFillColor('#f8fafc');
      pdf.setDrawColor('#e2e8f0');
      pdf.roundedRect(margin, yPosition, pageWidth - 2 * margin, 20, 3, 3, 'FD');
      yPosition += 5;
      addText('Report Information', 10, true, '#475569');
      addText(`Generated: ${new Date(generatedAt).toLocaleString()}`, 9, false, '#64748b');
      addText(`Analysis ID: ${data.id || 'N/A'}`, 9, false, '#64748b');
      yPosition += 15;

      // Calculate average score from criteria
      const criteria = review?.criteria || review?.videoAnalysis?.criteria || [];
      const averageScore = criteria.length > 0 
        ? (criteria.reduce((sum, criterion) => sum + criterion.score, 0) / criteria.length).toFixed(1)
        : '0.0';
      
      // Determine status based on average score
      const getStatusLabel = (score) => {
        const numScore = parseFloat(score);
        if (numScore >= 8) return 'Acceptable';
        if (numScore >= 5) return 'Needs Improvement';
        return 'Critical';
      };
      
      const statusLabel = getStatusLabel(averageScore);
      
      // Performance Timeline (Chart) with Average Score and Status
      addSectionHeader(`Average Score: ${averageScore}/10 - ${statusLabel}`, '#059669');
      try {
        const chartElement = document.querySelector('.recharts-wrapper');
        if (chartElement) {
          checkNewPage(80);
          const canvas = await html2canvas(chartElement, {
            backgroundColor: '#ffffff',
            scale: 2,
            logging: false,
            useCORS: true,
            allowTaint: true
          });
          const imgData = canvas.toDataURL('image/png');
          const imgWidth = pageWidth - 2 * margin;
          const imgHeight = (canvas.height * imgWidth) / canvas.width;
          if (yPosition + imgHeight > pageHeight - margin - 20) {
            pdf.addPage();
            yPosition = margin;
            pageNumber++;
          }
          pdf.addImage(imgData, 'PNG', margin, yPosition, imgWidth, imgHeight);
          yPosition += imgHeight + 10;
        }
      } catch (chartError) {
        addText('Performance chart not available in this export', 10, false, '#6b7280');
      }

      // Strengths
      const strengths = review.strengths || review.videoAnalysis?.strengths || [];
      if (strengths.length > 0) {
        addSectionHeader('Strengths', '#10b981');
        strengths.forEach(strength => {
          checkNewPage(10);
          pdf.setFillColor('#10b981');
          pdf.circle(margin + 3, yPosition + 2, 1, 'F');
          addText(`  ${strength}`, 10, false, '#374151');
        });
        yPosition += 5;
      }

      // Areas for Improvement
      const improvements = review.improvements || review.videoAnalysis?.improvements || [];
      if (improvements.length > 0) {
        addSectionHeader('Areas for Improvement', '#f59e0b');
        improvements.forEach(improvement => {
          checkNewPage(10);
          pdf.setFillColor('#f59e0b');
          pdf.circle(margin + 3, yPosition + 2, 1, 'F');
          addText(`  ${improvement}`, 10, false, '#374151');
        });
        yPosition += 5;
      }

      // Actionable Recommendations
      const recommendations = review.recommendations || review.videoAnalysis?.recommendations || [];
      if (recommendations.length > 0) {
        addSectionHeader('Actionable Recommendations', '#8b5cf6');
        recommendations.forEach((recommendation, index) => {
          checkNewPage(15);
          addText(`${index + 1}. ${recommendation}`, 10, false, '#374151');
        });
        yPosition += 5;
      }

      // Compliance and Audience Analysis
      addSectionHeader('Compliance & Audience Analysis', '#0ea5e9');
      const compliance = review.compliance || review.videoAnalysis?.compliance;
      const audienceAnalysis = review.audienceAnalysis || review.videoAnalysis?.audienceAnalysis;
      
      if (compliance) {
        addSubsection('Compliance Assessment');
        addText(compliance, 10, false, '#374151');
        yPosition += 5;
      }
      
      if (audienceAnalysis) {
        addSubsection('Audience Analysis');
        addText(audienceAnalysis, 10, false, '#374151');
        yPosition += 5;
      }

      // Detailed Analysis
      if (criteria.length > 0) {
        addSectionHeader('Detailed Analysis', '#6366f1');
        criteria.forEach((criterion, index) => {
          checkNewPage(25);
          addSubsection(`${index + 1}. ${criterion.name}`);
          
          // Score and status
          const scoreColor = criterion.score >= 8 ? '#10b981' : criterion.score >= 5 ? '#f59e0b' : '#ef4444';
          const statusText = criterion.score >= 8 ? 'Acceptable' : criterion.score >= 5 ? 'Needs Improvement' : 'Critical';
          
          pdf.setFillColor(scoreColor);
          pdf.circle(margin + 10, yPosition + 3, 3, 'F');
          pdf.setFontSize(12);
          pdf.setFont('helvetica', 'bold');
          pdf.setTextColor('#000000');
          pdf.text(`${criterion.score.toFixed(1)}/10`, margin + 20, yPosition + 5);
          pdf.setFontSize(10);
          pdf.setFont('helvetica', 'normal');
          pdf.setTextColor(scoreColor);
          pdf.text(statusText, margin + 35, yPosition + 5);
          yPosition += 10;
          
          if (criterion.explanation) {
            addText('Explanation:', 10, true, '#374151');
            addText(criterion.explanation, 9, false, '#6b7280');
          }
          
          if (criterion.recommendations) {
            addText('Recommendations:', 10, true, '#374151');
            addText(criterion.recommendations, 9, false, '#6b7280');
          }
          
          yPosition += 5;
        });
      }

      // Platform Insights
      addSectionHeader('Platform Insights', '#7c3aed');
      addSubsection(`${platformInfo?.name || platform} Best Practices`);
      
      if (platformInfo?.criteria) {
        addText('Evaluation Criteria:', 10, true, '#374151');
        platformInfo.criteria.forEach(criterion => {
          pdf.setFillColor('#6366f1');
          pdf.circle(margin + 3, yPosition + 2, 1, 'F');
          addText(`  ${criterion}`, 9, false, '#6b7280');
        });
        yPosition += 5;
      }
      
      if (platformInfo?.bestPractices) {
        addText('Best Practices:', 10, true, '#374151');
        platformInfo.bestPractices.forEach(practice => {
          pdf.setFillColor('#10b981');
          pdf.circle(margin + 3, yPosition + 2, 1, 'F');
          addText(`  ${practice}`, 9, false, '#6b7280');
        });
        yPosition += 5;
      }
      
      const platformSpecific = review.platformSpecific || review.videoAnalysis?.platformSpecific;
      if (platformSpecific) {
        addSubsection('Platform-Specific Insights');
        addText(platformSpecific, 10, false, '#374151');
        yPosition += 5;
      }

      // Improved Video Brief
      addSectionHeader('Improved Video Brief', '#ec4899');
      addSubsection('Recommended Video Variation Brief');
      
      // Video Concept & Hook
      addSubsection('Video Concept & Hook');
      const overallScore = review.overallScore || review.videoAnalysis?.overallScore || 0;
      let conceptText = '';
      if (overallScore >= 80) {
        conceptText = `Build on the strong foundation of your current video while experimenting with new creative approaches. Focus on maintaining the engaging hook while exploring fresh angles for your message.`;
      } else if (overallScore >= 50) {
        conceptText = `Create a variation that addresses the key improvement areas while maintaining the strengths of your current approach. Focus on optimizing the elements that scored in the needs improvement range.`;
      } else {
        conceptText = `Develop a strategic variation that addresses the fundamental issues identified in the analysis. Consider a new approach to content structure and presentation.`;
      }
      addText(conceptText, 10, false, '#374151');
      yPosition += 5;

      // Storytelling Structure
      addSubsection('Storytelling Structure');
      addText('Act 1: Setup (0-3s) - Establish problem/opportunity', 9, false, '#6b7280');
      addText('Act 2: Development (3-15s) - Build narrative arc', 9, false, '#6b7280');
      addText('Act 3: Resolution (15s+) - Compelling call-to-action', 9, false, '#6b7280');
      yPosition += 5;

      // Virality Optimization
      addSubsection('Virality Optimization');
      addText('Maximize Shareability & Viral Potential', 10, true, '#374151');
      addText('• Emotional Hooks: Create strong emotional moments', 9, false, '#6b7280');
      addText('• Trending Elements: Incorporate platform-specific trends', 9, false, '#6b7280');
      addText('• Shareability Factors: Focus on relatability and value', 9, false, '#6b7280');
      yPosition += 5;

      // Key Focus Areas
      addSubsection('Key Focus Areas');
      const criticalPoints = criteria.filter(c => c.score < 5);
      const needsImprovementPoints = criteria.filter(c => c.score >= 5 && c.score < 8);
      
      if (criticalPoints.length > 0) {
        addText('Critical Areas (Score < 5):', 10, true, '#ef4444');
        criticalPoints.slice(0, 3).forEach(point => {
          addText(`• ${point.name}: ${point.recommendations || 'Focus on improving this area'}`, 9, false, '#6b7280');
        });
      }
      
      if (needsImprovementPoints.length > 0) {
        addText('Areas Needing Improvement (Score 5-7):', 10, true, '#f59e0b');
        needsImprovementPoints.slice(0, 3).forEach(point => {
          addText(`• ${point.name}: ${point.recommendations || 'Optimize this element'}`, 9, false, '#6b7280');
        });
      }
      yPosition += 5;

      // Platform Optimization
      addSubsection('Platform Optimization');
      const platformName = platformInfo?.name || platform || 'Social Media';
      let optimizationText = `Tailor the variation specifically for ${platformName} by incorporating the platform's best practices.`;
      if (platformName === 'Meta') {
        optimizationText += ' Focus on mobile-first design, clear value proposition in the first 3 seconds, and strong call-to-action placement.';
      } else if (platformName === 'TikTok') {
        optimizationText += ' Emphasize trending elements, authentic content, and quick-paced editing.';
      } else if (platformName === 'Instagram') {
        optimizationText += ' Leverage visual storytelling, aesthetic consistency, and engaging captions.';
      } else if (platformName === 'YouTube') {
        optimizationText += ' Structure for longer attention spans, include compelling thumbnails, and optimize for search.';
      } else if (platformName === 'Twitter') {
        optimizationText += ' Keep messaging concise, leverage trending topics, and encourage sharing.';
      }
      addText(optimizationText, 10, false, '#374151');
      yPosition += 5;

      // Creative Direction
      addSubsection('Creative Direction');
      addText('• Visual Style: Consider refreshing visual approach', 9, false, '#6b7280');
      addText('• Content Structure: Build on existing structure while enhancing key moments', 9, false, '#6b7280');
      addText('• Call-to-Action: Enhance existing CTA for better conversion', 9, false, '#6b7280');
      addText(`• Engagement Strategy: Focus on elements specific to ${platformName} users`, 9, false, '#6b7280');
      yPosition += 5;

      // Success Metrics
      addSubsection('Success Metrics to Track');
      const shareRate = overallScore >= 80 ? '15%+' : overallScore >= 50 ? '25%+' : '40%+';
      const commentRate = overallScore >= 80 ? '8%+' : overallScore >= 50 ? '12%+' : '20%+';
      const reachMultiplier = overallScore >= 80 ? '5x' : overallScore >= 50 ? '8x' : '12x';
      const engagementLift = overallScore >= 80 ? '2x' : overallScore >= 50 ? '3x' : '5x';
      
      addText(`• Engagement Rate: Target ${shareRate}`, 9, false, '#6b7280');
      addText(`• Comment Rate: Target ${commentRate}`, 9, false, '#6b7280');
      addText(`• Reach Multiplier: Target ${reachMultiplier}`, 9, false, '#6b7280');
      addText(`• Engagement Lift: Target ${engagementLift}`, 9, false, '#6b7280');
      yPosition += 10;

      // Add footer to all pages
      for (let i = 1; i <= pageNumber; i++) {
        pdf.setPage(i);
        addFooter();
      }

      // Generate filename and save
      const fileNamePlatform = platformInfo?.name || platform || 'SocialMedia';
      const date = new Date().toISOString().split('T')[0];
      const fileName = `${fileNamePlatform}_Video_Analysis_${date}.pdf`;
      pdf.save(fileName);
      
      toast.success('PDF report generated successfully!');

    } catch (error) {
      console.error('Error generating PDF:', error);
      toast.error('Failed to generate PDF report. Please try again.');
    } finally {
      setIsGeneratingPDF(false);
    }
  };

  // Social share functionality






  // Get analysis status information
  const getAnalysisStatus = () => {
    if (!data?.analysisInfo) {
      return { mode: 'unknown', engine: 'unknown', quotaStatus: 'unknown' };
    }
    
    const { engine, mode, quotaStatus, quotaDetails } = data.analysisInfo;
    
    if (mode === 'ai_analysis' && quotaStatus === 'available') {
      return {
        mode: 'ai',
        engine: engine,
        quotaStatus: 'available',
        message: `Powered by ${engine} AI`,
        color: 'text-green-600',
        bgColor: 'bg-green-50',
        borderColor: 'border-green-200'
      };
    } else if (mode === 'fallback_analysis' && quotaStatus === 'exceeded') {
      return {
        mode: 'fallback',
        engine: engine,
        quotaStatus: 'exceeded',
        message: `Simple Analysis (${engine} quota exceeded)`,
        color: 'text-orange-600',
        bgColor: 'bg-orange-50',
        borderColor: 'border-orange-200',
        details: quotaDetails || 'Daily quota exceeded'
      };
    } else {
      return {
        mode: 'unknown',
        engine: engine || 'unknown',
        quotaStatus: quotaStatus || 'unknown',
        message: 'Analysis Mode Unknown',
        color: 'text-gray-600',
        bgColor: 'bg-gray-50',
        borderColor: 'border-gray-200'
      };
    }
  };

  const analysisStatus = getAnalysisStatus();

  return (
    <div className="max-w-7xl mx-auto" ref={reportRef}>
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div className="flex items-center space-x-4">
        <button
          onClick={onNewReview}
          className="bg-gradient-to-r from-purple-600 via-purple-700 to-blue-600 hover:from-purple-700 hover:via-purple-800 hover:to-blue-700 text-white font-bold py-2 px-4 rounded-lg shadow-lg hover:shadow-xl transition-all duration-300 flex items-center transform hover:scale-105 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Analyze Another Video
        </button>
          
          {/* Analysis Status Badge */}
          <div className={`flex items-center px-3 py-1 rounded-full text-xs font-medium border ${analysisStatus.bgColor} ${analysisStatus.color} ${analysisStatus.borderColor}`}>
            <div className={`w-2 h-2 rounded-full mr-2 ${
              analysisStatus.quotaStatus === 'available' ? 'bg-green-500' : 
              analysisStatus.quotaStatus === 'exceeded' ? 'bg-orange-500' : 'bg-gray-500'
            }`}></div>
            {analysisStatus.message}
            {analysisStatus.details && (
              <span className="ml-1 text-xs opacity-75">({analysisStatus.details})</span>
            )}
          </div>
        </div>
        
        <div className="flex items-center space-x-4">
          <button
            onClick={handleDownloadReport}
            disabled={isGeneratingPDF}
            className="flex items-center btn-secondary disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Download className="w-4 h-4 mr-2" />
            {isGeneratingPDF ? 'Generating PDF...' : 'Download PDF Report'}
          </button>
          
        </div>
      </div>

      {/* Story Arc Overview */}
      <div className="mb-8">
        {/* Background effect container */}
        <div className="relative">
          {/* Glowing background effect */}
          <div className="absolute -inset-4 bg-gradient-to-r from-purple-400 via-pink-400 to-blue-400 rounded-3xl blur-xl opacity-20 animate-pulse"></div>
          <div className="absolute -inset-2 bg-gradient-to-r from-purple-300 via-pink-300 to-blue-300 rounded-2xl blur-lg opacity-30"></div>
          
          {/* Main content container */}
          <div className="relative bg-gradient-to-br from-gray-50 via-blue-50 to-indigo-50 rounded-2xl p-8 border border-gray-200 shadow-lg backdrop-blur-sm">
          <div className="text-center mb-6">
            <div>
              <h2 className="text-2xl font-bold text-gray-900">Story Arc Analysis</h2>
              <p className="text-gray-600">Advanced Storytelling Insights for Your Video Ad</p>
            </div>
          </div>

          {/* Story Arc Scores Visualization */}
          <div className="mb-6">
            <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-200">
              <div className="flex items-start gap-4">
                {/* Start Frame - Left Side */}
                {data.videoFile && (
                  <div className="flex-shrink-0">
                    <div className="relative group">
                      <div className="w-28 h-50 bg-gradient-to-br from-gray-100 to-gray-200 rounded-lg shadow-md border border-gray-200 flex items-center justify-center overflow-hidden" style={{ aspectRatio: '9/16' }}>
                        <video 
                          className="w-full h-full object-cover"
                          preload="metadata"
                          muted
                          playsInline
                          onLoadedMetadata={(e) => {
                            e.target.currentTime = 0.001; // Seek to first millisecond for start frame
                          }}
                          onMouseEnter={(e) => {
                            e.target.play().catch(() => {});
                          }}
                          onMouseLeave={(e) => {
                            e.target.pause();
                            e.target.currentTime = 0.001;
                          }}
                        >
                          <source src={`/uploads/${data.videoFile.filename}`} type={data.videoFile.mimetype} />
                        </video>
                        <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-20 rounded-lg transition-all duration-200 flex items-center justify-center">
                          <div className="opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                            <div className="w-6 h-6 bg-white bg-opacity-90 rounded-full flex items-center justify-center">
                              <svg className="w-3 h-3 text-gray-700" fill="currentColor" viewBox="0 0 24 24">
                                <path d="M8 5v14l11-7z"/>
                              </svg>
                            </div>
                          </div>
                        </div>
                        <div className="absolute top-1 left-1 bg-black bg-opacity-70 text-white text-xs px-1.5 py-0.5 rounded">
                          Start
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Bar Chart - Center */}
                <div className="flex-1">
                  {(() => {
                const storyArcAnalysis = getStoryArcAnalysis();
                const { hook, problem, tension, climax, resolution } = storyArcAnalysis;
                
                const stages = [
                  { name: 'Hook', score: hook.score },
                  { name: 'Problem', score: problem.score },
                  { name: 'Tension', score: tension.score },
                  { name: 'Climax', score: climax.score },
                  { name: 'Resolution', score: resolution.score }
                ];
                
                const maxScore = 10;
                const chartHeight = 120;
                
                // Get traffic light colors based on score
                const getTrafficLightColor = (score) => {
                  if (score >= 8) return 'from-green-500 to-emerald-500';
                  if (score >= 5) return 'from-amber-500 to-orange-500';
                  return 'from-red-500 to-pink-500';
                };
                
                return (
                  <div className="space-y-3">
                    {/* Vertical Bar Chart */}
                    <div className="relative" style={{ height: `${chartHeight + 40}px` }}>
                      
                      {/* Chart area */}
                      <div className="h-full relative">
                        {/* Grid lines */}
                        <div className="absolute inset-0">
                          {[0, 2, 4, 6, 8, 10].map((value) => (
                            <div
                              key={value}
                              className="absolute w-full border-t border-gray-100"
                              style={{ top: `${((maxScore - value) / maxScore) * chartHeight}px` }}
                            />
                          ))}
                        </div>
                        
                        
                        {/* Bars */}
                        <div className="flex items-end justify-between h-full px-2">
                          {stages.map((stage, index) => {
                            const barHeight = (stage.score / maxScore) * chartHeight;
                            const getScoreColor = (score) => {
                              if (score >= 8) return 'text-green-600';
                              if (score >= 5) return 'text-amber-600';
                              return 'text-red-600';
                            };
                            
                            return (
                              <div key={stage.name} className="flex flex-col items-center flex-1">
                                {/* Bar */}
                                <div className="relative w-8 mb-2">
                                  <div 
                                    className={`w-full rounded-t bg-gradient-to-t ${getTrafficLightColor(stage.score)} transition-all duration-1000 ease-out`}
                                    style={{ height: `${barHeight}px` }}
                                  >
                                    {/* Score label on bar */}
                                    <div className="absolute -top-6 left-1/2 transform -translate-x-1/2">
                                      <span className={`text-xs font-semibold ${getScoreColor(stage.score)}`}>
                                        {stage.score}
                                      </span>
                                    </div>
                                  </div>
                                </div>
                                
                                {/* Stage name */}
                                <div className="text-xs font-medium text-gray-600 text-center leading-tight">
                                  {stage.name}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    </div>
                    
                    {/* Overall Performance Summary */}
                    <div className="flex items-center justify-center space-x-6 pt-2 border-t border-gray-100">
                      <div className="text-center">
                        <div className="text-xl font-bold text-gray-900">
                          {storyArcAnalysis.overall.toFixed(1)}
                        </div>
                        <div className="text-xs text-gray-500">Overall</div>
                      </div>
                      <div className="w-12 h-12 relative">
                        <svg className="w-12 h-12 transform -rotate-90" viewBox="0 0 36 36">
                          <path
                            className="text-gray-200"
                            stroke="currentColor"
                            strokeWidth="2"
                            fill="none"
                            d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
                          />
                          <path
                            className="text-purple-500"
                            stroke="currentColor"
                            strokeWidth="2"
                            fill="none"
                            strokeDasharray={`${storyArcAnalysis.overall * 10}, 100`}
                            d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
                          />
                        </svg>
                        <div className="absolute inset-0 flex items-center justify-center">
                          <span className="text-xs font-semibold text-gray-700">
                            {Math.round(storyArcAnalysis.overall * 10)}%
                          </span>
                        </div>
                      </div>
                      <div className="text-center">
                        <div className="text-xs font-medium text-gray-700">Performance</div>
                        <div className={`text-xs font-semibold ${
                          storyArcAnalysis.overall >= 8 ? 'text-green-600' :
                          storyArcAnalysis.overall >= 5 ? 'text-amber-600' : 'text-red-600'
                        }`}>
                          {storyArcAnalysis.overall >= 8 ? 'Acceptable' :
                           storyArcAnalysis.overall >= 5 ? 'Needs Improvement' : 'Critical'}
                        </div>
                      </div>
                    </div>
                    
                    {/* Strength Indicators */}
                    <div className="mt-4 pt-4 border-t border-gray-200">
                      <div className="grid grid-cols-3 gap-4">
                        {/* Hook Strength */}
                        <div className="text-center">
                          <div className="flex items-center justify-center mb-2">
                            <div className={`w-3 h-3 rounded-full mr-2 ${
                              hook.score >= 8 ? 'bg-green-500' : 
                              hook.score >= 5 ? 'bg-yellow-500' : 'bg-red-500'
                            }`}></div>
                            <span className="text-xs font-semibold text-gray-700">Hook Strength</span>
                          </div>
                          <div className={`text-sm font-bold ${
                            hook.score >= 8 ? 'text-green-600' : 
                            hook.score >= 5 ? 'text-yellow-600' : 'text-red-600'
                          }`}>
                            {hook.score >= 8 ? 'Strong' : hook.score >= 5 ? 'Moderate' : 'Weak'}
                          </div>
                        </div>
                        
                        {/* Call to Action Strength */}
                        <div className="text-center">
                          <div className="flex items-center justify-center mb-2">
                            <div className={`w-3 h-3 rounded-full mr-2 ${
                              resolution.score >= 8 ? 'bg-green-500' : 
                              resolution.score >= 5 ? 'bg-yellow-500' : 'bg-red-500'
                            }`}></div>
                            <span className="text-xs font-semibold text-gray-700">CTA Strength</span>
                          </div>
                          <div className={`text-sm font-bold ${
                            resolution.score >= 8 ? 'text-green-600' : 
                            resolution.score >= 5 ? 'text-yellow-600' : 'text-red-600'
                          }`}>
                            {resolution.score >= 8 ? 'Strong' : resolution.score >= 5 ? 'Moderate' : 'Weak'}
                          </div>
                        </div>
                        
                        {/* Narrative Strength */}
                        <div className="text-center">
                          <div className="flex items-center justify-center mb-2">
                            <div className={`w-3 h-3 rounded-full mr-2 ${
                              tension.score >= 8 ? 'bg-green-500' : 
                              tension.score >= 5 ? 'bg-yellow-500' : 'bg-red-500'
                            }`}></div>
                            <span className="text-xs font-semibold text-gray-700">Narrative Strength</span>
                          </div>
                          <div className={`text-sm font-bold ${
                            tension.score >= 8 ? 'text-green-600' : 
                            tension.score >= 5 ? 'text-yellow-600' : 'text-red-600'
                          }`}>
                            {tension.score >= 8 ? 'Strong' : tension.score >= 5 ? 'Moderate' : 'Weak'}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })()}
                </div>

                {/* End Frame - Right Side */}
                {data.videoFile && (
                  <div className="flex-shrink-0">
                    <div className="relative group">
                      <div className="w-28 h-50 bg-gradient-to-br from-gray-100 to-gray-200 rounded-lg shadow-md border border-gray-200 flex items-center justify-center overflow-hidden" style={{ aspectRatio: '9/16' }}>
                        <video 
                          className="w-full h-full object-cover"
                          preload="metadata"
                          muted
                          playsInline
                          onLoadedMetadata={(e) => {
                            // Seek to the last millisecond of the video for end frame
                            const duration = e.target.duration;
                            if (duration && duration > 0) {
                              e.target.currentTime = Math.max(0, duration - 0.001);
                            }
                          }}
                          onMouseEnter={(e) => {
                            e.target.play().catch(() => {});
                          }}
                          onMouseLeave={(e) => {
                            e.target.pause();
                            const duration = e.target.duration;
                            if (duration && duration > 0) {
                              e.target.currentTime = Math.max(0, duration - 0.001);
                            }
                          }}
                        >
                          <source src={`/uploads/${data.videoFile.filename}`} type={data.videoFile.mimetype} />
                        </video>
                        <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-20 rounded-lg transition-all duration-200 flex items-center justify-center">
                          <div className="opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                            <div className="w-6 h-6 bg-white bg-opacity-90 rounded-full flex items-center justify-center">
                              <svg className="w-3 h-3 text-gray-700" fill="currentColor" viewBox="0 0 24 24">
                                <path d="M8 5v14l11-7z"/>
                              </svg>
                            </div>
                          </div>
                        </div>
                        <div className="absolute top-1 left-1 bg-black bg-opacity-70 text-white text-xs px-1.5 py-0.5 rounded">
                          End
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-6">
            {/* Hook */}
            <div className="bg-white rounded-lg p-4 shadow-sm border border-purple-100">
              <div className="flex items-center mb-3">
                <div className="w-8 h-8 bg-gradient-to-r from-purple-500 to-pink-500 text-white rounded-full flex items-center justify-center text-sm font-bold mr-3">1</div>
                <h3 className="font-semibold text-gray-900 text-sm">Hook</h3>
              </div>
              <div className="space-y-2">
                <p className="text-xs text-gray-500 mb-2">Attention-grabbing opening</p>
                {(() => {
                  const storyArcAnalysis = getStoryArcAnalysis();
                  const { hook } = storyArcAnalysis;
                  
                  return (
                    <div className="space-y-3">
                      {/* Score Bar */}
                      <div className="space-y-1">
                        <div className="flex justify-between items-center">
                          <span className="text-xs text-gray-600">Score</span>
                          <span className="text-xs font-medium text-gray-900">{hook.score.toFixed(1)}/10</span>
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-2">
                          <div 
                            className={`h-2 rounded-full transition-all duration-300 ${
                              hook.score >= 8 ? 'bg-green-500' : 
                              hook.score >= 6 ? 'bg-yellow-500' : 'bg-red-500'
                            }`}
                            style={{ width: `${(hook.score / 10) * 100}%` }}
                          ></div>
                        </div>
                      </div>
                      
                      {/* Analysis Text */}
                      <div className="space-y-2">
                        <p className="text-xs text-gray-700 leading-relaxed">
                          {hook.analysis || 'Hook effectiveness analysis not available'}
                        </p>
                      </div>
                      
                      {/* Status Indicator */}
                      <div className="flex items-center justify-center">
                        <div className={`w-2 h-2 rounded-full mr-2 ${
                          hook.score >= 8 ? 'bg-green-500' : 
                          hook.score >= 6 ? 'bg-yellow-500' : 'bg-red-500'
                        }`}></div>
                        <p className={`text-xs font-medium ${
                          hook.score >= 8 ? 'text-green-700' : 
                          hook.score >= 6 ? 'text-yellow-700' : 'text-red-700'
                        }`}>
                          {hook.score >= 8 ? 'Strong Hook' : 
                           hook.score >= 6 ? 'Good Hook' : 'Needs Work'}
                        </p>
                      </div>
                    </div>
                  );
                })()}
              </div>
            </div>

            {/* Problem */}
            <div className="bg-white rounded-lg p-4 shadow-sm border border-purple-100">
              <div className="flex items-center mb-3">
                <div className="w-8 h-8 bg-gradient-to-r from-purple-500 to-pink-500 text-white rounded-full flex items-center justify-center text-sm font-bold mr-3">2</div>
                <h3 className="font-semibold text-gray-900 text-sm">Problem</h3>
              </div>
              <div className="space-y-2">
                <p className="text-xs text-gray-500 mb-2">Define the problem</p>
                {(() => {
                  const storyArcAnalysis = getStoryArcAnalysis();
                  const { problem } = storyArcAnalysis;
                  
                  return (
                    <div className="space-y-3">
                      {/* Score Bar */}
                      <div className="space-y-1">
                        <div className="flex justify-between items-center">
                          <span className="text-xs text-gray-600">Score</span>
                          <span className="text-xs font-medium text-gray-900">{problem.score.toFixed(1)}/10</span>
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-2">
                          <div 
                            className={`h-2 rounded-full transition-all duration-300 ${
                              problem.score >= 8 ? 'bg-green-500' : 
                              problem.score >= 6 ? 'bg-yellow-500' : 'bg-red-500'
                            }`}
                            style={{ width: `${(problem.score / 10) * 100}%` }}
                          ></div>
                        </div>
                      </div>
                      
                      {/* Analysis Text */}
                      <div className="space-y-2">
                        <p className="text-xs text-gray-700 leading-relaxed">
                          {problem.analysis || 'Problem identification analysis not available'}
                        </p>
                      </div>
                      
                      {/* Status Indicator */}
                      <div className="flex items-center justify-center">
                        <div className={`w-2 h-2 rounded-full mr-2 ${
                          problem.score >= 8 ? 'bg-green-500' : 
                          problem.score >= 6 ? 'bg-yellow-500' : 'bg-red-500'
                        }`}></div>
                        <p className={`text-xs font-medium ${
                          problem.score >= 8 ? 'text-green-700' : 
                          problem.score >= 6 ? 'text-yellow-700' : 'text-red-700'
                        }`}>
                          {problem.score >= 8 ? 'Strong Problem' : 
                           problem.score >= 6 ? 'Good Problem' : 'Needs Work'}
                        </p>
                      </div>
                    </div>
                  );
                })()}
              </div>
            </div>

            {/* Tension */}
            <div className="bg-white rounded-lg p-4 shadow-sm border border-purple-100">
              <div className="flex items-center mb-3">
                <div className="w-8 h-8 bg-gradient-to-r from-purple-500 to-pink-500 text-white rounded-full flex items-center justify-center text-sm font-bold mr-3">3</div>
                <h3 className="font-semibold text-gray-900 text-sm">Tension</h3>
              </div>
              <div className="space-y-2">
                <p className="text-xs text-gray-500 mb-2">Build tension</p>
                {(() => {
                  const storyArcAnalysis = getStoryArcAnalysis();
                  const { tension } = storyArcAnalysis;
                  
                  return (
                    <div className="space-y-3">
                      {/* Score Bar */}
                      <div className="space-y-1">
                        <div className="flex justify-between items-center">
                          <span className="text-xs text-gray-600">Score</span>
                          <span className="text-xs font-medium text-gray-900">{tension.score.toFixed(1)}/10</span>
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-2">
                          <div 
                            className={`h-2 rounded-full transition-all duration-300 ${
                              tension.score >= 8 ? 'bg-green-500' : 
                              tension.score >= 6 ? 'bg-yellow-500' : 'bg-red-500'
                            }`}
                            style={{ width: `${(tension.score / 10) * 100}%` }}
                          ></div>
                        </div>
                      </div>
                      
                      {/* Analysis Text */}
                      <div className="space-y-2">
                        <p className="text-xs text-gray-700 leading-relaxed">
                          {tension.analysis || 'Tension building analysis not available'}
                        </p>
                      </div>
                      
                      {/* Status Indicator */}
                      <div className="flex items-center justify-center">
                        <div className={`w-2 h-2 rounded-full mr-2 ${
                          tension.score >= 8 ? 'bg-green-500' : 
                          tension.score >= 6 ? 'bg-yellow-500' : 'bg-red-500'
                        }`}></div>
                        <p className={`text-xs font-medium ${
                          tension.score >= 8 ? 'text-green-700' : 
                          tension.score >= 6 ? 'text-yellow-700' : 'text-red-700'
                        }`}>
                          {tension.score >= 8 ? 'Strong Tension' : 
                           tension.score >= 6 ? 'Good Tension' : 'Needs Work'}
                        </p>
                      </div>
                    </div>
                  );
                })()}
              </div>
            </div>

            {/* Climax/Resolution */}
            <div className="bg-white rounded-lg p-4 shadow-sm border border-purple-100">
              <div className="flex items-center mb-3">
                <div className="w-8 h-8 bg-gradient-to-r from-purple-500 to-pink-500 text-white rounded-full flex items-center justify-center text-sm font-bold mr-3">4</div>
                <h3 className="font-semibold text-gray-900 text-sm">Climax</h3>
              </div>
              <div className="space-y-2">
                <p className="text-xs text-gray-500 mb-2">Reveal payoff</p>
                {(() => {
                  const storyArcAnalysis = getStoryArcAnalysis();
                  const { climax } = storyArcAnalysis;
                  
                    return (
                    <div className="space-y-3">
                      {/* Score Bar */}
                      <div className="space-y-1">
                        <div className="flex justify-between items-center">
                          <span className="text-xs text-gray-600">Score</span>
                          <span className="text-xs font-medium text-gray-900">{climax.score.toFixed(1)}/10</span>
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-2">
                          <div 
                            className={`h-2 rounded-full transition-all duration-300 ${
                              climax.score >= 8 ? 'bg-green-500' : 
                              climax.score >= 6 ? 'bg-yellow-500' : 'bg-red-500'
                            }`}
                            style={{ width: `${(climax.score / 10) * 100}%` }}
                          ></div>
                        </div>
                      </div>
                      
                      {/* Analysis Text */}
                      <div className="space-y-2">
                        <p className="text-xs text-gray-700 leading-relaxed">
                          {climax.analysis || 'Climax analysis not available'}
                        </p>
                        </div>
                      
                      {/* Status Indicator */}
                      <div className="flex items-center justify-center">
                        <div className={`w-2 h-2 rounded-full mr-2 ${
                          climax.score >= 8 ? 'bg-green-500' : 
                          climax.score >= 6 ? 'bg-yellow-500' : 'bg-red-500'
                        }`}></div>
                        <p className={`text-xs font-medium ${
                          climax.score >= 8 ? 'text-green-700' : 
                          climax.score >= 6 ? 'text-yellow-700' : 'text-red-700'
                        }`}>
                          {climax.score >= 8 ? 'Strong Climax' : 
                           climax.score >= 6 ? 'Good Climax' : 'Needs Work'}
                        </p>
                        </div>
                      </div>
                    );
                })()}
              </div>
            </div>

            {/* Resolution */}
            <div className="bg-white rounded-lg p-4 shadow-sm border border-purple-100">
              <div className="flex items-center mb-3">
                <div className="w-8 h-8 bg-gradient-to-r from-purple-500 to-pink-500 text-white rounded-full flex items-center justify-center text-sm font-bold mr-3">5</div>
                <h3 className="font-semibold text-gray-900 text-sm">Resolution</h3>
              </div>
              <div className="space-y-2">
                <p className="text-xs text-gray-500 mb-2">Complete the story</p>
                {(() => {
                  const storyArcAnalysis = getStoryArcAnalysis();
                  const { resolution } = storyArcAnalysis;
                  
                    return (
                    <div className="space-y-3">
                      {/* Score Bar */}
                      <div className="space-y-1">
                        <div className="flex justify-between items-center">
                          <span className="text-xs text-gray-600">Score</span>
                          <span className="text-xs font-medium text-gray-900">{resolution.score.toFixed(1)}/10</span>
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-2">
                          <div 
                            className={`h-2 rounded-full transition-all duration-300 ${
                              resolution.score >= 8 ? 'bg-green-500' : 
                              resolution.score >= 6 ? 'bg-yellow-500' : 'bg-red-500'
                            }`}
                            style={{ width: `${(resolution.score / 10) * 100}%` }}
                          ></div>
                        </div>
                      </div>
                      
                      {/* Analysis Text */}
                      <div className="space-y-2">
                        <p className="text-xs text-gray-700 leading-relaxed">
                          {resolution.analysis || 'Resolution analysis not available'}
                        </p>
                        </div>
                      
                      {/* Status Indicator */}
                      <div className="flex items-center justify-center">
                        <div className={`w-2 h-2 rounded-full mr-2 ${
                          resolution.score >= 8 ? 'bg-green-500' : 
                          resolution.score >= 6 ? 'bg-yellow-500' : 'bg-red-500'
                        }`}></div>
                        <p className={`text-xs font-medium ${
                          resolution.score >= 8 ? 'text-green-700' : 
                          resolution.score >= 6 ? 'text-yellow-700' : 'text-red-700'
                        }`}>
                          {resolution.score >= 8 ? 'Strong Resolution' : 
                           resolution.score >= 6 ? 'Good Resolution' : 'Needs Work'}
                        </p>
                        </div>
                      </div>
                    );
                })()}
              </div>
            </div>
          </div>
          
          {/* Story Arc Recommendations */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Strengths */}
            <div className="card">
              <div className="flex items-center mb-4">
                <div className="w-8 h-8 bg-green-100 rounded-lg flex items-center justify-center mr-3">
                  <span className="text-green-600 font-bold">✓</span>
                </div>
                <h3 className="text-lg font-semibold text-gray-900">Story Arc Strengths</h3>
              </div>
              <div className="space-y-3">
                {(() => {
                   const storyArcAnalysis = getStoryArcAnalysis();
                   const { hook, problem, tension, climax, resolution } = storyArcAnalysis;
                   
                   const storyElements = [
                     { name: 'Hook', score: hook.score, analysis: hook.analysis, description: 'Attention-grabbing opening', color: 'purple' },
                     { name: 'Problem', score: problem.score, analysis: problem.analysis, description: 'Problem identification and setup', color: 'blue' },
                     { name: 'Tension', score: tension.score, analysis: tension.analysis, description: 'Building narrative tension', color: 'orange' },
                     { name: 'Climax', score: climax.score, analysis: climax.analysis, description: 'Reveal payoff moment', color: 'red' },
                     { name: 'Resolution', score: resolution.score, analysis: resolution.analysis, description: 'Complete story resolution', color: 'green' }
                   ];
                   
                   const strengths = storyElements.filter(element => element.score >= 7);
                   
                   if (strengths.length === 0) {
                     return (
                       <div className="text-center py-6">
                         <div className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-3">
                           <TrendingUp className="w-6 h-6 text-gray-400" />
                         </div>
                         <p className="text-gray-500 text-sm mb-2">Focus on improving story arc elements</p>
                         <p className="text-xs text-gray-400">All elements are currently below 7/10 - prioritize hook and problem identification</p>
                       </div>
                     );
                   }
                   
                   return strengths.map((strength, index) => {
                     // Traffic light color system based on score
                     const getTrafficLightColor = (score) => {
                       if (score >= 8) return {
                         bg: 'bg-green-50',
                         border: 'border-green-200',
                         dot: 'bg-green-500',
                         text: 'text-green-800',
                         badge: 'bg-green-200 text-green-800'
                       };
                       if (score >= 5) return {
                         bg: 'bg-yellow-50',
                         border: 'border-yellow-200',
                         dot: 'bg-yellow-500',
                         text: 'text-yellow-800',
                         badge: 'bg-yellow-200 text-yellow-800'
                       };
                       return {
                         bg: 'bg-red-50',
                         border: 'border-red-200',
                         dot: 'bg-red-500',
                         text: 'text-red-800',
                         badge: 'bg-red-200 text-red-800'
                       };
                     };
                     
                     const colors = getTrafficLightColor(strength.score);
                     
                    return (
                       <div key={index} className={`p-4 rounded-lg border ${colors.bg} ${colors.border} hover:shadow-md transition-all duration-200`}>
                         <div className="flex items-start justify-between mb-2">
                           <div className="flex items-center">
                             <div className={`w-8 h-8 ${colors.bg} rounded-full flex items-center justify-center mr-3 flex-shrink-0`}>
                               <div className={`w-3 h-3 ${colors.dot} rounded-full`}></div>
                  </div>
                             <div>
                               <h4 className={`font-semibold text-sm ${colors.text}`}>{strength.name}</h4>
                               <p className="text-xs text-gray-600">{strength.description}</p>
                      </div>
                    </div>
                           <div className={`px-2 py-1 ${colors.badge} text-xs font-bold rounded-full`}>
                             {strength.score.toFixed(1)}/10
                           </div>
                         </div>
                         <p className="text-xs text-gray-700 leading-relaxed ml-11">
                           {strength.analysis || `${strength.name} analysis not available`}
                         </p>
                       </div>
                     );
                   });
                })()}
              </div>
            </div>

            {/* Improvements */}
            <div className="card">
              <div className="flex items-center mb-4">
                <div className="w-8 h-8 bg-amber-100 rounded-lg flex items-center justify-center mr-3">
                  <span className="text-amber-600 font-bold">!</span>
                </div>
                <h3 className="text-lg font-semibold text-gray-900">Story Arc Improvements</h3>
              </div>
              <div className="space-y-3">
                {(() => {
                  const storyArcAnalysis = getStoryArcAnalysis();
                  const { hook, problem, tension, climax, resolution } = storyArcAnalysis;
                  
                  const storyElements = [
                    { name: 'Hook', score: hook.score, analysis: hook.analysis, description: 'Attention-grabbing opening', color: 'purple', tips: 'Start with a compelling question, bold statement, or unexpected visual' },
                    { name: 'Problem', score: problem.score, analysis: problem.analysis, description: 'Problem identification and setup', color: 'blue', tips: 'Clearly define the problem your audience faces within the first 10 seconds' },
                    { name: 'Tension', score: tension.score, analysis: tension.analysis, description: 'Building narrative tension', color: 'orange', tips: 'Create obstacles, challenges, or emotional stakes that build viewer investment' },
                    { name: 'Climax', score: climax.score, analysis: climax.analysis, description: 'Reveal payoff moment', color: 'red', tips: 'Reveal your product/service as the solution with emotional impact' },
                    { name: 'Resolution', score: resolution.score, analysis: resolution.analysis, description: 'Complete story resolution', color: 'green', tips: 'Provide clear call-to-action and deliver on the transformation promise' }
                  ];
                  
                  const improvements = storyElements.filter(element => element.score < 7);
                  
                  if (improvements.length === 0) {
                    return (
                      <div className="text-center py-6">
                        <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-3">
                          <CheckCircle className="w-6 h-6 text-green-600" />
                        </div>
                        <p className="text-green-600 text-sm font-medium mb-2">Excellent story arc performance!</p>
                        <p className="text-xs text-gray-500">All story elements are scoring 7+ - focus on optimization</p>
                      </div>
                    );
                  }
                  
                  return improvements.map((improvement, index) => {
                    // Traffic light color system based on score
                    const getTrafficLightColor = (score) => {
                      if (score >= 8) return {
                        bg: 'bg-green-50',
                        border: 'border-green-200',
                        icon: 'text-green-600',
                        text: 'text-green-800',
                        badge: 'bg-green-200 text-green-800'
                      };
                      if (score >= 5) return {
                        bg: 'bg-yellow-50',
                        border: 'border-yellow-200',
                        icon: 'text-yellow-600',
                        text: 'text-yellow-800',
                        badge: 'bg-yellow-200 text-yellow-800'
                      };
                      return {
                        bg: 'bg-red-50',
                        border: 'border-red-200',
                        icon: 'text-red-600',
                        text: 'text-red-800',
                        badge: 'bg-red-200 text-red-800'
                      };
                    };
                    
                    const colors = getTrafficLightColor(improvement.score);
                    
                    return (
                      <div key={index} className={`p-4 rounded-lg border ${colors.bg} ${colors.border} hover:shadow-md transition-all duration-200`}>
                        <div className="flex items-start justify-between mb-3">
                          <div className="flex items-center">
                            <div className={`w-8 h-8 ${colors.bg} rounded-full flex items-center justify-center mr-3 flex-shrink-0`}>
                              <AlertTriangle className={`w-4 h-4 ${colors.icon}`} />
                  </div>
                            <div>
                              <h4 className={`font-semibold text-sm ${colors.text}`}>{improvement.name}</h4>
                              <p className="text-xs text-gray-600">{improvement.description}</p>
                            </div>
                          </div>
                          <div className={`px-2 py-1 ${colors.badge} text-xs font-bold rounded-full`}>
                            {improvement.score.toFixed(1)}/10
                          </div>
                        </div>
                        
                        <div className="ml-11 space-y-2">
                          <p className="text-xs text-gray-700 leading-relaxed">
                            {improvement.analysis || `${improvement.name} needs improvement`}
                          </p>
                          <div className="mt-2 p-2 bg-white rounded border border-gray-200">
                            <p className="text-xs text-gray-800 font-medium mb-1">💡 Quick Improvement:</p>
                            <p className="text-xs text-gray-700">{improvement.tips}</p>
              </div>
                    </div>
                      </div>
                    );
                  });
                })()}
              </div>
            </div>
          </div>
          
          
        </div>
        </div>
      </div>

      {/* Highest Scored Frameworks Summary */}
      <div className="mb-6">
        <div className="bg-gradient-to-br from-indigo-50 via-purple-50 to-pink-50 rounded-xl p-6 border border-indigo-200 shadow-lg">
          <div className="text-center mb-4">
            <div>
              <h2 className="text-xl font-bold text-gray-900">Top Performing Frameworks</h2>
              <p className="text-gray-600 text-sm">Your highest scored brand storytelling and conversion frameworks</p>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Highest Brand Storytelling Framework */}
            <div className="bg-white rounded-lg p-4 border border-gray-200 shadow-sm">
              <div className="text-center mb-3">
                <h3 className="text-lg font-bold text-gray-900 mb-1">Best Brand Storytelling</h3>
                <p className="text-gray-600 text-xs">Highest performing narrative framework</p>
              </div>
              
              {(() => {
                const hrrAnalysis = getHRRAnalysis();
                const threeActAnalysis = get3ActAnalysis();
                const heroAnalysis = getHeroJourneyAnalysis();
                const storyArcAnalysis = getStoryArcAnalysis();
                
                const frameworks = [
                  { name: 'HRR', score: hrrAnalysis.overall, description: 'Hook → Relate → Resolution', color: 'from-green-500 to-emerald-500', bgColor: 'bg-green-50', borderColor: 'border-green-200' },
                  { name: '3-Act Structure', score: threeActAnalysis.overall, description: 'Setup → Confrontation → Resolution', color: 'from-blue-500 to-indigo-500', bgColor: 'bg-blue-50', borderColor: 'border-blue-200' },
                  { name: "Hero's Journey", score: heroAnalysis.overall, description: 'Hero → Call → Mentor → Transformation → Return', color: 'from-amber-500 to-orange-500', bgColor: 'bg-amber-50', borderColor: 'border-amber-200' },
                  { name: 'Story Arc', score: storyArcAnalysis.overall, description: 'Hook → Problem → Tension → Climax → Resolution', color: 'from-purple-500 to-pink-500', bgColor: 'bg-purple-50', borderColor: 'border-purple-200' }
                ];
                
                const bestFramework = frameworks.reduce((best, current) => 
                  current.score > best.score ? current : best
                );
                
                return (
                  <div className="text-center">
                    <div className={`inline-flex items-center px-3 py-1 rounded-md bg-gradient-to-r ${bestFramework.color} text-white mb-2`}>
                      <span className="font-bold text-sm">{bestFramework.name}</span>
                    </div>
                    <div className="text-2xl font-bold text-gray-900 mb-1">
                      {bestFramework.score.toFixed(1)}/10
                    </div>
                    <p className="text-xs text-gray-600 mb-3">{bestFramework.description}</p>
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div 
                        className={`bg-gradient-to-r ${bestFramework.color} h-2 rounded-full transition-all duration-500`}
                        style={{ width: `${(bestFramework.score / 10) * 100}%` }}
                      ></div>
                    </div>
                    <p className="text-xs text-gray-500 mt-1">
                      {bestFramework.score >= 8 ? 'Excellent performance' : 
                       bestFramework.score >= 6 ? 'Good performance' : 
                       bestFramework.score >= 4 ? 'Fair performance' : 'Needs improvement'}
                    </p>
                  </div>
                );
              })()}
            </div>

            {/* Highest Conversion Framework */}
            <div className="bg-white rounded-lg p-4 border border-gray-200 shadow-sm">
              <div className="text-center mb-3">
                <h3 className="text-lg font-bold text-gray-900 mb-1">Best Conversion Framework</h3>
                <p className="text-gray-600 text-xs">Highest performing persuasion framework</p>
              </div>
              
              {(() => {
                const aidaAnalysis = getAIDAAnalysis();
                const pasAnalysis = getPASAnalysis();
                const babAnalysis = getBABAnalysis();
                
                const frameworks = [
                  { name: 'AIDA', score: aidaAnalysis.overall, description: 'Attention → Interest → Desire → Action', color: 'from-gray-500 to-blue-500', bgColor: 'bg-gray-50', borderColor: 'border-gray-200' },
                  { name: 'PAS', score: pasAnalysis.overall, description: 'Problem → Agitate → Solution', color: 'from-red-500 to-pink-500', bgColor: 'bg-red-50', borderColor: 'border-red-200' },
                  { name: 'BAB', score: babAnalysis.overall, description: 'Before → After → Bridge', color: 'from-teal-500 to-cyan-500', bgColor: 'bg-teal-50', borderColor: 'border-teal-200' }
                ];
                
                const bestFramework = frameworks.reduce((best, current) => 
                  current.score > best.score ? current : best
                );
                
                return (
                  <div className="text-center">
                    <div className={`inline-flex items-center px-3 py-1 rounded-md bg-gradient-to-r ${bestFramework.color} text-white mb-2`}>
                      <span className="font-bold text-sm">{bestFramework.name}</span>
                    </div>
                    <div className="text-2xl font-bold text-gray-900 mb-1">
                      {bestFramework.score.toFixed(1)}/10
                    </div>
                    <p className="text-xs text-gray-600 mb-3">{bestFramework.description}</p>
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div 
                        className={`bg-gradient-to-r ${bestFramework.color} h-2 rounded-full transition-all duration-500`}
                        style={{ width: `${(bestFramework.score / 10) * 100}%` }}
                      ></div>
                    </div>
                    <p className="text-xs text-gray-500 mt-1">
                      {bestFramework.score >= 8 ? 'Excellent performance' : 
                       bestFramework.score >= 6 ? 'Good performance' : 
                       bestFramework.score >= 4 ? 'Fair performance' : 'Needs improvement'}
                    </p>
                  </div>
                );
              })()}
            </div>
          </div>

          {/* Framework Comparison Insights */}
          <div className="mt-4 bg-white rounded-lg p-4 border border-gray-200 shadow-sm">
            <h4 className="text-base font-semibold text-gray-900 mb-3 text-center">Framework Performance Insights</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="text-center">
                <h5 className="font-semibold text-gray-900 mb-2">Storytelling Strength</h5>
                <p className="text-sm text-gray-600">
                  {(() => {
                    const hrrAnalysis = getHRRAnalysis();
                    const threeActAnalysis = get3ActAnalysis();
                    const heroAnalysis = getHeroJourneyAnalysis();
                    const storyArcAnalysis = getStoryArcAnalysis();
                    
                    const frameworks = [
                      { name: 'HRR', score: hrrAnalysis.overall },
                      { name: '3-Act Structure', score: threeActAnalysis.overall },
                      { name: "Hero's Journey", score: heroAnalysis.overall },
                      { name: 'Story Arc', score: storyArcAnalysis.overall }
                    ];
                    
                    const bestFramework = frameworks.reduce((best, current) => 
                      current.score > best.score ? current : best
                    );
                    
                    const avgScore = frameworks.reduce((sum, f) => sum + f.score, 0) / frameworks.length;
                    
                    return `${bestFramework.name} leads your brand storytelling with ${bestFramework.score.toFixed(1)}/10. Average storytelling performance: ${avgScore.toFixed(1)}/10.`;
                  })()}
                </p>
              </div>
              
              <div className="text-center">
                <h5 className="font-semibold text-gray-900 mb-2">Conversion Strength</h5>
                <p className="text-sm text-gray-600">
                  {(() => {
                    const aidaAnalysis = getAIDAAnalysis();
                    const pasAnalysis = getPASAnalysis();
                    const babAnalysis = getBABAnalysis();
                    
                    const frameworks = [
                      { name: 'AIDA', score: aidaAnalysis.overall },
                      { name: 'PAS', score: pasAnalysis.overall },
                      { name: 'BAB', score: babAnalysis.overall }
                    ];
                    
                    const bestFramework = frameworks.reduce((best, current) => 
                      current.score > best.score ? current : best
                    );
                    
                    const avgScore = frameworks.reduce((sum, f) => sum + f.score, 0) / frameworks.length;
                    
                    return `${bestFramework.name} leads your conversion strategy with ${bestFramework.score.toFixed(1)}/10. Average conversion performance: ${avgScore.toFixed(1)}/10.`;
                  })()}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Cross-Framework Analysis Heading */}
      <div className="text-center mb-8">
        <h2 className="text-2xl font-bold text-gray-900 mb-2">Cross-Framework Analysis</h2>
        <p className="text-gray-600">Compare and analyze your video across multiple marketing and storytelling frameworks</p>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200 mb-8">
        <nav className="-mb-px flex space-x-8">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === tab.id
                  ? 'border-primary-500 text-primary-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              {tab.icon}
              <span className="ml-2">{tab.label}</span>
            </button>
          ))}
        </nav>
      </div>

      {/* Tab Content */}
      {activeTab === 'overview' && (
        <div className="space-y-8">

          {/* Performance Timeline */}
          <AnalysisTimeline 
            review={review}
            platform={platform}
            metadata={data}
          />

          {/* Strengths and Improvements */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            <div className="card">
              <div className="flex items-center mb-4">
                <CheckCircle className="w-6 h-6 text-green-600 mr-3" />
                <h3 className="text-lg font-semibold text-gray-900">Strengths</h3>
              </div>
              <ul className="space-y-3">
                {(review.strengths || review.videoAnalysis?.strengths || []).map((strength, index) => (
                  <li key={index} className="flex items-start">
                    <div className="w-2 h-2 bg-green-500 rounded-full mt-2 mr-3 flex-shrink-0"></div>
                    <span className="text-gray-700">{strength}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div className="card">
              <div className="flex items-center mb-4">
                <AlertTriangle className="w-6 h-6 text-yellow-600 mr-3" />
                <h3 className="text-lg font-semibold text-gray-900">Areas for Improvement</h3>
              </div>
              <ul className="space-y-3">
                {(review.improvements || review.videoAnalysis?.improvements || []).map((improvement, index) => (
                  <li key={index} className="flex items-start">
                    <div className="w-2 h-2 bg-yellow-500 rounded-full mt-2 mr-3 flex-shrink-0"></div>
                    <span className="text-gray-700">{improvement}</span>
                  </li>
                ))}
              </ul>
                </div>
              </div>
              
          {/* Actionable Recommendations */}
          <div className="card">
            <div className="flex items-center mb-6">
              <Lightbulb className="w-6 h-6 text-yellow-600 mr-3" />
              <h3 className="text-lg font-semibold text-gray-900">Actionable Recommendations</h3>
            </div>
            
            <div className="space-y-4">
              {(review.recommendations || review.videoAnalysis?.recommendations || []).map((recommendation, index) => (
                <div key={index} className="flex items-start p-4 bg-gray-50 rounded-lg">
                  <div className="flex items-center justify-center w-8 h-8 bg-primary-100 text-primary-600 rounded-full mr-4 flex-shrink-0">
                    <span className="text-sm font-semibold">{index + 1}</span>
                  </div>
                  <p className="text-gray-700">{recommendation}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Compliance and Audience Analysis */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            <div className="card">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Compliance Assessment</h3>
              <p className="text-gray-700">{review.compliance || review.videoAnalysis?.compliance || 'No compliance information available'}</p>
            </div>

            <div className="card">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Audience Analysis</h3>
              <p className="text-gray-700">{review.audienceAnalysis || review.videoAnalysis?.audienceAnalysis || 'No audience analysis available'}</p>
            </div>
          </div>



          {/* Detailed Analysis */}
          <div className="card">
            <div className="text-center mb-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Detailed Analysis</h3>
              <p className="text-gray-600">Comprehensive scoring and insights across all performance criteria</p>
            </div>
            
            
            <div className="space-y-6">
              {criteria.map((criterion, index) => (
                <div key={index} className="border border-gray-200 rounded-lg p-4">
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex-1">
                      <h4 className="text-lg font-semibold text-gray-900 mb-2">
                        {criterion.name}
                      </h4>
                      <p className="text-gray-600 mb-3">{criterion.explanation}</p>
                      <div className="flex items-center space-x-4">
                        <div className="flex items-center space-x-2">
                          <span className="text-sm text-gray-500">Score:</span>
                          <div className={`score-badge ${getScoreColor(criterion.score)}`}>
                            {criterion.score.toFixed(1)}/10
                          </div>
                        </div>
                        <div className="flex items-center space-x-2">
                          <span className="text-sm text-gray-500">Status:</span>
                          <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                            criterion.score >= 8 ? 'bg-green-100 text-green-800' :
                            criterion.score >= 5 ? 'bg-amber-100 text-amber-800' : 'bg-red-100 text-red-800'
                          }`}>
                            {criterion.score >= 8 ? 'Acceptable' : criterion.score >= 5 ? 'Needs Improvement' : 'Critical'}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  <div className="border-t border-gray-200 pt-4">
                    <h5 className="font-medium text-gray-900 mb-2">Recommendations:</h5>
                    <p className="text-gray-700">{criterion.recommendations}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Platform Insights */}
          <div className="space-y-6">
          <div className="card">
            <h3 className="text-lg font-semibold text-gray-900 mb-6">
              {platformInfo.name} Best Practices
            </h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div>
                <h4 className="font-medium text-gray-900 mb-4">Evaluation Criteria</h4>
                <ul className="space-y-2">
                  {platformInfo.criteria.map((criterion, index) => (
                    <li key={index} className="flex items-start">
                      <div className="w-2 h-2 bg-primary-500 rounded-full mt-2 mr-3 flex-shrink-0"></div>
                      <span className="text-gray-700 text-sm">{criterion}</span>
                    </li>
                  ))}
                </ul>
              </div>
              
              <div>
                <h4 className="font-medium text-gray-900 mb-4">Best Practices</h4>
                <ul className="space-y-2">
                  {platformInfo.bestPractices.map((practice, index) => (
                    <li key={index} className="flex items-start">
                      <div className="w-2 h-2 bg-green-500 rounded-full mt-2 mr-3 flex-shrink-0"></div>
                      <span className="text-gray-700 text-sm">{practice}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>

          <div className="card">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Platform-Specific Insights</h3>
            <p className="text-gray-700">{review.platformSpecific || review.videoAnalysis?.platformSpecific || 'No platform-specific information available'}</p>
            </div>
          </div>


        </div>
      )}

      {activeTab === 'conversion' && (
        <div className="space-y-8">
          {/* Conversion Framework Comparison Overview */}
          <div className="mb-8">
            <div className="bg-gradient-to-br from-gray-50 via-blue-50 to-indigo-50 rounded-2xl p-8 border border-gray-200 shadow-lg">
              <div className="text-center mb-6">
                <div>
                  <h2 className="text-2xl font-bold text-gray-900">Conversion Framework Comparison</h2>
                  <p className="text-gray-600">Compare AIDA, PAS, and BAB frameworks to optimize your conversion strategy</p>
                  {(() => {
                    const aidaAnalysis = getAIDAAnalysis();
                    const pasAnalysis = getPASAnalysis();
                    const babAnalysis = getBABAnalysis();
                    
                    const frameworks = [
                      { name: 'AIDA', score: aidaAnalysis.overall, color: 'from-gray-500 to-blue-500', bgColor: 'bg-gray-50', borderColor: 'border-gray-200' },
                      { name: 'PAS', score: pasAnalysis.overall, color: 'from-gray-500 to-blue-500', bgColor: 'bg-gray-50', borderColor: 'border-gray-200' },
                      { name: 'BAB', score: babAnalysis.overall, color: 'from-gray-500 to-blue-500', bgColor: 'bg-gray-50', borderColor: 'border-gray-200' }
                    ];
                    
                    const bestFramework = frameworks.reduce((best, current) => 
                      current.score > best.score ? current : best
                    );
                    
                    return (
                      <div className="mt-6">
                        <div className="text-center mb-4">
                          <span className="text-sm text-gray-600">Best Performing Framework:</span>
                          <div className={`inline-flex items-center px-4 py-2 rounded-full text-white font-semibold bg-gradient-to-r ${bestFramework.color} mt-2`}>
                            {bestFramework.name} ({bestFramework.score.toFixed(1)}/10)
                          </div>
                        </div>
                      </div>
                    );
                  })()}
                </div>
              </div>
            </div>
          </div>

          {/* Framework Comparison Cards */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
            {/* AIDA Card */}
            {(() => {
              const aidaAnalysis = getAIDAAnalysis();
              const { attention, interest, desire, action, overall } = aidaAnalysis;
              
              return (
                <div className="bg-gradient-to-br from-gray-50 to-blue-50 rounded-2xl p-6 border border-gray-200 shadow-lg">
                  <div className="text-center mb-6">
                    <div className="w-12 h-12 bg-gradient-to-r from-gray-500 to-blue-500 rounded-lg flex items-center justify-center mx-auto mb-4">
                      <Target className="w-6 h-6 text-white" />
                    </div>
                    <h3 className="text-xl font-bold text-gray-900 mb-2">AIDA Framework</h3>
                    <p className="text-gray-600 text-sm mb-4">Attention → Interest → Desire → Action</p>
                    
                    <div className="flex items-center justify-center mb-4">
                      <span className="text-3xl font-bold text-gray-900">{overall}/10</span>
                      <div className="ml-3">
                        <div className="w-20 bg-gray-200 rounded-full h-2">
                          <div 
                            className="bg-gradient-to-r from-gray-500 to-blue-500 h-2 rounded-full transition-all duration-500"
                            style={{ width: `${overall * 10}%` }}
                          ></div>
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  <div className="space-y-3">
                    <div className="flex justify-between items-center">
                      <span className="text-sm font-medium text-gray-700">Attention</span>
                      <span className={`text-sm font-bold ${attention.score >= 8 ? 'text-green-600' : attention.score >= 5 ? 'text-amber-600' : 'text-red-600'}`}>{attention.score.toFixed(1)}/10</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-sm font-medium text-gray-700">Interest</span>
                      <span className={`text-sm font-bold ${interest.score >= 8 ? 'text-green-600' : interest.score >= 5 ? 'text-amber-600' : 'text-red-600'}`}>{interest.score.toFixed(1)}/10</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-sm font-medium text-gray-700">Desire</span>
                      <span className={`text-sm font-bold ${desire.score >= 8 ? 'text-green-600' : desire.score >= 5 ? 'text-amber-600' : 'text-red-600'}`}>{desire.score.toFixed(1)}/10</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-sm font-medium text-gray-700">Action</span>
                      <span className={`text-sm font-bold ${action.score >= 8 ? 'text-green-600' : action.score >= 5 ? 'text-amber-600' : 'text-red-600'}`}>{action.score.toFixed(1)}/10</span>
                    </div>
                  </div>
                </div>
              );
            })()}

            {/* PAS Card */}
            {(() => {
              const pasAnalysis = getPASAnalysis();
              const { problem, agitate, solution, overall } = pasAnalysis;
              
              return (
                <div className="bg-gradient-to-br from-gray-50 to-blue-50 rounded-2xl p-6 border border-gray-200 shadow-lg">
                  <div className="text-center mb-6">
                    <div className="w-12 h-12 bg-gradient-to-r from-gray-500 to-blue-500 rounded-lg flex items-center justify-center mx-auto mb-4">
                      <Zap className="w-6 h-6 text-white" />
                    </div>
                    <h3 className="text-xl font-bold text-gray-900 mb-2">PAS Framework</h3>
                    <p className="text-gray-600 text-sm mb-4">Problem → Agitate → Solution</p>
                    
                    <div className="flex items-center justify-center mb-4">
                      <span className="text-3xl font-bold text-gray-900">{overall}/10</span>
                      <div className="ml-3">
                        <div className="w-20 bg-gray-200 rounded-full h-2">
                          <div 
                            className="bg-gradient-to-r from-gray-500 to-blue-500 h-2 rounded-full transition-all duration-500"
                            style={{ width: `${overall * 10}%` }}
                          ></div>
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  <div className="space-y-3">
                    <div className="flex justify-between items-center">
                      <span className="text-sm font-medium text-gray-700">Problem</span>
                      <span className={`text-sm font-bold ${problem.score >= 8 ? 'text-green-600' : problem.score >= 5 ? 'text-amber-600' : 'text-red-600'}`}>{problem.score.toFixed(1)}/10</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-sm font-medium text-gray-700">Agitate</span>
                      <span className={`text-sm font-bold ${agitate.score >= 8 ? 'text-green-600' : agitate.score >= 5 ? 'text-amber-600' : 'text-red-600'}`}>{agitate.score.toFixed(1)}/10</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-sm font-medium text-gray-700">Solution</span>
                      <span className={`text-sm font-bold ${solution.score >= 8 ? 'text-green-600' : solution.score >= 5 ? 'text-amber-600' : 'text-red-600'}`}>{solution.score.toFixed(1)}/10</span>
                    </div>
                  </div>
                </div>
              );
            })()}

            {/* BAB Card */}
            {(() => {
              const babAnalysis = getBABAnalysis();
              const { before, after, bridge, overall } = babAnalysis;
              
              return (
                <div className="bg-gradient-to-br from-gray-50 to-blue-50 rounded-2xl p-6 border border-gray-200 shadow-lg">
                  <div className="text-center mb-6">
                    <div className="w-12 h-12 bg-gradient-to-r from-gray-500 to-blue-500 rounded-lg flex items-center justify-center mx-auto mb-4">
                      <TrendingUp className="w-6 h-6 text-white" />
                    </div>
                    <h3 className="text-xl font-bold text-gray-900 mb-2">BAB Framework</h3>
                    <p className="text-gray-600 text-sm mb-4">Before → After → Bridge</p>
                    
                    <div className="flex items-center justify-center mb-4">
                      <span className="text-3xl font-bold text-gray-900">{overall}/10</span>
                      <div className="ml-3">
                        <div className="w-20 bg-gray-200 rounded-full h-2">
                          <div 
                            className="bg-gradient-to-r from-gray-500 to-blue-500 h-2 rounded-full transition-all duration-500"
                            style={{ width: `${overall * 10}%` }}
                          ></div>
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  <div className="space-y-3">
                    <div className="flex justify-between items-center">
                      <span className="text-sm font-medium text-gray-700">Before</span>
                      <span className={`text-sm font-bold ${before.score >= 8 ? 'text-green-600' : before.score >= 5 ? 'text-amber-600' : 'text-red-600'}`}>{before.score.toFixed(1)}/10</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-sm font-medium text-gray-700">After</span>
                      <span className={`text-sm font-bold ${after.score >= 8 ? 'text-green-600' : after.score >= 5 ? 'text-amber-600' : 'text-red-600'}`}>{after.score.toFixed(1)}/10</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-sm font-medium text-gray-700">Bridge</span>
                      <span className={`text-sm font-bold ${bridge.score >= 8 ? 'text-green-600' : bridge.score >= 5 ? 'text-amber-600' : 'text-red-600'}`}>{bridge.score.toFixed(1)}/10</span>
                    </div>
                  </div>
                </div>
              );
            })()}
          </div>


          {/* Conversion Insights & Recommendations */}
          <div className="bg-white rounded-2xl p-8 border border-gray-200 shadow-lg mb-8">
            <div className="text-center mb-6">
              <h3 className="text-2xl font-bold text-gray-900 mb-2">Conversion Insights & Recommendations</h3>
              <p className="text-gray-600">Professional analysis of your video's conversion framework performance</p>
            </div>
            
            <div className="prose prose-lg max-w-none">
              {(() => {
                const aidaAnalysis = getAIDAAnalysis();
                const pasAnalysis = getPASAnalysis();
                const babAnalysis = getBABAnalysis();
                
                const frameworks = [
                  { name: 'AIDA', score: aidaAnalysis.overall, analysis: aidaAnalysis },
                  { name: 'PAS', score: pasAnalysis.overall, analysis: pasAnalysis },
                  { name: 'BAB', score: babAnalysis.overall, analysis: babAnalysis }
                ];
                
                const sortedFrameworks = frameworks.sort((a, b) => b.score - a.score);
                const bestFramework = sortedFrameworks[0];
                const worstFramework = sortedFrameworks[sortedFrameworks.length - 1];
                
                const getPerformanceLevel = (score) => {
                  if (score >= 8) return { level: 'excellent', description: 'outstanding', color: 'text-green-600' };
                  if (score >= 6) return { level: 'good', description: 'solid', color: 'text-blue-600' };
                  if (score >= 4) return { level: 'fair', description: 'moderate', color: 'text-yellow-600' };
                  return { level: 'needs improvement', description: 'limited', color: 'text-red-600' };
                };
                
                const bestPerf = getPerformanceLevel(bestFramework.score);
                const worstPerf = getPerformanceLevel(worstFramework.score);
                
                return (
                  <div className="space-y-6">
                    {/* Overall Performance Summary */}
                    <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg p-6 border border-blue-200">
                      <h4 className="text-lg font-semibold text-gray-900 mb-3">Overall Performance Summary</h4>
                      <p className="text-gray-700 leading-relaxed">
                        Your video demonstrates <span className={`font-semibold ${bestPerf.color}`}>{bestPerf.description}</span> performance 
                        across conversion frameworks, with <strong>{bestFramework.name}</strong> leading at {bestFramework.score.toFixed(1)}/10. 
                        {bestFramework.score >= 7 ? 
                          ` This indicates strong conversion potential and effective use of the ${bestFramework.name} methodology.` :
                          ` While there's room for improvement, the ${bestFramework.name} framework shows the most promise for optimization.`
                        }
                      </p>
                    </div>
                    
                    {/* Framework-Specific Insights */}
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                      {frameworks.map((framework, index) => {
                        const perf = getPerformanceLevel(framework.score);
                        const isBest = framework.name === bestFramework.name;
                        const isWorst = framework.name === worstFramework.name;
                        
                        return (
                          <div key={index} className={`rounded-lg p-4 border ${
                            isBest ? 'bg-green-50 border-green-200' : 
                            isWorst ? 'bg-amber-50 border-amber-200' : 
                            'bg-gray-50 border-gray-200'
                          }`}>
                            <div className="flex items-center justify-between mb-3">
                              <h5 className="font-semibold text-gray-900">{framework.name}</h5>
                              <span className={`text-sm font-bold ${perf.color}`}>{framework.score}/10</span>
                            </div>
                            
                            {framework.name === 'AIDA' && (
                              <div className="text-sm text-gray-600 space-y-1">
                                <p><strong>Attention:</strong> {framework.analysis.attention.score}/10</p>
                                <p><strong>Interest:</strong> {framework.analysis.interest.score}/10</p>
                                <p><strong>Desire:</strong> {framework.analysis.desire.score}/10</p>
                                <p><strong>Action:</strong> {framework.analysis.action.score}/10</p>
                              </div>
                            )}
                            
                            {framework.name === 'PAS' && (
                              <div className="text-sm text-gray-600 space-y-1">
                                <p><strong>Problem:</strong> {framework.analysis.problem.score}/10</p>
                                <p><strong>Agitate:</strong> {framework.analysis.agitate.score}/10</p>
                                <p><strong>Solution:</strong> {framework.analysis.solution.score}/10</p>
                              </div>
                            )}
                            
                            {framework.name === 'BAB' && (
                              <div className="text-sm text-gray-600 space-y-1">
                                <p><strong>Before:</strong> {framework.analysis.before.score}/10</p>
                                <p><strong>After:</strong> {framework.analysis.after.score}/10</p>
                                <p><strong>Bridge:</strong> {framework.analysis.bridge.score}/10</p>
                              </div>
                            )}
                            
                            {isBest && (
                              <div className="mt-2 text-xs text-green-700 font-medium">
                                ✓ Best performing framework
                              </div>
                            )}
                            
                            {isWorst && (
                              <div className="mt-2 text-xs text-amber-700 font-medium">
                                ⚠ Needs most attention
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                    
                    {/* Strategic Recommendations */}
                    <div className="bg-gradient-to-r from-purple-50 to-pink-50 rounded-lg p-6 border border-purple-200">
                      <h4 className="text-lg font-semibold text-gray-900 mb-3">Strategic Recommendations</h4>
                      <div className="space-y-3 text-gray-700">
                        {bestFramework.score >= 7 ? (
                          <p className="leading-relaxed">
                            <strong>Leverage Your Strength:</strong> Your {bestFramework.name} performance is excellent. 
                            Consider doubling down on this approach and using it as a template for future content. 
                            The strong performance suggests your audience responds well to this particular conversion methodology.
                          </p>
                        ) : (
                          <p className="leading-relaxed">
                            <strong>Focus on Improvement:</strong> While no framework is performing at an optimal level, 
                            {bestFramework.name} shows the most promise. Prioritize strengthening the weakest elements 
                            of this framework to create a more compelling conversion experience.
                          </p>
                        )}
                        
                        {worstFramework.score < 6 && (
                          <p className="leading-relaxed">
                            <strong>Address Weaknesses:</strong> The {worstFramework.name} framework needs significant attention. 
                            Consider whether this approach aligns with your brand and audience, or if it requires 
                            fundamental restructuring to better serve your conversion goals.
                          </p>
                        )}
                        
                        <p className="leading-relaxed">
                          <strong>Holistic Approach:</strong> The best conversion strategies often combine elements from multiple frameworks. 
                          Consider how you might integrate the strongest aspects of each approach to create a more 
                          comprehensive and effective conversion experience.
                        </p>
                      </div>
                    </div>
                    
                    {/* Next Steps */}
                    <div className="bg-gradient-to-r from-gray-50 to-blue-50 rounded-lg p-6 border border-gray-200">
                      <h4 className="text-lg font-semibold text-gray-900 mb-3">Recommended Next Steps</h4>
                      {(() => {
                        const avgScore = Math.round(criteria.reduce((sum, c) => sum + (c.score || 0), 0) / criteria.length);
                        const lowestScoring = criteria.filter(c => c.score < 6);
                        const highestScoring = criteria.filter(c => c.score >= 8);
                        
                        return (
                          <div className="space-y-4">
                            {lowestScoring.length > 0 && (
                              <div className="bg-white rounded-lg p-4 border border-red-200">
                                <h5 className="font-semibold text-red-800 mb-2 flex items-center">
                                  <AlertTriangle className="w-4 h-4 mr-2" />
                                  Priority Improvements ({lowestScoring.length} elements below 6/10)
                                </h5>
                                <ul className="text-sm text-gray-700 space-y-1">
                                  {lowestScoring.slice(0, 3).map((item, idx) => (
                                    <li key={idx}>• <strong>{item.name}</strong> (Score: {item.score}/10) - {item.recommendations}</li>
                                  ))}
                                </ul>
                              </div>
                            )}
                            
                            {highestScoring.length > 0 && (
                              <div className="bg-white rounded-lg p-4 border border-green-200">
                                <h5 className="font-semibold text-green-800 mb-2 flex items-center">
                                  <CheckCircle className="w-4 h-4 mr-2" />
                                  Leverage Your Strengths ({highestScoring.length} elements scoring 8+/10)
                                </h5>
                                <ul className="text-sm text-gray-700 space-y-1">
                                  <li>• Create templates based on your strongest elements: {highestScoring.slice(0, 2).map(h => h.name).join(', ')}</li>
                                  <li>• Apply these successful patterns to future content</li>
                                </ul>
                              </div>
                            )}
                            
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                              <div>
                                <h5 className="font-semibold text-gray-800 mb-2">Priority Actions</h5>
                                <ul className="text-sm text-gray-600 space-y-1">
                                  <li>• {avgScore < 6 ? 'Focus on foundational storytelling elements first' : avgScore < 8 ? 'Optimize your weakest-performing elements' : 'Test variations of your strongest elements'}</li>
                                  <li>• {platformInfo?.platform ? `Apply ${platformInfo.platform} best practices` : 'Test different engagement approaches'}</li>
                                  <li>• A/B test the improvements soon after implementation</li>
                                </ul>
                              </div>
                              <div>
                                <h5 className="font-semibold text-gray-800 mb-2">Strategic Actions</h5>
                                <ul className="text-sm text-gray-600 space-y-1">
                                  <li>• Develop content templates based on this analysis</li>
                                  <li>• Monitor performance improvements over time</li>
                                  <li>• Create a systematic approach to video optimization</li>
                                </ul>
                              </div>
                            </div>
                          </div>
                        );
                      })()}
                    </div>
                  </div>
                );
              })()}
            </div>
          </div>

          {/* Conversion Insights */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="card">
              <div className="flex items-center mb-4">
                <div className="w-8 h-8 bg-green-100 rounded-lg flex items-center justify-center mr-3">
                  <span className="text-green-600 font-bold">✓</span>
                </div>
                <h3 className="text-lg font-semibold text-gray-900">Conversion Strengths</h3>
              </div>
              <div className="space-y-3">
                {(() => {
                  const aidaAnalysis = getAIDAAnalysis();
                  const pasAnalysis = getPASAnalysis();
                  const babAnalysis = getBABAnalysis();
                  
                  const strengths = [];
                  
                  if (aidaAnalysis.overall >= 7) {
                    strengths.push({
                      framework: 'AIDA',
                      description: 'Strong attention-grabbing and conversion flow',
                      color: 'text-orange-700',
                      bgColor: 'bg-orange-50',
                      borderColor: 'border-orange-200'
                    });
                  }
                  
                  if (pasAnalysis.overall >= 7) {
                    strengths.push({
                      framework: 'PAS',
                      description: 'Effective problem identification and solution positioning',
                      color: 'text-blue-700',
                      bgColor: 'bg-blue-50',
                      borderColor: 'border-blue-200'
                    });
                  }
                  
                  if (babAnalysis.overall >= 7) {
                    strengths.push({
                      framework: 'BAB',
                      description: 'Clear transformation narrative and bridge positioning',
                      color: 'text-green-700',
                      bgColor: 'bg-green-50',
                      borderColor: 'border-green-200'
                    });
                  }
                  
                  if (strengths.length === 0) {
                    return (
                      <div className="text-center py-4">
                        <p className="text-gray-500 text-sm">Focus on improving conversion frameworks to build stronger persuasion.</p>
                      </div>
                    );
                  }
                  
                  return strengths.map((strength, index) => (
                    <div key={index} className={`p-3 rounded-lg border ${strength.bgColor} ${strength.borderColor}`}>
                      <div className="flex items-center mb-1">
                        <span className={`font-semibold text-sm ${strength.color}`}>{strength.framework}</span>
                      </div>
                      <p className="text-sm text-gray-600">{strength.description}</p>
                    </div>
                  ));
                })()}
              </div>
            </div>

            <div className="card">
              <div className="flex items-center mb-4">
                <div className="w-8 h-8 bg-amber-100 rounded-lg flex items-center justify-center mr-3">
                  <span className="text-amber-600 font-bold">!</span>
                </div>
                <h3 className="text-lg font-semibold text-gray-900">Conversion Improvements</h3>
              </div>
              <div className="space-y-3">
                {(() => {
                  const aidaAnalysis = getAIDAAnalysis();
                  const pasAnalysis = getPASAnalysis();
                  const babAnalysis = getBABAnalysis();
                  
                  const improvements = [];
                  
                  if (aidaAnalysis.overall < 7) {
                    improvements.push({
                      framework: 'AIDA',
                      description: 'Strengthen attention-grabbing and conversion flow elements',
                      color: 'text-orange-700',
                      bgColor: 'bg-orange-50',
                      borderColor: 'border-orange-200'
                    });
                  }
                  
                  if (pasAnalysis.overall < 7) {
                    improvements.push({
                      framework: 'PAS',
                      description: 'Improve problem identification and solution positioning',
                      color: 'text-blue-700',
                      bgColor: 'bg-blue-50',
                      borderColor: 'border-blue-200'
                    });
                  }
                  
                  if (babAnalysis.overall < 7) {
                    improvements.push({
                      framework: 'BAB',
                      description: 'Enhance transformation narrative and bridge positioning',
                      color: 'text-green-700',
                      bgColor: 'bg-green-50',
                      borderColor: 'border-green-200'
                    });
                  }
                  
                  if (improvements.length === 0) {
                    return (
                      <div className="text-center py-4">
                        <p className="text-gray-500 text-sm">Great job! All conversion frameworks are performing well.</p>
                      </div>
                    );
                  }
                  
                  return improvements.map((improvement, index) => (
                    <div key={index} className={`p-3 rounded-lg border ${improvement.bgColor} ${improvement.borderColor}`}>
                      <div className="flex items-center mb-1">
                        <span className={`font-semibold text-sm ${improvement.color}`}>{improvement.framework}</span>
                      </div>
                      <p className="text-sm text-gray-600">{improvement.description}</p>
                    </div>
                  ));
                })()}
              </div>
            </div>
          </div>
        </div>
      )}

      {activeTab === 'article' && (
        <div ref={articleRef} className="p-8 max-w-4xl mx-auto bg-white">
          {/* Magazine Article Header */}
          <div className="mb-8 pb-6 border-b-2 border-gray-200">
            <div className="text-xs text-indigo-600 font-semibold mb-2 tracking-wider uppercase">
              Professional Video Analysis Report
            </div>
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4 leading-tight">
              {generateAdvancedHeadline()}
            </h1>
            <div className="flex items-center text-gray-600 text-sm space-x-4 flex-wrap">
              <div className="flex items-center">
                <Eye className="w-4 h-4 mr-1" />
                <span>Professional Analysis</span>
              </div>
              <span>•</span>
              <div className="flex items-center">
                <span>{new Date().toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}</span>
              </div>
              <span>•</span>
              <div className={`px-2 py-1 rounded text-xs font-semibold ${
                overallScore >= 8 ? 'bg-green-100 text-green-700' :
                overallScore >= 5 ? 'bg-yellow-100 text-yellow-700' : 'bg-red-100 text-red-700'
              }`}>
                {overallScore}/10 Overall Score
              </div>
            </div>
          </div>

          {/* Hero Image - First Frame */}
          {data.videoFile && (
            <div className="mb-6 float-right ml-4 w-64">
              <div className="relative rounded-lg overflow-hidden shadow-md">
                <video 
                  className="w-full h-auto"
                  preload="metadata"
                  muted
                  playsInline
                  onLoadedMetadata={(e) => {
                    e.target.currentTime = 0.001;
                  }}
                >
                  <source src={`/uploads/${data.videoFile.filename}`} type={data.videoFile.mimetype} />
                </video>
                <div className="absolute bottom-0 left-0 right-0 bg-black bg-opacity-70 text-white text-xs px-2 py-1">
                  Opening Frame
                </div>
              </div>
            </div>
          )}

          {/* Lead Paragraph */}
          <div className="prose prose-lg max-w-none mb-8">
            <p className="text-xl text-gray-700 leading-relaxed font-serif">
              {generateAdvancedLeadParagraph()}
            </p>
          </div>

          {/* Pull Quote - Top Strength */}
          {(() => {
            const storyArcAnalysis = getStoryArcAnalysis();
            if (storyArcAnalysis.hook.criteria.length > 0) {
              const topCriterion = storyArcAnalysis.hook.criteria[0];
              return (
                <div className="my-8 border-l-4 border-indigo-600 bg-indigo-50 p-6 rounded-r-lg">
                  <Quote className="w-8 h-8 text-indigo-600 mb-2" />
                  <p className="text-xl font-serif italic text-gray-800 leading-relaxed">
                    "{topCriterion.explanation}"
                  </p>
                  <p className="text-sm text-gray-600 mt-2">— {topCriterion.name}</p>
                </div>
              );
            }
          })()}

          {/* Section 1: The Hook - Critical First 3 Seconds */}
          <div className="mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              The 3-Second Rule: Your Hook's Performance
            </h2>
            <div className="h-1 w-20 bg-purple-600 mb-6"></div>
            
            {(() => {
              const storyArcAnalysis = getStoryArcAnalysis();
              return (
                <>
                  <p className="text-gray-700 leading-relaxed mb-6">
                    {generateHookAnalysis(storyArcAnalysis.hook.score, storyArcAnalysis.hook.criteria)}
                  </p>

                  {storyArcAnalysis.hook.criteria.length > 0 && (
                    <div className="bg-purple-50 border-l-4 border-purple-500 p-6 rounded-r-lg mb-6">
                      <h4 className="font-semibold text-gray-900 mb-3">Hook Analysis:</h4>
                      <ul className="space-y-3">
                        {storyArcAnalysis.hook.criteria.map((criterion, index) => (
                          <li key={index}>
                            <span className="font-medium text-gray-900">{criterion.name}: </span>
                            <span className="text-gray-700">{criterion.explanation}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </>
              );
            })()}
          </div>

          {/* Section 2: Story Arc with Mid-Frame */}
          <div className="mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              The Narrative Arc: From Setup to Payoff
            </h2>
            <div className="h-1 w-20 bg-blue-600 mb-6"></div>

            {/* Story Arc Visualization with Frame */}
            {data.videoFile && (
              <div className="mb-4 float-right ml-4 w-48">
                <div className="relative rounded-lg overflow-hidden shadow-md">
                  <video 
                    className="w-full h-auto"
                    preload="metadata"
                    muted
                    playsInline
                    onLoadedMetadata={(e) => {
                      const duration = e.target.duration;
                      if (duration && duration > 0) {
                        e.target.currentTime = duration * 0.5;
                      }
                    }}
                  >
                    <source src={`/uploads/${data.videoFile.filename}`} type={data.videoFile.mimetype} />
                  </video>
                  <div className="absolute bottom-0 left-0 right-0 bg-black bg-opacity-70 text-white text-xs px-2 py-1">
                    Mid-point
                  </div>
                </div>
              </div>
            )}

            {(() => {
              const storyArcAnalysis = getStoryArcAnalysis();
              return (
                <>
                  <p className="text-gray-700 leading-relaxed mb-6">
                    Your story arc scored an overall <strong className="text-blue-600">{storyArcAnalysis.overall.toFixed(1)}/10</strong> across 
                    five critical narrative moments: Hook ({storyArcAnalysis.hook.score}), Problem ({storyArcAnalysis.problem.score}), 
                    Tension ({storyArcAnalysis.tension.score}), Climax ({storyArcAnalysis.climax.score}), and 
                    Resolution ({storyArcAnalysis.resolution.score}).
                  </p>

                  <div className="grid grid-cols-5 gap-3 mb-6">
                    {[
                      { name: 'Hook', score: storyArcAnalysis.hook.score, color: 'purple' },
                      { name: 'Problem', score: storyArcAnalysis.problem.score, color: 'blue' },
                      { name: 'Tension', score: storyArcAnalysis.tension.score, color: 'orange' },
                      { name: 'Climax', score: storyArcAnalysis.climax.score, color: 'red' },
                      { name: 'Resolution', score: storyArcAnalysis.resolution.score, color: 'green' }
                    ].map((stage) => (
                      <div key={stage.name} className={`text-center p-3 bg-gradient-to-br from-${stage.color}-50 to-${stage.color}-100 rounded-lg`}>
                        <div className={`text-2xl font-bold text-${stage.color}-600 mb-1`}>{stage.score}</div>
                        <div className="text-xs text-gray-600">{stage.name}</div>
                      </div>
                    ))}
                  </div>

                  <blockquote className="text-lg italic text-gray-600 border-l-4 border-gray-300 pl-4 my-6">
                    "A strong story arc isn't just about entertainment—it's about guiding viewers through an emotional journey 
                    that naturally leads to your call-to-action."
                  </blockquote>
                </>
              );
            })()}
          </div>

          {/* Section 3: Framework Performance */}
          <div className="mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              Multi-Framework Analysis: The Science of Persuasion
            </h2>
            <div className="h-1 w-20 bg-indigo-600 mb-6"></div>

            <p className="text-gray-700 leading-relaxed mb-6">
              We analyzed your video against 7 proven marketing frameworks. Here's how each framework reveals different 
              aspects of your content's persuasive power:
            </p>

            {(() => {
              const aidaAnalysis = getAIDAAnalysis();
              const pasAnalysis = getPASAnalysis();
              const babAnalysis = getBABAnalysis();
              const hrrAnalysis = getHRRAnalysis();
              const threeActAnalysis = get3ActAnalysis();
              const heroAnalysis = getHeroJourneyAnalysis();
              const storyArcAnalysis = getStoryArcAnalysis();

              const frameworks = [
                { name: 'AIDA', full: 'Attention → Interest → Desire → Action', score: aidaAnalysis.overall, desc: 'Classic advertising framework', color: 'blue' },
                { name: 'PAS', full: 'Problem → Agitate → Solution', score: pasAnalysis.overall, desc: 'Pain-point driven approach', color: 'red' },
                { name: 'BAB', full: 'Before → After → Bridge', score: babAnalysis.overall, desc: 'Transformation storytelling', color: 'green' },
                { name: 'HRR', full: 'Hook → Relate → Resolve', score: hrrAnalysis.overall, desc: 'Empathy-first structure', color: 'purple' },
                { name: '3-Act', full: 'Setup → Confrontation → Resolution', score: threeActAnalysis.overall, desc: 'Classical story structure', color: 'indigo' },
                { name: "Hero's Journey", full: 'Transformation narrative', score: heroAnalysis.overall, desc: 'Epic storytelling', color: 'amber' },
                { name: 'Story Arc', full: 'Hook → Problem → Tension → Climax → Resolution', score: storyArcAnalysis.overall, desc: '5-point narrative', color: 'pink' }
              ];

              const topFramework = frameworks.reduce((best, current) => 
                current.score > best.score ? current : best
              );

              return (
                <>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
                    {frameworks.map((fw) => (
                      <div key={fw.name} className={`p-5 rounded-lg border-2 ${
                        fw.name === topFramework.name ? 'border-indigo-500 bg-indigo-50' : 'border-gray-200 bg-gray-50'
                      }`}>
                        <div className="flex items-center justify-between mb-2">
                          <div>
                            <h4 className="font-bold text-gray-900">{fw.name}</h4>
                            <p className="text-xs text-gray-600">{fw.desc}</p>
                          </div>
                          <div className={`text-2xl font-bold ${
                            fw.score >= 8 ? 'text-green-600' :
                            fw.score >= 5 ? 'text-yellow-600' : 'text-red-600'
                          }`}>
                            {fw.score.toFixed(1)}
                          </div>
                        </div>
                        <p className="text-sm text-gray-600">{fw.full}</p>
                        {fw.name === topFramework.name && (
                          <div className="mt-2 flex items-center text-xs text-indigo-600 font-semibold">
                            <Award className="w-3 h-3 mr-1" />
                            Top Performer
                          </div>
                        )}
                      </div>
                    ))}
                  </div>

                  <div className="bg-gradient-to-r from-indigo-50 to-purple-50 rounded-lg p-6 border border-indigo-200">
                    <h4 className="font-semibold text-gray-900 mb-2">Top Performing Framework Analysis:</h4>
                    <p className="text-gray-700">
                      <strong className="text-indigo-600">{topFramework.name}</strong> scored highest at {topFramework.score.toFixed(1)}/10. {generateFrameworkInsights(topFramework.name, topFramework.score, null)}
                    </p>
                  </div>
                </>
              );
            })()}
          </div>

          {/* Section 4: AIDA Deep Dive with Frame */}
          <div className="mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              AIDA Framework: The Attention Funnel
            </h2>
            <div className="h-1 w-20 bg-blue-600 mb-6"></div>

            {(() => {
              const aidaAnalysis = getAIDAAnalysis();
              return (
                <>
                  <p className="text-gray-700 leading-relaxed mb-6">
                    AIDA (Attention, Interest, Desire, Action) is the cornerstone of effective advertising. Your video's AIDA score 
                    of <strong className="text-blue-600">{aidaAnalysis.overall.toFixed(1)}/10</strong> reveals how well you guide viewers 
                    through each stage of the persuasion journey.
                  </p>

                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                    <div className="text-center p-4 bg-blue-50 rounded-lg">
                      <div className="text-3xl font-bold text-blue-600 mb-1">{aidaAnalysis.attention.score}</div>
                      <div className="text-xs text-gray-600">Attention</div>
                    </div>
                    <div className="text-center p-4 bg-indigo-50 rounded-lg">
                      <div className="text-3xl font-bold text-indigo-600 mb-1">{aidaAnalysis.interest.score}</div>
                      <div className="text-xs text-gray-600">Interest</div>
                    </div>
                    <div className="text-center p-4 bg-purple-50 rounded-lg">
                      <div className="text-3xl font-bold text-purple-600 mb-1">{aidaAnalysis.desire.score}</div>
                      <div className="text-xs text-gray-600">Desire</div>
                    </div>
                    <div className="text-center p-4 bg-pink-50 rounded-lg">
                      <div className="text-3xl font-bold text-pink-600 mb-1">{aidaAnalysis.action.score}</div>
                      <div className="text-xs text-gray-600">Action</div>
                    </div>
                  </div>

                  {aidaAnalysis.attention.criteria.length > 0 && (
                    <div className="space-y-4">
                      {aidaAnalysis.attention.criteria.slice(0, 2).map((criterion, index) => (
                        <div key={index} className="p-4 bg-gray-50 rounded-lg">
                          <div className="flex items-center justify-between mb-2">
                            <span className="font-semibold text-gray-900">{criterion.name}</span>
                            <span className={`px-2 py-1 rounded text-xs font-bold ${
                              criterion.score >= 8 ? 'bg-green-100 text-green-700' :
                              criterion.score >= 5 ? 'bg-yellow-100 text-yellow-700' : 'bg-red-100 text-red-700'
                            }`}>
                              {criterion.score.toFixed(1)}/10
                            </span>
                          </div>
                          <p className="text-sm text-gray-700">{criterion.explanation}</p>
                        </div>
                      ))}
                    </div>
                  )}
                </>
              );
            })()}
          </div>

          {/* Section 5: Problem-Agitate-Solution */}
          <div className="mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              PAS Framework: The Pain Point Strategy
            </h2>
            <div className="h-1 w-20 bg-red-600 mb-6"></div>

            {/* Last Frame for Resolution */}
            {data.videoFile && (
              <div className="float-left mr-4 mb-4 w-48">
                <div className="relative rounded-lg overflow-hidden shadow-md">
                  <video 
                    className="w-full h-auto"
                    preload="metadata"
                    muted
                    playsInline
                    onLoadedMetadata={(e) => {
                      const duration = e.target.duration;
                      if (duration && duration > 0) {
                        e.target.currentTime = Math.max(0, duration - 0.001);
                      }
                    }}
                  >
                    <source src={`/uploads/${data.videoFile.filename}`} type={data.videoFile.mimetype} />
                  </video>
                  <div className="absolute bottom-0 left-0 right-0 bg-black bg-opacity-70 text-white text-xs px-2 py-1">
                    Final Frame
                  </div>
                </div>
              </div>
            )}

            {(() => {
              const pasAnalysis = getPASAnalysis();
              return (
                <>
                  <p className="text-gray-700 leading-relaxed mb-6">
                    The PAS framework (Problem-Agitate-Solution) is built on emotional triggers. Your PAS score 
                    of <strong className="text-red-600">{pasAnalysis.overall.toFixed(1)}/10</strong> shows how effectively you 
                    identify pain points, intensify urgency, and position your solution.
                  </p>

                  <div className="clear-both"></div>

                  {pasAnalysis.solution.criteria.length > 0 && (
                    <div className="bg-red-50 border-l-4 border-red-500 p-6 rounded-r-lg">
                      <h4 className="font-semibold text-gray-900 mb-3">Solution Positioning:</h4>
                      <p className="text-gray-700 mb-3">{pasAnalysis.solution.criteria[0].explanation}</p>
                      <p className="text-sm text-gray-600">
                        <strong>Recommendation:</strong> {pasAnalysis.solution.criteria[0].recommendations}
                      </p>
                    </div>
                  )}
                </>
              );
            })()}
          </div>

          {/* Section 6: Best Performing Elements */}
          <div className="mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              Your Strongest Assets: What's Working
            </h2>
            <div className="h-1 w-20 bg-green-600 mb-6"></div>

            {(() => {
              const storyArcAnalysis = getStoryArcAnalysis();
              const allStages = [
                { name: 'Hook', ...storyArcAnalysis.hook },
                { name: 'Problem', ...storyArcAnalysis.problem },
                { name: 'Tension', ...storyArcAnalysis.tension },
                { name: 'Climax', ...storyArcAnalysis.climax },
                { name: 'Resolution', ...storyArcAnalysis.resolution }
              ];

              const topPerformers = allStages
                .filter(s => s.score >= 8)
                .sort((a, b) => b.score - a.score)
                .slice(0, 3);

              return topPerformers.length > 0 ? (
                <div className="grid grid-cols-1 gap-4">
                  {topPerformers.map((stage, index) => (
                    <div key={stage.name} className="flex items-start p-5 bg-green-50 border border-green-200 rounded-lg">
                      <div className="w-10 h-10 bg-green-500 text-white rounded-full flex items-center justify-center font-bold mr-4 flex-shrink-0">
                        {index + 1}
                      </div>
                      <div>
                        <h4 className="font-bold text-gray-900 mb-1">{stage.name} Excellence</h4>
                        <p className="text-gray-700 text-sm">
                          Scored {stage.score}/10 - {stage.criteria.length > 0 ? stage.criteria[0].explanation : 
                          'Demonstrates strong execution in this critical area.'}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-gray-600 italic">
                  While no individual elements scored above 8, there are multiple areas showing solid performance with opportunity for optimization.
                </p>
              );
            })()}
          </div>

          {/* Section 7: Areas for Improvement */}
          <div className="mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              Growth Opportunities: Priority Action Items
            </h2>
            <div className="h-1 w-20 bg-yellow-600 mb-6"></div>

            {(() => {
              const storyArcAnalysis = getStoryArcAnalysis();
              const allStages = [
                { name: 'Hook', ...storyArcAnalysis.hook },
                { name: 'Problem', ...storyArcAnalysis.problem },
                { name: 'Tension', ...storyArcAnalysis.tension },
                { name: 'Climax', ...storyArcAnalysis.climax },
                { name: 'Resolution', ...storyArcAnalysis.resolution }
              ];

              const needsWork = allStages
                .filter(s => s.score < 6)
                .sort((a, b) => a.score - b.score)
                .slice(0, 3);

              return needsWork.length > 0 ? (
                <>
                  <p className="text-gray-700 leading-relaxed mb-6">
                    Every video has room for improvement. Focusing on these areas will yield the highest ROI on your optimization efforts:
                  </p>
                  <div className="space-y-4">
                    {needsWork.map((stage, index) => (
                      <div key={stage.name} className="p-4 bg-yellow-50 border-l-4 border-yellow-500 rounded-r-lg">
                        <div className="flex items-start">
                          <AlertTriangle className="w-5 h-5 text-yellow-600 mr-3 mt-0.5 flex-shrink-0" />
                          <div>
                            <h4 className="font-semibold text-gray-900 mb-1">{stage.name} - Score: {stage.score}/10</h4>
                            <p className="text-gray-700 text-sm">
                              {stage.criteria.length > 0 ? stage.criteria[0].recommendations : 
                              'Consider strengthening this element to improve overall narrative flow.'}
                            </p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </>
              ) : (
                <p className="text-gray-700">
                  Strong performance across all narrative elements! Focus on minor refinements to take your content from good to exceptional.
                </p>
              );
            })()}
          </div>

          {/* Section 8: The Conversion Moment */}
          <div className="mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              The Conversion Moment: Driving Action
            </h2>
            <div className="h-1 w-20 bg-indigo-600 mb-6"></div>

            {(() => {
              const aidaAnalysis = getAIDAAnalysis();
              return (
                <>
                  <p className="text-gray-700 leading-relaxed mb-6">
                    All the storytelling in the world means nothing without a clear call-to-action. Your Action score 
                    of <strong className="text-indigo-600">{aidaAnalysis.action.score}/10</strong> determines how many 
                    viewers actually take the next step.
                  </p>

                  {aidaAnalysis.action.criteria.length > 0 && (
                    <div className="space-y-4">
                      {aidaAnalysis.action.criteria.map((criterion, index) => (
                        <div key={index} className="p-5 bg-white border border-indigo-200 rounded-lg shadow-sm">
                          <div className="flex items-center justify-between mb-2">
                            <h4 className="font-medium text-gray-900">{criterion.name}</h4>
                            <span className={`px-3 py-1 rounded-full text-sm font-bold ${
                              criterion.score >= 8 ? 'bg-green-100 text-green-700' :
                              criterion.score >= 5 ? 'bg-yellow-100 text-yellow-700' : 'bg-red-100 text-red-700'
                            }`}>
                              {criterion.score.toFixed(1)}/10
                            </span>
                          </div>
                          <p className="text-sm text-gray-700 mb-2">{criterion.explanation}</p>
                          <div className="flex items-start mt-3 p-3 bg-indigo-50 rounded">
                            <Lightbulb className="w-4 h-4 text-indigo-600 mr-2 mt-0.5 flex-shrink-0" />
                            <p className="text-sm text-gray-700">{criterion.recommendations}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </>
              );
            })()}
          </div>

          {/* Final Verdict */}
          <div className="mb-8 p-8 bg-gradient-to-br from-indigo-50 via-purple-50 to-pink-50 rounded-2xl border border-indigo-200">
            <h2 className="text-3xl font-bold text-gray-900 mb-4 text-center">
              {generateAdvancedConclusion().verdict}
            </h2>
            <div className="h-1 w-20 bg-indigo-600 mb-6 mx-auto"></div>

            <div className="text-center mb-6">
              <div className={`inline-flex items-center justify-center w-28 h-28 rounded-full text-4xl font-bold ${
                overallScore >= 8 ? 'bg-green-100 text-green-600 border-4 border-green-600' :
                overallScore >= 5 ? 'bg-yellow-100 text-yellow-600 border-4 border-yellow-600' : 
                'bg-red-100 text-red-600 border-4 border-red-600'
              }`}>
                {overallScore}/10
              </div>
            </div>

            <p className="text-lg text-gray-700 leading-relaxed text-center max-w-3xl mx-auto mb-6">
              {generateAdvancedConclusion().message}
            </p>

            <div className="bg-white rounded-lg p-6 border border-indigo-200 mb-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-3 text-center">Strategic Action Plan</h3>
              <p className="text-gray-700 leading-relaxed text-center">
                {generateAdvancedConclusion().recommendation}
              </p>
            </div>

            <div className="grid grid-cols-3 gap-4 text-center text-sm">
              <div>
                <div className="font-bold text-2xl text-indigo-600">{tabs.length - 2}</div>
                <div className="text-gray-600">Frameworks</div>
              </div>
              <div>
                <div className="font-bold text-2xl text-indigo-600">{review.criteria?.length || 0}</div>
                <div className="text-gray-600">Criteria</div>
              </div>
              <div>
                <div className="font-bold text-2xl text-indigo-600">
                  {(() => {
                    const criteriaWithScores = review.criteria?.filter(c => c.score >= 8) || [];
                    return criteriaWithScores.length;
                  })()}
                </div>
                <div className="text-gray-600">Strengths</div>
              </div>
            </div>
          </div>

          {/* Expert Recommendations Section */}
          <div className="mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              Next Steps
            </h2>
            <div className="h-1 w-20 bg-indigo-600 mb-6"></div>

            <p className="text-gray-700 leading-relaxed mb-6">
              Based on your specific analysis results, here's a personalized action plan to maximize your video's impact:
            </p>

            {(() => {
              const storyArcAnalysis = getStoryArcAnalysis();
              const hrrAnalysis = getHRRAnalysis();
              const threeActAnalysis = get3ActAnalysis();
              const heroAnalysis = getHeroJourneyAnalysis();
              
              // Get overall performance insights
              const overallScore = Math.round((storyArcAnalysis.overall + hrrAnalysis.overall + threeActAnalysis.overall + heroAnalysis.overall) / 4);
              const weakestFramework = [
                { name: 'Story Arc', score: storyArcAnalysis.overall },
                { name: 'HRR', score: hrrAnalysis.overall },
                { name: '3-Act Structure', score: threeActAnalysis.overall },
                { name: "Hero's Journey", score: heroAnalysis.overall }
              ].sort((a, b) => a.score - b.score)[0];
              
              const strongestFramework = [
                { name: 'Story Arc', score: storyArcAnalysis.overall },
                { name: 'HRR', score: hrrAnalysis.overall },
                { name: '3-Act Structure', score: threeActAnalysis.overall },
                { name: "Hero's Journey", score: heroAnalysis.overall }
              ].sort((a, b) => b.score - a.score)[0];

              // Generate specific recommendations based on analysis
              const recommendations = [];
              
              if (overallScore < 6) {
                recommendations.push({
                  priority: 1,
                  category: 'Foundation',
                  title: 'Strengthen Core Storytelling',
                  description: `Your video scores ${overallScore}/10 overall. Focus on fundamental storytelling elements first.`,
                  action: 'Start with improving your Hook and Problem identification - these are the foundation of all successful videos.',
                  impact: 'High'
                });
              }
              
              if (weakestFramework.score < 7) {
                recommendations.push({
                  priority: recommendations.length + 1,
                  category: 'Framework Optimization',
                  title: `Improve ${weakestFramework.name} Performance`,
                  description: `${weakestFramework.name} scores ${weakestFramework.score}/10 - below industry standard.`,
                  action: `Focus on ${weakestFramework.name.toLowerCase()} elements: strengthen the core components and ensure clear progression.`,
                  impact: 'High'
                });
              }
              
              if (strongestFramework.score >= 8) {
                recommendations.push({
                  priority: recommendations.length + 1,
                  category: 'Leverage Strengths',
                  title: `Scale Your ${strongestFramework.name} Success`,
                  description: `Your ${strongestFramework.name} scores ${strongestFramework.score}/10 - excellent performance!`,
                  action: `Create variations and templates based on your ${strongestFramework.name.toLowerCase()} success to apply to other content.`,
                  impact: 'Medium'
                });
              }

              // Add engagement-specific recommendations
              const lowScoringElements = criteria.filter(c => c.score < 6).length;
              if (lowScoringElements > 0) {
                recommendations.push({
                  priority: recommendations.length + 1,
                  category: 'Engagement',
                  title: 'Address Low-Scoring Elements',
                  description: `${lowScoringElements} elements scored below 6/10, indicating engagement gaps.`,
                  action: 'Prioritize improving visual appeal, emotional connection, and call-to-action clarity.',
                  impact: 'High'
                });
              }

              // Add platform-specific recommendations if available
              if (platformInfo && platformInfo.platform) {
                const platformRecs = {
                  'tiktok': 'Optimize for TikTok\'s fast-paced format with stronger hooks and clearer CTAs.',
                  'instagram': 'Enhance visual storytelling and use Instagram\'s native features more effectively.',
                  'youtube': 'Improve retention with better pacing and stronger narrative structure.',
                  'meta': 'Focus on emotional connection and shareability for Facebook/Instagram feeds.'
                };
                
                if (platformRecs[platformInfo.platform.toLowerCase()]) {
                  recommendations.push({
                    priority: recommendations.length + 1,
                    category: 'Platform Optimization',
                    title: `${platformInfo.platform} Best Practices`,
                    description: `Tailor your content for ${platformInfo.platform} success.`,
                    action: platformRecs[platformInfo.platform.toLowerCase()],
                    impact: 'Medium'
                  });
                }
              }

              return recommendations.length > 0 ? (
                <div className="space-y-4">
                  {recommendations.map((rec, index) => (
                    <div key={index} className="bg-white border border-gray-200 rounded-lg p-6 shadow-sm hover:shadow-md transition-shadow">
                      <div className="flex items-start">
                        <div className={`w-10 h-10 rounded-full flex items-center justify-center font-bold mr-4 flex-shrink-0 ${
                          rec.impact === 'High' ? 'bg-red-100 text-red-600' :
                          rec.impact === 'Medium' ? 'bg-yellow-100 text-yellow-600' :
                          'bg-green-100 text-green-600'
                        }`}>
                          {rec.priority}
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center justify-between mb-2">
                            <div className="flex items-center space-x-3">
                              <h4 className="font-bold text-gray-900">{rec.title}</h4>
                              <span className={`px-2 py-1 text-xs rounded-full font-semibold ${
                                rec.category === 'Foundation' ? 'bg-red-100 text-red-700' :
                                rec.category === 'Framework Optimization' ? 'bg-blue-100 text-blue-700' :
                                rec.category === 'Leverage Strengths' ? 'bg-green-100 text-green-700' :
                                rec.category === 'Engagement' ? 'bg-purple-100 text-purple-700' :
                                'bg-gray-100 text-gray-700'
                              }`}>
                                {rec.category}
                              </span>
                            </div>
                          </div>
                          <p className="text-gray-700 text-sm mb-3">{rec.description}</p>
                          <div className="bg-gray-50 rounded-lg p-3">
                            <p className="text-gray-800 text-sm font-medium">{rec.action}</p>
                          </div>
                          <div className="mt-3 flex items-center text-xs text-gray-500">
                            <span className={`mr-2 px-2 py-1 rounded-full ${
                              rec.impact === 'High' ? 'bg-red-100 text-red-600' :
                              rec.impact === 'Medium' ? 'bg-yellow-100 text-yellow-600' :
                              'bg-green-100 text-green-600'
                            }`}>
                              {rec.impact} Impact
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="bg-green-50 border border-green-200 rounded-lg p-6">
                  <div className="flex items-center">
                    <CheckCircle className="w-8 h-8 text-green-600 mr-3" />
                    <div>
                      <h4 className="font-bold text-green-800 mb-1">Excellent Performance!</h4>
                      <p className="text-green-700">
                        Your video is already performing at a high level across all frameworks. Focus on maintaining quality and testing minor variations to optimize further.
                      </p>
                    </div>
                  </div>
                </div>
              );
            })()}
          </div>

          {/* Go to Top Button */}
          <div className="flex justify-center py-6">
            <button
              type="button"
              onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
              className="flex items-center space-x-2 px-6 py-3 text-sm font-medium text-gray-600 hover:text-gray-800 bg-gray-50 hover:bg-gray-100 rounded-lg transition-all duration-200 border border-gray-200 hover:border-gray-300 shadow-sm hover:shadow-md"
            >
              <ChevronUp className="w-4 h-4" />
              <span>Go to Top</span>
            </button>
          </div>

          {/* Overall Score Display */}
          <div className="text-center py-8">
            <div className="inline-flex items-center justify-center">
              <div className={`w-32 h-32 rounded-full flex items-center justify-center shadow-xl border-4 ${
                overallScore >= 8 ? 'bg-green-100 border-green-500' :
                overallScore >= 6 ? 'bg-yellow-100 border-yellow-500' : 'bg-red-100 border-red-500'
              }`}>
                <div className="text-center">
                  <div className={`text-4xl font-bold ${
                    overallScore >= 8 ? 'text-green-600' :
                    overallScore >= 6 ? 'text-yellow-600' : 'text-red-600'
                  }`}>
                    {overallScore.toFixed(1)}
                  </div>
                  <div className={`text-lg font-semibold ${
                    overallScore >= 8 ? 'text-green-600' :
                    overallScore >= 6 ? 'text-yellow-600' : 'text-red-600'
                  }`}>
                    /10
                  </div>
                  <div className="text-xs text-gray-600 font-medium mt-1">
                    Overall Score
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Closing Statement */}
          <div className="text-center py-8 border-t-2 border-gray-200">
            <p className="text-gray-500 italic text-sm">
              This professional analysis examined your video across multiple proven marketing and storytelling frameworks
            </p>
            <p className="text-xs text-gray-400 mt-2">
              Generated on {new Date().toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })} using AI-powered analysis
            </p>
          </div>
        </div>
      )}

      {activeTab === 'branding' && (
        <div className="space-y-8">
          {/* Branding Framework Comparison Overview */}
          <div className="mb-8">
            <div className="bg-gradient-to-br from-gray-50 via-blue-50 to-indigo-50 rounded-2xl p-8 border border-gray-200 shadow-lg">
              <div className="text-center mb-6">
                <div>
                  <h2 className="text-2xl font-bold text-gray-900">Branding Framework Comparison</h2>
                  <p className="text-gray-600">Compare HRR, 3-Act Structure, Hero's Journey, and Story Arc frameworks to optimize your brand storytelling</p>
                  {(() => {
                    const hrrAnalysis = getHRRAnalysis();
                    const threeActAnalysis = get3ActAnalysis();
                    const heroAnalysis = getHeroJourneyAnalysis();
                    const storyArcAnalysis = getStoryArcAnalysis();
                    
                    const frameworks = [
                      { name: 'HRR', score: hrrAnalysis.overall, color: 'from-green-500 to-emerald-500', bgColor: 'bg-green-50', borderColor: 'border-green-200' },
                      { name: '3-Act Structure', score: threeActAnalysis.overall, color: 'from-blue-500 to-indigo-500', bgColor: 'bg-blue-50', borderColor: 'border-blue-200' },
                      { name: "Hero's Journey", score: heroAnalysis.overall, color: 'from-amber-500 to-orange-500', bgColor: 'bg-amber-50', borderColor: 'border-amber-200' },
                      { name: 'Story Arc', score: storyArcAnalysis.overall, color: 'from-purple-500 to-pink-500', bgColor: 'bg-purple-50', borderColor: 'border-purple-200' }
                    ];
                    
                    const bestFramework = frameworks.reduce((best, current) => 
                      current.score > best.score ? current : best
                    );
                    
                    return (
                      <div className="mt-6">
                        <div className="text-center mb-4">
                          <span className="text-sm text-gray-600">Best Performing Framework:</span>
                          <div className={`inline-flex items-center px-4 py-2 rounded-full text-white font-semibold bg-gradient-to-r ${bestFramework.color} mt-2`}>
                            {bestFramework.name} ({bestFramework.score.toFixed(1)}/10)
                          </div>
                        </div>
                      </div>
                    );
                  })()}
                </div>
              </div>
            </div>
          </div>

          {/* Framework Comparison Cards */}
          <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-4 gap-6 mb-8">
            {/* Story Arc Card */}
            {(() => {
              const storyArcAnalysis = getStoryArcAnalysis();
              const { hook, problem, tension, climax, resolution, overall } = storyArcAnalysis;
              
              return (
                <div className="bg-gradient-to-br from-purple-50 to-pink-50 rounded-2xl p-6 border border-purple-200 shadow-lg">
                  <div className="text-center mb-6">
                    <div className="w-12 h-12 bg-gradient-to-r from-purple-500 to-pink-500 rounded-lg flex items-center justify-center mx-auto mb-4">
                      <TrendingUp className="w-6 h-6 text-white" />
                    </div>
                    <h3 className="text-xl font-bold text-gray-900 mb-2">Story Arc</h3>
                    <p className="text-gray-600 text-sm mb-4">Hook → Problem → Tension → Climax → Resolution</p>
                    
                    <div className="flex items-center justify-center mb-4">
                      <span className="text-3xl font-bold text-gray-900">{overall}/10</span>
                      <div className="ml-3">
                        <div className="w-20 bg-gray-200 rounded-full h-2">
                          <div 
                            className="bg-gradient-to-r from-purple-500 to-pink-500 h-2 rounded-full transition-all duration-500"
                            style={{ width: `${overall * 10}%` }}
                          ></div>
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  <div className="space-y-3">
                    <div className="flex justify-between items-center">
                      <span className="text-sm font-medium text-gray-700">Hook</span>
                      <span className="text-sm font-bold text-purple-600">{hook.score.toFixed(1)}/10</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-sm font-medium text-gray-700">Problem</span>
                      <span className="text-sm font-bold text-purple-600">{problem.score.toFixed(1)}/10</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-sm font-medium text-gray-700">Tension</span>
                      <span className="text-sm font-bold text-purple-600">{tension.score.toFixed(1)}/10</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-sm font-medium text-gray-700">Climax</span>
                      <span className="text-sm font-bold text-purple-600">{climax.score.toFixed(1)}/10</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-sm font-medium text-gray-700">Resolution</span>
                      <span className="text-sm font-bold text-purple-600">{resolution.score.toFixed(1)}/10</span>
                    </div>
                  </div>
                </div>
              );
            })()}

            {/* HRR Card */}
            {(() => {
              const hrrAnalysis = getHRRAnalysis();
              const { hook, relate, resolution, overall } = hrrAnalysis;
              
              return (
                <div className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-2xl p-6 border border-green-200 shadow-lg">
                  <div className="text-center mb-6">
                    <div className="w-12 h-12 bg-gradient-to-r from-green-500 to-emerald-500 rounded-lg flex items-center justify-center mx-auto mb-4">
                      <CheckCircle className="w-6 h-6 text-white" />
                    </div>
                    <h3 className="text-xl font-bold text-gray-900 mb-2">HRR Framework</h3>
                    <p className="text-gray-600 text-sm mb-4">Hook → Relate → Resolution</p>
                    
                    <div className="flex items-center justify-center mb-4">
                      <span className="text-3xl font-bold text-gray-900">{overall}/10</span>
                      <div className="ml-3">
                        <div className="w-20 bg-gray-200 rounded-full h-2">
                          <div 
                            className="bg-gradient-to-r from-green-500 to-emerald-500 h-2 rounded-full transition-all duration-500"
                            style={{ width: `${overall * 10}%` }}
                          ></div>
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  <div className="space-y-3">
                    <div className="flex justify-between items-center">
                      <span className="text-sm font-medium text-gray-700">Hook</span>
                      <span className="text-sm font-bold text-green-600">{hook.score.toFixed(1)}/10</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-sm font-medium text-gray-700">Relate</span>
                      <span className="text-sm font-bold text-green-600">{relate.score.toFixed(1)}/10</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-sm font-medium text-gray-700">Resolution</span>
                      <span className="text-sm font-bold text-green-600">{resolution.score.toFixed(1)}/10</span>
                    </div>
                  </div>
                </div>
              );
            })()}

            {/* 3-Act Structure Card */}
            {(() => {
              const threeActAnalysis = get3ActAnalysis();
              const { setup, confrontation, resolution, overall } = threeActAnalysis;
              
              return (
                <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-2xl p-6 border border-blue-200 shadow-lg">
                  <div className="text-center mb-6">
                    <div className="w-12 h-12 bg-gradient-to-r from-blue-500 to-indigo-500 rounded-lg flex items-center justify-center mx-auto mb-4">
                      <AlertTriangle className="w-6 h-6 text-white" />
                    </div>
                    <h3 className="text-xl font-bold text-gray-900 mb-2">3-Act Structure</h3>
                    <p className="text-gray-600 text-sm mb-4">Setup → Confrontation → Resolution</p>
                    
                    <div className="flex items-center justify-center mb-4">
                      <span className="text-3xl font-bold text-gray-900">{overall}/10</span>
                      <div className="ml-3">
                        <div className="w-20 bg-gray-200 rounded-full h-2">
                          <div 
                            className="bg-gradient-to-r from-blue-500 to-indigo-500 h-2 rounded-full transition-all duration-500"
                            style={{ width: `${overall * 10}%` }}
                          ></div>
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  <div className="space-y-3">
                    <div className="flex justify-between items-center">
                      <span className="text-sm font-medium text-gray-700">Setup</span>
                      <span className="text-sm font-bold text-blue-600">{setup.score.toFixed(1)}/10</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-sm font-medium text-gray-700">Confrontation</span>
                      <span className="text-sm font-bold text-blue-600">{confrontation.score.toFixed(1)}/10</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-sm font-medium text-gray-700">Resolution</span>
                      <span className="text-sm font-bold text-blue-600">{resolution.score.toFixed(1)}/10</span>
                    </div>
                  </div>
                </div>
              );
            })()}

            {/* Hero's Journey Card */}
            {(() => {
              const heroAnalysis = getHeroJourneyAnalysis();
              const { hero, callToAdventure, mentor, transformation, returnReward, overall } = heroAnalysis;
              
              return (
                <div className="bg-gradient-to-br from-amber-50 to-orange-50 rounded-2xl p-6 border border-amber-200 shadow-lg">
                  <div className="text-center mb-6">
                    <div className="w-12 h-12 bg-gradient-to-r from-amber-500 to-orange-500 rounded-lg flex items-center justify-center mx-auto mb-4">
                      <Star className="w-6 h-6 text-white" />
                    </div>
                    <h3 className="text-xl font-bold text-gray-900 mb-2">Hero's Journey</h3>
                    <p className="text-gray-600 text-sm mb-4">Hero → Call → Mentor → Transformation → Return</p>
                    
                    <div className="flex items-center justify-center mb-4">
                      <span className="text-3xl font-bold text-gray-900">{overall}/10</span>
                      <div className="ml-3">
                        <div className="w-20 bg-gray-200 rounded-full h-2">
                          <div 
                            className="bg-gradient-to-r from-amber-500 to-orange-500 h-2 rounded-full transition-all duration-500"
                            style={{ width: `${overall * 10}%` }}
                          ></div>
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  <div className="space-y-3">
                    <div className="flex justify-between items-center">
                      <span className="text-sm font-medium text-gray-700">Hero</span>
                      <span className="text-sm font-bold text-amber-600">{hero.score.toFixed(1)}/10</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-sm font-medium text-gray-700">Call to Adventure</span>
                      <span className="text-sm font-bold text-amber-600">{callToAdventure.score.toFixed(1)}/10</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-sm font-medium text-gray-700">Mentor</span>
                      <span className="text-sm font-bold text-amber-600">{mentor.score.toFixed(1)}/10</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-sm font-medium text-gray-700">Transformation</span>
                      <span className="text-sm font-bold text-amber-600">{transformation.score.toFixed(1)}/10</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-sm font-medium text-gray-700">Return with Reward</span>
                      <span className="text-sm font-bold text-amber-600">{returnReward.score.toFixed(1)}/10</span>
                    </div>
                  </div>
                </div>
              );
            })()}
          </div>

          {/* Branding Insights & Recommendations */}
          <div className="bg-white rounded-2xl p-8 border border-gray-200 shadow-lg mb-8">
            <div className="text-center mb-6">
              <h3 className="text-2xl font-bold text-gray-900 mb-2">Branding Insights & Recommendations</h3>
              <p className="text-gray-600">Professional analysis of your video's brand storytelling framework performance</p>
            </div>
            
            <div className="prose prose-lg max-w-none">
              {(() => {
                const hrrAnalysis = getHRRAnalysis();
                const threeActAnalysis = get3ActAnalysis();
                const heroAnalysis = getHeroJourneyAnalysis();
                const storyArcAnalysis = getStoryArcAnalysis();
                
                const frameworks = [
                  { name: 'HRR', score: hrrAnalysis.overall, analysis: hrrAnalysis },
                  { name: '3-Act Structure', score: threeActAnalysis.overall, analysis: threeActAnalysis },
                  { name: "Hero's Journey", score: heroAnalysis.overall, analysis: heroAnalysis },
                  { name: 'Story Arc', score: storyArcAnalysis.overall, analysis: storyArcAnalysis }
                ];
                
                const sortedFrameworks = frameworks.sort((a, b) => b.score - a.score);
                const bestFramework = sortedFrameworks[0];
                const worstFramework = sortedFrameworks[sortedFrameworks.length - 1];
                
                const getPerformanceLevel = (score) => {
                  if (score >= 8) return { level: 'excellent', description: 'outstanding', color: 'text-green-600' };
                  if (score >= 6) return { level: 'good', description: 'solid', color: 'text-blue-600' };
                  if (score >= 4) return { level: 'fair', description: 'moderate', color: 'text-yellow-600' };
                  return { level: 'needs improvement', description: 'limited', color: 'text-red-600' };
                };
                
                const bestPerf = getPerformanceLevel(bestFramework.score);
                const worstPerf = getPerformanceLevel(worstFramework.score);
                
                return (
                  <div className="space-y-6">
                    {/* Overall Performance Summary */}
                    <div className="bg-gradient-to-r from-purple-50 to-pink-50 rounded-lg p-6 border border-purple-200">
                      <h4 className="text-lg font-semibold text-gray-900 mb-3">Overall Performance Summary</h4>
                      <p className="text-gray-700 leading-relaxed">
                        Your video demonstrates <span className={`font-semibold ${bestPerf.color}`}>{bestPerf.description}</span> performance 
                        across brand storytelling frameworks, with <strong>{bestFramework.name}</strong> leading at {bestFramework.score.toFixed(1)}/10. 
                        {bestFramework.score >= 7 ? 
                          ` This indicates strong brand storytelling potential and effective use of the ${bestFramework.name} methodology.` :
                          ` While there's room for improvement, the ${bestFramework.name} framework shows the most promise for brand optimization.`
                        }
                      </p>
                    </div>
                    
                    {/* Framework-Specific Insights */}
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                      {frameworks.map((framework, index) => {
                        const perf = getPerformanceLevel(framework.score);
                        const isBest = framework.name === bestFramework.name;
                        const isWorst = framework.name === worstFramework.name;
                        
                        return (
                          <div key={index} className={`rounded-lg p-4 border ${
                            isBest ? 'bg-green-50 border-green-200' : 
                            isWorst ? 'bg-amber-50 border-amber-200' : 
                            'bg-gray-50 border-gray-200'
                          }`}>
                            <div className="flex items-center justify-between mb-3">
                              <h5 className="font-semibold text-gray-900">{framework.name}</h5>
                              <span className={`text-sm font-bold ${perf.color}`}>{framework.score}/10</span>
                            </div>
                            
                            {framework.name === 'HRR' && (
                              <div className="text-sm text-gray-600 space-y-1">
                                <p><strong>Hook:</strong> {framework.analysis.hook.score}/10</p>
                                <p><strong>Relate:</strong> {framework.analysis.relate.score}/10</p>
                                <p><strong>Resolution:</strong> {framework.analysis.resolution.score}/10</p>
                              </div>
                            )}
                            
                            {framework.name === '3-Act Structure' && (
                              <div className="text-sm text-gray-600 space-y-1">
                                <p><strong>Setup:</strong> {framework.analysis.setup.score}/10</p>
                                <p><strong>Confrontation:</strong> {framework.analysis.confrontation.score}/10</p>
                                <p><strong>Resolution:</strong> {framework.analysis.resolution.score}/10</p>
                              </div>
                            )}
                            
                            {framework.name === "Hero's Journey" && (
                              <div className="text-sm text-gray-600 space-y-1">
                                <p><strong>Hero:</strong> {framework.analysis.hero.score}/10</p>
                                <p><strong>Call to Adventure:</strong> {framework.analysis.callToAdventure.score}/10</p>
                                <p><strong>Mentor:</strong> {framework.analysis.mentor.score}/10</p>
                                <p><strong>Transformation:</strong> {framework.analysis.transformation.score}/10</p>
                                <p><strong>Return with Reward:</strong> {framework.analysis.returnReward.score}/10</p>
                              </div>
                            )}
                            
                            {isBest && (
                              <div className="mt-2 text-xs text-green-700 font-medium">
                                ✓ Best performing framework
                              </div>
                            )}
                            
                            {isWorst && (
                              <div className="mt-2 text-xs text-amber-700 font-medium">
                                ⚠ Needs most attention
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                    
                    {/* Strategic Recommendations */}
                    <div className="bg-gradient-to-r from-indigo-50 to-blue-50 rounded-lg p-6 border border-indigo-200">
                      <h4 className="text-lg font-semibold text-gray-900 mb-3">Strategic Recommendations</h4>
                      <div className="space-y-3 text-gray-700">
                        {bestFramework.score >= 7 ? (
                          <p className="leading-relaxed">
                            <strong>Leverage Your Brand Strength:</strong> Your {bestFramework.name} performance is excellent. 
                            Consider using this framework as your primary brand storytelling approach and developing 
                            brand guidelines around this methodology. The strong performance suggests your audience 
                            connects deeply with this particular narrative structure.
                          </p>
                        ) : (
                          <p className="leading-relaxed">
                            <strong>Focus on Brand Improvement:</strong> While no framework is performing at an optimal level, 
                            {bestFramework.name} shows the most promise for brand storytelling. Prioritize strengthening 
                            the weakest elements of this framework to create a more compelling and memorable brand narrative.
                          </p>
                        )}
                        
                        {worstFramework.score < 6 && (
                          <p className="leading-relaxed">
                            <strong>Address Brand Weaknesses:</strong> The {worstFramework.name} framework needs significant attention. 
                            Consider whether this approach aligns with your brand personality and values, or if it requires 
                            fundamental restructuring to better serve your brand storytelling goals.
                          </p>
                        )}
                        
                        <p className="leading-relaxed">
                          <strong>Holistic Brand Approach:</strong> The most powerful brand stories often weave together elements 
                          from multiple frameworks. Consider how you might integrate the strongest aspects of each approach 
                          to create a more comprehensive and emotionally resonant brand narrative.
                        </p>
                      </div>
                    </div>
                    
                    {/* Next Steps */}
                    <div className="bg-gradient-to-r from-gray-50 to-purple-50 rounded-lg p-6 border border-gray-200">
                      <h4 className="text-lg font-semibold text-gray-900 mb-3">Recommended Next Steps</h4>
                      {(() => {
                        const storyArcAnalysis = getStoryArcAnalysis();
                        const hrrAnalysis = getHRRAnalysis();
                        const threeActAnalysis = get3ActAnalysis();
                        const heroAnalysis = getHeroJourneyAnalysis();
                        
                        const frameworks = [
                          { name: 'Story Arc', score: storyArcAnalysis.overall },
                          { name: 'HRR', score: hrrAnalysis.overall },
                          { name: '3-Act Structure', score: threeActAnalysis.overall },
                          { name: "Hero's Journey", score: heroAnalysis.overall }
                        ];
                        
                        const avgBrandScore = Math.round(frameworks.reduce((sum, f) => sum + f.score, 0) / frameworks.length);
                        const weakestFramework = frameworks.sort((a, b) => a.score - b.score)[0];
                        const strongestFramework = frameworks.sort((a, b) => b.score - a.score)[0];
                        
                        return (
                          <div className="space-y-4">
                            {avgBrandScore < 6 && (
                              <div className="bg-white rounded-lg p-4 border border-red-200">
                                <h5 className="font-semibold text-red-800 mb-2 flex items-center">
                                  <AlertTriangle className="w-4 h-4 mr-2" />
                                  Brand Foundation Issues (Average Score: {avgBrandScore}/10)
                                </h5>
                                <p className="text-sm text-gray-700">Your brand storytelling needs fundamental improvement. Focus on core messaging and brand clarity first.</p>
                              </div>
                            )}
                            
                            {weakestFramework.score < 7 && (
                              <div className="bg-white rounded-lg p-4 border border-yellow-200">
                                <h5 className="font-semibold text-yellow-800 mb-2 flex items-center">
                                  <Target className="w-4 h-4 mr-2" />
                                  Framework Optimization Needed
                                </h5>
                                <p className="text-sm text-gray-700">
                                  <strong>{weakestFramework.name}</strong> scores {weakestFramework.score}/10. Strengthen this framework to improve overall brand storytelling.
                                </p>
                              </div>
                            )}
                            
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                              <div>
                                <h5 className="font-semibold text-gray-800 mb-2">Priority Actions</h5>
                                <ul className="text-sm text-gray-600 space-y-1">
                                  <li>• {avgBrandScore < 6 ? 'Strengthen core brand messaging and value proposition' : `Focus on improving ${weakestFramework.name} framework`}</li>
                                  <li>• {strongestFramework.score >= 8 ? `Scale your ${strongestFramework.name} success to other frameworks` : 'Develop consistent brand voice across all content'}</li>
                                  <li>• Create brand-specific content guidelines based on analysis</li>
                                </ul>
                              </div>
                              <div>
                                <h5 className="font-semibold text-gray-800 mb-2">Strategic Actions</h5>
                                <ul className="text-sm text-gray-600 space-y-1">
                                  <li>• Develop a hybrid brand storytelling strategy using your strongest frameworks</li>
                                  <li>• Create framework-specific brand content series</li>
                                  <li>• Monitor brand resonance and consistency across audience segments</li>
                                </ul>
                              </div>
                            </div>
                          </div>
                        );
                      })()}
                    </div>
                  </div>
                );
              })()}
            </div>
          </div>

          {/* Branding Insights */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="card">
              <div className="flex items-center mb-4">
                <div className="w-8 h-8 bg-green-100 rounded-lg flex items-center justify-center mr-3">
                  <span className="text-green-600 font-bold">✓</span>
                </div>
                <h3 className="text-lg font-semibold text-gray-900">Branding Strengths</h3>
              </div>
              <div className="space-y-3">
                {(() => {
                  const hrrAnalysis = getHRRAnalysis();
                  const threeActAnalysis = get3ActAnalysis();
                  const heroAnalysis = getHeroJourneyAnalysis();
                  const storyArcAnalysis = getStoryArcAnalysis();
                  
                  const strengths = [];
                  
                  if (hrrAnalysis.overall >= 7) {
                    strengths.push({
                      framework: 'HRR',
                      description: 'Strong hook, relatability, and resolution elements for brand storytelling',
                      color: 'text-green-700',
                      bgColor: 'bg-green-50',
                      borderColor: 'border-green-200'
                    });
                  }
                  
                  if (threeActAnalysis.overall >= 7) {
                    strengths.push({
                      framework: '3-Act Structure',
                      description: 'Effective narrative structure and character development for brand building',
                      color: 'text-blue-700',
                      bgColor: 'bg-blue-50',
                      borderColor: 'border-blue-200'
                    });
                  }
                  
                  if (heroAnalysis.overall >= 7) {
                    strengths.push({
                      framework: "Hero's Journey",
                      description: 'Compelling mythic storytelling and brand transformation narrative',
                      color: 'text-amber-700',
                      bgColor: 'bg-amber-50',
                      borderColor: 'border-amber-200'
                    });
                  }
                  
                  if (storyArcAnalysis.overall >= 7) {
                    strengths.push({
                      framework: 'Story Arc',
                      description: 'Strong narrative progression and emotional journey for brand engagement',
                      color: 'text-purple-700',
                      bgColor: 'bg-purple-50',
                      borderColor: 'border-purple-200'
                    });
                  }
                  
                  if (strengths.length === 0) {
                    return (
                      <div className="text-center py-4">
                        <p className="text-gray-500 text-sm">Focus on improving brand storytelling frameworks to build stronger brand resonance.</p>
                      </div>
                    );
                  }
                  
                  return strengths.map((strength, index) => (
                    <div key={index} className={`p-3 rounded-lg border ${strength.bgColor} ${strength.borderColor}`}>
                      <div className="flex items-center mb-1">
                        <span className={`font-semibold text-sm ${strength.color}`}>{strength.framework}</span>
                      </div>
                      <p className="text-sm text-gray-600">{strength.description}</p>
                    </div>
                  ));
                })()}
              </div>
            </div>

            <div className="card">
              <div className="flex items-center mb-4">
                <div className="w-8 h-8 bg-amber-100 rounded-lg flex items-center justify-center mr-3">
                  <span className="text-amber-600 font-bold">!</span>
                </div>
                <h3 className="text-lg font-semibold text-gray-900">Branding Improvements</h3>
              </div>
              <div className="space-y-3">
                {(() => {
                  const hrrAnalysis = getHRRAnalysis();
                  const threeActAnalysis = get3ActAnalysis();
                  const heroAnalysis = getHeroJourneyAnalysis();
                  const storyArcAnalysis = getStoryArcAnalysis();
                  
                  const improvements = [];
                  
                  if (hrrAnalysis.overall < 7) {
                    improvements.push({
                      framework: 'HRR',
                      description: 'Strengthen hook, relatability, and resolution elements for better brand connection',
                      color: 'text-green-700',
                      bgColor: 'bg-green-50',
                      borderColor: 'border-green-200'
                    });
                  }
                  
                  if (threeActAnalysis.overall < 7) {
                    improvements.push({
                      framework: '3-Act Structure',
                      description: 'Improve narrative structure and character development for stronger brand building',
                      color: 'text-blue-700',
                      bgColor: 'bg-blue-50',
                      borderColor: 'border-blue-200'
                    });
                  }
                  
                  if (heroAnalysis.overall < 7) {
                    improvements.push({
                      framework: "Hero's Journey",
                      description: 'Enhance mythic storytelling and brand transformation narrative elements',
                      color: 'text-amber-700',
                      bgColor: 'bg-amber-50',
                      borderColor: 'border-amber-200'
                    });
                  }
                  
                  if (storyArcAnalysis.overall < 7) {
                    improvements.push({
                      framework: 'Story Arc',
                      description: 'Strengthen narrative progression and emotional journey for better brand engagement',
                      color: 'text-purple-700',
                      bgColor: 'bg-purple-50',
                      borderColor: 'border-purple-200'
                    });
                  }
                  
                  if (improvements.length === 0) {
                    return (
                      <div className="text-center py-4">
                        <p className="text-gray-500 text-sm">Great job! All brand storytelling frameworks are performing well.</p>
                      </div>
                    );
                  }
                  
                  return improvements.map((improvement, index) => (
                    <div key={index} className={`p-3 rounded-lg border ${improvement.bgColor} ${improvement.borderColor}`}>
                      <div className="flex items-center mb-1">
                        <span className={`font-semibold text-sm ${improvement.color}`}>{improvement.framework}</span>
                      </div>
                      <p className="text-sm text-gray-600">{improvement.description}</p>
                    </div>
                  ));
                })()}
              </div>
            </div>
          </div>
        </div>
      )}

      {activeTab === 'brief' && (
        <div className="space-y-6">
          {/* Improved Brief Recommendation */}
          <div className="card">
            <div className="flex items-center mb-6">
              <div className="flex items-center justify-center w-10 h-10 bg-gradient-to-r from-blue-500 to-purple-600 rounded-lg mr-3">
                <Lightbulb className="w-5 h-5 text-white" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900">Improved Video Brief</h3>
            </div>
            
            <div className="bg-gradient-to-r from-blue-50 to-purple-50 border border-blue-200 rounded-lg p-6">
              <h4 className="font-semibold text-gray-900 mb-4 flex items-center">
                <Target className="w-4 h-4 text-blue-600 mr-2" />
                Recommended Video Variation Brief
              </h4>
              
              <div className="space-y-4">
                {/* Video Concept */}
                <div>
                  <h5 className="font-medium text-gray-800 mb-2">Video Concept & Hook</h5>
                  <div className="bg-white rounded-lg p-4 border border-blue-100">
                    {(() => {
                      const strengths = review.strengths || review.videoAnalysis?.strengths || [];
                      const improvements = review.improvements || review.videoAnalysis?.improvements || [];
                      const highScoringCriteria = chartData.filter(c => c.score >= 8);
                      const lowScoringCriteria = chartData.filter(c => c.score < 5);
                      
                      // Extract specific insights for personalized recommendations
                      const hookStrengths = strengths.filter(s => 
                        s.toLowerCase().includes('hook') || 
                        s.toLowerCase().includes('opening') || 
                        s.toLowerCase().includes('start') ||
                        s.toLowerCase().includes('attention')
                      );
                      
                      const hookImprovements = improvements.filter(i => 
                        i.toLowerCase().includes('hook') || 
                        i.toLowerCase().includes('opening') || 
                        i.toLowerCase().includes('start') ||
                        i.toLowerCase().includes('attention')
                      );
                      
                      
                      if (overallScore >= 80) {
                        return (
                          <div className="space-y-3">
                            <div className="flex items-center mb-2">
                              <div className="w-2 h-2 bg-green-500 rounded-full mr-2"></div>
                              <span className="font-medium text-green-700">Strong Foundation Detected</span>
                            </div>
                            <p className="text-gray-700">
                              Your video demonstrates strong performance with {highScoringCriteria.length > 0 ? `${highScoringCriteria.map(c => c.name).join(', ')} scoring highly` : 'solid overall execution'}. 
                              {hookStrengths.length > 0 ? ` Specifically, ${hookStrengths[0].toLowerCase()}.` : ''}
                            </p>
                            <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                              <p className="text-sm text-green-800 font-medium mb-2">🎯 Recommended Approach:</p>
                              <p className="text-sm text-green-700">
                                Build on your strengths while experimenting with new creative angles. 
                                {lowScoringCriteria.length > 0 ? ` Focus on improving ${lowScoringCriteria.map(c => c.name).join(', ')} while maintaining your successful elements.` : ' Explore fresh perspectives and innovative storytelling techniques.'}
                              </p>
                            </div>
                          </div>
                        );
                      } else if (overallScore >= 50) {
                        return (
                          <div className="space-y-3">
                            <div className="flex items-center mb-2">
                              <div className="w-2 h-2 bg-amber-500 rounded-full mr-2"></div>
                              <span className="font-medium text-amber-700">Moderate Performance</span>
                            </div>
                            <p className="text-gray-700">
                              Your video shows potential with some strong elements, but there are key areas for improvement. 
                              {hookImprovements.length > 0 ? ` Specifically, ${hookImprovements[0].toLowerCase()}.` : ''}
                            </p>
                            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                              <p className="text-sm text-amber-800 font-medium mb-2">🎯 Recommended Approach:</p>
                              <p className="text-sm text-amber-700">
                                Create a strategic variation that addresses the critical improvement areas. 
                                {lowScoringCriteria.length > 0 ? ` Prioritize ${lowScoringCriteria.map(c => c.name).join(', ')} while building on your existing strengths.` : ' Focus on optimizing the elements that need enhancement.'}
                              </p>
                            </div>
                          </div>
                        );
                      } else {
                        return (
                          <div className="space-y-3">
                            <div className="flex items-center mb-2">
                              <div className="w-2 h-2 bg-red-500 rounded-full mr-2"></div>
                              <span className="font-medium text-red-700">Needs Strategic Refresh</span>
                            </div>
                            <p className="text-gray-700">
                              Your video requires significant improvements across multiple areas. 
                              {hookImprovements.length > 0 ? ` Key issues include ${hookImprovements[0].toLowerCase()}.` : ''}
                            </p>
                            <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                              <p className="text-sm text-red-800 font-medium mb-2">🎯 Recommended Approach:</p>
                              <p className="text-sm text-red-700">
                                Develop a complete creative refresh with a new approach to content structure and presentation. 
                                {lowScoringCriteria.length > 0 ? ` Focus on rebuilding ${lowScoringCriteria.map(c => c.name).join(', ')} from the ground up.` : ' Consider a fundamental restructuring of your content strategy.'}
                              </p>
                            </div>
                          </div>
                        );
                      }
                    })()}
                  </div>
                </div>

                {/* Storytelling Structure */}
                <div>
                  <h5 className="font-medium text-gray-800 mb-2">Storytelling Structure</h5>
                  <div className="bg-white rounded-lg p-4 border border-blue-100">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      {/* Act 1: Setup */}
                      <div className="bg-gradient-to-br from-blue-50 to-blue-100 border border-blue-200 rounded-lg p-3">
                        <div className="flex items-center mb-2">
                          <div className="w-6 h-6 bg-blue-500 text-white rounded-full flex items-center justify-center text-xs font-bold mr-2">1</div>
                          <span className="font-semibold text-blue-800">Act 1: Setup (0-3s)</span>
                        </div>
                        <div className="space-y-2">
                          {(() => {
                            const hookCriteria = chartData.filter(c => c.name.toLowerCase().includes('hook') || c.name.toLowerCase().includes('opening'));
                            const hookScore = hookCriteria.length > 0 ? hookCriteria[0].score : null;
                            const hookImprovements = (review.improvements || []).filter(i => 
                              i.toLowerCase().includes('hook') || i.toLowerCase().includes('opening') || i.toLowerCase().includes('start')
                            );
                            
                            if (hookScore && hookScore < 5) {
                              return (
                                <>
                                  <div className="flex items-center mb-1">
                                    <div className="w-2 h-2 bg-red-500 rounded-full mr-2"></div>
                                    <span className="text-sm font-medium text-red-700">Hook Needs Improvement</span>
                        </div>
                        <p className="text-sm text-gray-700">
                                    {hookImprovements.length > 0 ? hookImprovements[0] : 'Strengthen the opening with a compelling visual or statement that immediately captures attention and clearly states the value proposition.'}
                                  </p>
                                  <div className="bg-red-50 border border-red-200 rounded-lg p-2 mt-2">
                                    <p className="text-xs text-red-800 font-medium">💡 Quick Fix:</p>
                                    <p className="text-xs text-red-700">Start with a surprising fact, bold statement, or visual element that stops the scroll within the first 2 seconds.</p>
                                  </div>
                                </>
                              );
                            } else if (hookScore && hookScore >= 8) {
                              return (
                                <>
                                  <div className="flex items-center mb-1">
                                    <div className="w-2 h-2 bg-green-500 rounded-full mr-2"></div>
                                    <span className="text-sm font-medium text-green-700">Strong Hook Detected</span>
                                  </div>
                                  <p className="text-sm text-gray-700">
                                    Your opening effectively captures attention. Build on this strength while experimenting with new hook variations.
                                  </p>
                                  <div className="bg-green-50 border border-green-200 rounded-lg p-2 mt-2">
                                    <p className="text-xs text-green-800 font-medium">🎯 Pro Tip:</p>
                                    <p className="text-xs text-green-700">Try A/B testing different hook styles while maintaining your successful approach.</p>
                                  </div>
                                </>
                              );
                            } else {
                              return (
                                <>
                                  <p className="text-sm text-gray-700">
                                    Ensure the first 3 seconds clearly establish the problem, opportunity, or benefit for your audience.
                                  </p>
                                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-2 mt-2">
                                    <p className="text-xs text-blue-800 font-medium">🎯 Pro Tip:</p>
                                    <p className="text-xs text-blue-700">Use the "problem-agitation-solution" structure to immediately connect with viewer pain points.</p>
                                  </div>
                                </>
                              );
                            }
                          })()}
                        </div>
                      </div>

                      {/* Act 2: Development */}
                      <div className="bg-gradient-to-br from-purple-50 to-purple-100 border border-purple-200 rounded-lg p-3">
                        <div className="flex items-center mb-2">
                          <div className="w-6 h-6 bg-purple-500 text-white rounded-full flex items-center justify-center text-xs font-bold mr-2">2</div>
                          <span className="font-semibold text-purple-800">Act 2: Development (3-15s)</span>
                        </div>
                        <div className="space-y-2">
                          {(() => {
                            const engagementCriteria = chartData.filter(c => 
                              c.name.toLowerCase().includes('engagement') || 
                              c.name.toLowerCase().includes('story') || 
                              c.name.toLowerCase().includes('narrative') ||
                              c.name.toLowerCase().includes('content')
                            );
                            const engagementScore = engagementCriteria.length > 0 ? engagementCriteria[0].score : null;
                            const engagementImprovements = (review.improvements || []).filter(i => 
                              i.toLowerCase().includes('engagement') || 
                              i.toLowerCase().includes('story') || 
                              i.toLowerCase().includes('narrative') ||
                              i.toLowerCase().includes('middle')
                            );
                            
                            if (engagementScore && engagementScore < 5) {
                              return (
                                <>
                                  <div className="flex items-center mb-1">
                                    <div className="w-2 h-2 bg-red-500 rounded-full mr-2"></div>
                                    <span className="text-sm font-medium text-red-700">Engagement Needs Work</span>
                        </div>
                        <p className="text-sm text-gray-700">
                                    {engagementImprovements.length > 0 ? engagementImprovements[0] : 'Build a stronger narrative arc with clear progression, emotional beats, and engaging visual storytelling elements.'}
                                  </p>
                                  <div className="bg-red-50 border border-red-200 rounded-lg p-2 mt-2">
                                    <p className="text-xs text-red-800 font-medium">💡 Quick Fix:</p>
                                    <p className="text-xs text-red-700">Add visual variety, use transitions, and maintain consistent pacing to keep viewers engaged.</p>
                                  </div>
                                </>
                              );
                            } else if (engagementScore && engagementScore >= 8) {
                              return (
                                <>
                                  <div className="flex items-center mb-1">
                                    <div className="w-2 h-2 bg-green-500 rounded-full mr-2"></div>
                                    <span className="text-sm font-medium text-green-700">Strong Engagement</span>
                                  </div>
                                  <p className="text-sm text-gray-700">
                                    Your middle section effectively maintains audience interest. Continue building on this strength.
                                  </p>
                                  <div className="bg-green-50 border border-green-200 rounded-lg p-2 mt-2">
                                    <p className="text-xs text-green-800 font-medium">🎯 Pro Tip:</p>
                                    <p className="text-xs text-green-700">Experiment with new pacing techniques while maintaining your successful engagement formula.</p>
                                  </div>
                                </>
                              );
                            } else {
                              return (
                                <>
                                  <p className="text-sm text-gray-700">
                                    Develop the story with clear progression, building tension or interest while maintaining audience engagement throughout the middle section.
                                  </p>
                                  <div className="bg-purple-50 border border-purple-200 rounded-lg p-2 mt-2">
                                    <p className="text-xs text-purple-800 font-medium">🎯 Pro Tip:</p>
                                    <p className="text-xs text-purple-700">Use the "conflict-resolution" structure to maintain viewer interest and emotional investment.</p>
                                  </div>
                                </>
                              );
                            }
                          })()}
                        </div>
                      </div>

                      {/* Act 3: Resolution */}
                      <div className="bg-gradient-to-br from-green-50 to-green-100 border border-green-200 rounded-lg p-3">
                        <div className="flex items-center mb-2">
                          <div className="w-6 h-6 bg-green-500 text-white rounded-full flex items-center justify-center text-xs font-bold mr-2">3</div>
                          <span className="font-semibold text-green-800">Act 3: Resolution (15s+)</span>
                        </div>
                        <div className="space-y-2">
                          {(() => {
                            const ctaCriteria = chartData.filter(c => 
                              c.name.toLowerCase().includes('call') || 
                              c.name.toLowerCase().includes('action') ||
                              c.name.toLowerCase().includes('conclusion') ||
                              c.name.toLowerCase().includes('ending')
                            );
                            const ctaScore = ctaCriteria.length > 0 ? ctaCriteria[0].score : null;
                            const ctaImprovements = (review.improvements || []).filter(i => 
                              i.toLowerCase().includes('call') || 
                              i.toLowerCase().includes('action') ||
                              i.toLowerCase().includes('conclusion') ||
                              i.toLowerCase().includes('ending')
                            );
                            
                            if (ctaScore && ctaScore < 5) {
                              return (
                                <>
                                  <div className="flex items-center mb-1">
                                    <div className="w-2 h-2 bg-red-500 rounded-full mr-2"></div>
                                    <span className="text-sm font-medium text-red-700">CTA Needs Improvement</span>
                        </div>
                        <p className="text-sm text-gray-700">
                                    {ctaImprovements.length > 0 ? ctaImprovements[0] : 'End with a powerful, clear call-to-action that drives immediate response and creates urgency or excitement.'}
                                  </p>
                                  <div className="bg-red-50 border border-red-200 rounded-lg p-2 mt-2">
                                    <p className="text-xs text-red-800 font-medium">💡 Quick Fix:</p>
                                    <p className="text-xs text-red-700">Create urgency with time-limited offers, clear next steps, or compelling reasons to act now.</p>
                                  </div>
                                </>
                              );
                            } else if (ctaScore && ctaScore >= 8) {
                              return (
                                <>
                                  <div className="flex items-center mb-1">
                                    <div className="w-2 h-2 bg-green-500 rounded-full mr-2"></div>
                                    <span className="text-sm font-medium text-green-700">Strong Call-to-Action</span>
                                  </div>
                                  <p className="text-sm text-gray-700">
                                    Your conclusion effectively drives action. Maintain this strength while testing new CTA variations.
                                  </p>
                                  <div className="bg-green-50 border border-green-200 rounded-lg p-2 mt-2">
                                    <p className="text-xs text-green-800 font-medium">🎯 Pro Tip:</p>
                                    <p className="text-xs text-green-700">A/B test different CTA styles and urgency levels to optimize conversion rates.</p>
                                  </div>
                                </>
                              );
                            } else {
                              return (
                                <>
                                  <p className="text-sm text-gray-700">
                                    Conclude with a compelling call-to-action that reinforces the key message and motivates the desired audience response.
                                  </p>
                                  <div className="bg-green-50 border border-green-200 rounded-lg p-2 mt-2">
                                    <p className="text-xs text-green-800 font-medium">🎯 Pro Tip:</p>
                                    <p className="text-xs text-green-700">Use the "problem-solution-benefit" structure to create a compelling closing argument.</p>
                                  </div>
                                </>
                              );
                            }
                          })()}
                        </div>
                      </div>
                    </div>

                    {/* Storytelling Techniques */}
                    <div className="mt-4 pt-4 border-t border-gray-200">
                      <h6 className="font-medium text-gray-800 mb-3">Recommended Storytelling Techniques</h6>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                          <div className="flex items-center mb-1">
                            <div className="w-2 h-2 bg-amber-500 rounded-full mr-2"></div>
                            <span className="text-sm font-medium text-amber-800">Emotional Journey</span>
                          </div>
                          <p className="text-xs text-gray-700">
                            {overallScore >= 80 
                              ? 'Maintain emotional resonance while exploring new emotional angles'
                              : overallScore >= 50 
                              ? 'Strengthen emotional connection through relatable scenarios and authentic moments'
                              : 'Create a strong emotional foundation with clear character development and relatable situations'
                            }
                          </p>
                        </div>

                        <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-3">
                          <div className="flex items-center mb-1">
                            <div className="w-2 h-2 bg-indigo-500 rounded-full mr-2"></div>
                            <span className="text-sm font-medium text-indigo-800">Visual Storytelling</span>
                          </div>
                          <p className="text-xs text-gray-700">
                            {chartData.filter(c => c.name.toLowerCase().includes('visual') || c.name.toLowerCase().includes('brand')).length > 0 && chartData.find(c => c.name.toLowerCase().includes('visual') || c.name.toLowerCase().includes('brand'))?.score < 5
                              ? 'Enhance visual narrative with consistent branding, dynamic shots, and compelling visual metaphors'
                              : 'Maintain strong visual storytelling while experimenting with new visual techniques and compositions'
                            }
                          </p>
                        </div>

                        <div className="bg-pink-50 border border-pink-200 rounded-lg p-3">
                          <div className="flex items-center mb-1">
                            <div className="w-2 h-2 bg-pink-500 rounded-full mr-2"></div>
                            <span className="text-sm font-medium text-pink-800">Pacing & Rhythm</span>
                          </div>
                          <p className="text-xs text-gray-700">
                            {platformInfo.name === 'TikTok' 
                              ? 'Use quick cuts, dynamic transitions, and beat-sync editing for maximum impact'
                              : platformInfo.name === 'YouTube'
                              ? 'Allow for longer story development with strategic pacing and natural rhythm'
                              : 'Optimize pacing for platform-specific attention spans and engagement patterns'
                            }
                          </p>
                        </div>

                        <div className="bg-teal-50 border border-teal-200 rounded-lg p-3">
                          <div className="flex items-center mb-1">
                            <div className="w-2 h-2 bg-teal-500 rounded-full mr-2"></div>
                            <span className="text-sm font-medium text-teal-800">Character Development</span>
                          </div>
                          <p className="text-xs text-gray-700">
                            {chartData.filter(c => c.name.toLowerCase().includes('audience') || c.name.toLowerCase().includes('target')).length > 0 && chartData.find(c => c.name.toLowerCase().includes('audience') || c.name.toLowerCase().includes('target'))?.score < 5
                              ? 'Create stronger character connections that resonate with your target audience demographics'
                              : 'Build on existing character elements while adding depth and relatability'
                            }
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Virality Optimization */}
                <div>
                  <h5 className="font-medium text-gray-800 mb-2">Virality Optimization</h5>
                  <div className="bg-gradient-to-r from-pink-50 to-orange-50 border border-pink-200 rounded-lg p-4">
                    <div className="flex items-center mb-4">
                      <div className="w-8 h-8 bg-gradient-to-r from-pink-500 to-orange-500 rounded-lg flex items-center justify-center mr-3">
                        <TrendingUp className="w-4 h-4 text-white" />
                      </div>
                      <span className="font-semibold text-pink-800">Maximize Shareability & Viral Potential</span>
                    </div>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {/* Viral Triggers */}
                      <div className="bg-white rounded-lg p-4 border border-pink-100">
                        <h6 className="font-semibold text-gray-800 mb-3 flex items-center">
                          <div className="w-2 h-2 bg-pink-500 rounded-full mr-2"></div>
                          Viral Triggers
                        </h6>
                        <div className="space-y-2">
                          <div className="bg-red-50 border border-red-200 rounded-lg p-2">
                            <span className="text-sm font-medium text-red-800">Emotional Hooks</span>
                            <p className="text-xs text-gray-700 mt-1">
                              {overallScore >= 80 
                                ? 'Leverage surprise, joy, or inspiration moments to create emotional peaks'
                                : 'Create strong emotional moments that trigger sharing - use relatable struggles, victories, or unexpected outcomes'
                              }
                            </p>
                          </div>
                          <div className="bg-blue-50 border border-blue-200 rounded-lg p-2">
                            <span className="text-sm font-medium text-blue-800">Trending Elements</span>
                            <p className="text-xs text-gray-700 mt-1">
                              {platformInfo.name === 'TikTok' 
                                ? 'Incorporate trending sounds, hashtags, and challenges while adding your unique twist'
                                : platformInfo.name === 'Instagram'
                                ? 'Use trending filters, music, and format adaptations while maintaining brand voice'
                                : platformInfo.name === 'YouTube'
                                ? 'Reference trending topics, create trending thumbnail styles, and optimize for trending keywords'
                                : 'Adapt trending content formats and topics while maintaining authentic brand voice'
                              }
                            </p>
                          </div>
                          <div className="bg-green-50 border border-green-200 rounded-lg p-2">
                            <span className="text-sm font-medium text-green-800">Controversy & Debate</span>
                            <p className="text-xs text-gray-700 mt-1">
                              'Present thought-provoking questions, challenge common beliefs, or share unpopular opinions that spark discussion'
                            </p>
                          </div>
                        </div>
                      </div>

                      {/* Shareability Factors */}
                      <div className="bg-white rounded-lg p-4 border border-pink-100">
                        <h6 className="font-semibold text-gray-800 mb-3 flex items-center">
                          <div className="w-2 h-2 bg-orange-500 rounded-full mr-2"></div>
                          Shareability Factors
                        </h6>
                        <div className="space-y-2">
                          <div className="bg-purple-50 border border-purple-200 rounded-lg p-2">
                            <span className="text-sm font-medium text-purple-800">Relatability</span>
                            <p className="text-xs text-gray-700 mt-1">
                              {chartData.filter(c => c.name.toLowerCase().includes('audience')).length > 0 && chartData.find(c => c.name.toLowerCase().includes('audience'))?.score < 5
                                ? 'Create content that deeply resonates with your target audience\'s daily experiences and challenges'
                                : 'Build on existing relatability while exploring new audience segments and universal experiences'
                              }
                            </p>
                          </div>
                          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-2">
                            <span className="text-sm font-medium text-yellow-800">Educational Value</span>
                            <p className="text-xs text-gray-700 mt-1">
                              {overallScore >= 80 
                                ? 'Share insider tips, behind-the-scenes insights, or advanced knowledge that provides real value'
                                : 'Provide actionable tips, surprising facts, or practical knowledge that viewers can immediately apply'
                              }
                            </p>
                          </div>
                          <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-2">
                            <span className="text-sm font-medium text-indigo-800">Visual Appeal</span>
                            <p className="text-xs text-gray-700 mt-1">
                              {chartData.filter(c => c.name.toLowerCase().includes('visual') || c.name.toLowerCase().includes('brand')).length > 0 && chartData.find(c => c.name.toLowerCase().includes('visual') || c.name.toLowerCase().includes('brand'))?.score < 5
                                ? 'Create stunning visuals, smooth transitions, and eye-catching graphics that stop the scroll'
                                : 'Enhance visual elements with dynamic compositions, vibrant colors, and compelling visual metaphors'
                              }
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Platform-Specific Virality Tactics */}
                    <div className="mt-4 pt-4 border-t border-pink-200">
                      <h6 className="font-semibold text-gray-800 mb-3">Platform-Specific Virality Tactics</h6>
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                        {platformInfo.name === 'TikTok' && (
                          <>
                            <div className="bg-black bg-opacity-5 border border-gray-300 rounded-lg p-3">
                              <span className="text-sm font-medium text-gray-800">Sound Strategy</span>
                              <p className="text-xs text-gray-700 mt-1">Use trending audio, create original sounds, or add unexpected sound effects</p>
                            </div>
                            <div className="bg-black bg-opacity-5 border border-gray-300 rounded-lg p-3">
                              <span className="text-sm font-medium text-gray-800">Format Innovation</span>
                              <p className="text-xs text-gray-700 mt-1">Experiment with trending formats, transitions, and editing styles</p>
                            </div>
                            <div className="bg-black bg-opacity-5 border border-gray-300 rounded-lg p-3">
                              <span className="text-sm font-medium text-gray-800">Hashtag Strategy</span>
                              <p className="text-xs text-gray-700 mt-1">Mix trending hashtags with niche-specific tags for maximum reach</p>
                            </div>
                          </>
                        )}
                        
                        {platformInfo.name === 'Instagram' && (
                          <>
                            <div className="bg-gradient-to-r from-purple-50 to-pink-50 border border-purple-200 rounded-lg p-3">
                              <span className="text-sm font-medium text-purple-800">Story Features</span>
                              <p className="text-xs text-gray-700 mt-1">Use polls, questions, and interactive stickers to boost engagement</p>
                            </div>
                            <div className="bg-gradient-to-r from-purple-50 to-pink-50 border border-purple-200 rounded-lg p-3">
                              <span className="text-sm font-medium text-purple-800">Reels Optimization</span>
                              <p className="text-xs text-gray-700 mt-1">Create Reels with trending music, effects, and quick-paced editing</p>
                            </div>
                            <div className="bg-gradient-to-r from-purple-50 to-pink-50 border border-purple-200 rounded-lg p-3">
                              <span className="text-sm font-medium text-purple-800">Carousel Posts</span>
                              <p className="text-xs text-gray-700 mt-1">Use swipe-worthy carousels with valuable tips or behind-the-scenes content</p>
                            </div>
                          </>
                        )}

                        {platformInfo.name === 'Meta' && (
                          <>
                            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                              <span className="text-sm font-medium text-blue-800">Mobile-First</span>
                              <p className="text-xs text-gray-700 mt-1">Optimize for vertical viewing and mobile engagement patterns</p>
                            </div>
                            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                              <span className="text-sm font-medium text-blue-800">Native Features</span>
                              <p className="text-xs text-gray-700 mt-1">Leverage Facebook and Instagram native features for algorithm boost</p>
                            </div>
                            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                              <span className="text-sm font-medium text-blue-800">Cross-Platform</span>
                              <p className="text-xs text-gray-700 mt-1">Create content that works across Facebook and Instagram ecosystems</p>
                            </div>
                          </>
                        )}

                        {platformInfo.name === 'YouTube' && (
                          <>
                            <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                              <span className="text-sm font-medium text-red-800">Thumbnail Strategy</span>
                              <p className="text-xs text-gray-700 mt-1">Create click-worthy thumbnails with bold text and contrasting colors</p>
                            </div>
                            <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                              <span className="text-sm font-medium text-red-800">Hook Optimization</span>
                              <p className="text-xs text-gray-700 mt-1">Start with immediate value or intrigue to prevent early drop-off</p>
                            </div>
                            <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                              <span className="text-sm font-medium text-red-800">SEO Strategy</span>
                              <p className="text-xs text-gray-700 mt-1">Optimize titles, descriptions, and tags for trending search terms</p>
                            </div>
                          </>
                        )}

                        {platformInfo.name === 'Twitter' && (
                          <>
                            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                              <span className="text-sm font-medium text-blue-800">Timing Strategy</span>
                              <p className="text-xs text-gray-700 mt-1">Post during peak engagement hours and trending topic moments</p>
                            </div>
                            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                              <span className="text-sm font-medium text-blue-800">Thread Creation</span>
                              <p className="text-xs text-gray-700 mt-1">Create compelling threads that encourage retweets and replies</p>
                            </div>
                            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                              <span className="text-sm font-medium text-blue-800">Trend Participation</span>
                              <p className="text-xs text-gray-700 mt-1">Engage with trending hashtags and topics authentically</p>
                            </div>
                          </>
                        )}
                      </div>
                    </div>

                    {/* Virality Metrics */}
                    <div className="mt-4 pt-4 border-t border-pink-200">
                      <h6 className="font-semibold text-gray-800 mb-3">Virality Success Indicators</h6>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                        <div className="bg-gradient-to-br from-pink-100 to-red-100 border border-pink-300 rounded-lg p-3 text-center">
                          <div className="text-pink-700 font-bold text-lg">
                            {overallScore >= 80 ? '15%+' : overallScore >= 50 ? '25%+' : '40%+'}
                          </div>
                          <div className="text-xs text-gray-700 mt-1">Share Rate</div>
                        </div>
                        <div className="bg-gradient-to-br from-blue-100 to-indigo-100 border border-blue-300 rounded-lg p-3 text-center">
                          <div className="text-blue-700 font-bold text-lg">
                            {overallScore >= 80 ? '8%+' : overallScore >= 50 ? '12%+' : '20%+'}
                          </div>
                          <div className="text-xs text-gray-700 mt-1">Comment Rate</div>
                        </div>
                        <div className="bg-gradient-to-br from-green-100 to-teal-100 border border-green-300 rounded-lg p-3 text-center">
                          <div className="text-green-700 font-bold text-lg">
                            {overallScore >= 80 ? '5x' : overallScore >= 50 ? '8x' : '12x'}
                          </div>
                          <div className="text-xs text-gray-700 mt-1">Reach Multiplier</div>
                        </div>
                        <div className="bg-gradient-to-br from-purple-100 to-pink-100 border border-purple-300 rounded-lg p-3 text-center">
                          <div className="text-purple-700 font-bold text-lg">
                            {overallScore >= 80 ? '2x' : overallScore >= 50 ? '3x' : '5x'}
                          </div>
                          <div className="text-xs text-gray-700 mt-1">Engagement Lift</div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Key Focus Areas */}
                <div>
                  <h5 className="font-medium text-gray-800 mb-2">Key Focus Areas</h5>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {chartData.filter(c => c.score < 5).slice(0, 3).map((criterion, index) => (
                      <div key={index} className="bg-red-50 border border-red-200 rounded-lg p-3">
                        <div className="flex items-center mb-1">
                          <AlertTriangle className="w-4 h-4 text-red-600 mr-2" />
                          <span className="text-sm font-medium text-red-800">Critical: {criterion.fullName}</span>
                        </div>
                        <p className="text-xs text-gray-700">{criteria.find(c => c.name === criterion.fullName)?.recommendations || 'Focus on improving this area'}</p>
                      </div>
                    ))}
                    {chartData.filter(c => c.score >= 5 && c.score < 8).slice(0, 3).map((criterion, index) => (
                      <div key={index} className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                        <div className="flex items-center mb-1">
                          <Target className="w-4 h-4 text-amber-600 mr-2" />
                          <span className="text-sm font-medium text-amber-800">Improve: {criterion.fullName}</span>
                        </div>
                        <p className="text-xs text-gray-700">{criteria.find(c => c.name === criterion.fullName)?.recommendations || 'Optimize this element'}</p>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Platform Optimization */}
                <div>
                  <h5 className="font-medium text-gray-800 mb-2">Platform Optimization</h5>
                  <p className="text-gray-700 bg-white rounded-lg p-3 border border-blue-100">
                    Tailor the variation specifically for {platformInfo.name} by incorporating the platform's best practices. 
                    {platformInfo.name === 'Meta' ? ' Focus on mobile-first design, clear value proposition in the first 3 seconds, and strong call-to-action placement.' :
                     platformInfo.name === 'TikTok' ? ' Emphasize trending elements, authentic content, and quick-paced editing.' :
                     platformInfo.name === 'Instagram' ? ' Leverage visual storytelling, aesthetic consistency, and engaging captions.' :
                     platformInfo.name === 'YouTube' ? ' Structure for longer attention spans, include compelling thumbnails, and optimize for search.' :
                     platformInfo.name === 'Twitter' ? ' Keep messaging concise, leverage trending topics, and encourage sharing.' :
                     ' Follow platform-specific engagement patterns and content formats.'}
                  </p>
                </div>

                {/* Creative Direction */}
                <div>
                  <h5 className="font-medium text-gray-800 mb-2">Creative Direction</h5>
                  <div className="bg-white rounded-lg p-3 border border-blue-100">
                    <ul className="space-y-2 text-gray-700">
                      <li className="flex items-start">
                        <div className="w-2 h-2 bg-blue-500 rounded-full mt-2 mr-3 flex-shrink-0"></div>
                        <span><strong>Visual Style:</strong> {overallScore >= 80 ? 'Maintain current aesthetic while experimenting with new visual elements' : 'Consider refreshing the visual approach to improve engagement'}</span>
                      </li>
                      <li className="flex items-start">
                        <div className="w-2 h-2 bg-purple-500 rounded-full mt-2 mr-3 flex-shrink-0"></div>
                        <span><strong>Content Structure:</strong> {chartData.filter(c => c.name.toLowerCase().includes('hook')).length > 0 && chartData.find(c => c.name.toLowerCase().includes('hook'))?.score < 5 ? 'Strengthen the opening hook to capture attention immediately' : 'Build on the existing structure while enhancing key moments'}</span>
                      </li>
                      <li className="flex items-start">
                        <div className="w-2 h-2 bg-green-500 rounded-full mt-2 mr-3 flex-shrink-0"></div>
                        <span><strong>Call-to-Action:</strong> {chartData.filter(c => c.name.toLowerCase().includes('call') || c.name.toLowerCase().includes('action')).length > 0 && chartData.find(c => c.name.toLowerCase().includes('call') || c.name.toLowerCase().includes('action'))?.score < 5 ? 'Make the CTA more prominent and compelling' : 'Enhance the existing CTA for better conversion'}</span>
                      </li>
                      <li className="flex items-start">
                        <div className="w-2 h-2 bg-orange-500 rounded-full mt-2 mr-3 flex-shrink-0"></div>
                        <span><strong>Engagement Strategy:</strong> Focus on elements that encourage shares, comments, and saves specific to {platformInfo.name} users</span>
                      </li>
                    </ul>
                  </div>
                </div>

                {/* Success Metrics */}
                <div>
                  <h5 className="font-medium text-gray-800 mb-2">Success Metrics to Track</h5>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                    <div className="bg-green-50 border border-green-200 rounded-lg p-3 text-center">
                      <div className="text-green-600 font-semibold text-sm">Engagement Rate</div>
                      <div className="text-xs text-gray-600 mt-1">Target: {overallScore >= 80 ? 'Maintain +10%' : overallScore >= 50 ? '+25%' : '+50%'}</div>
                    </div>
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-center">
                      <div className="text-blue-600 font-semibold text-sm">Completion Rate</div>
                      <div className="text-xs text-gray-600 mt-1">Target: {overallScore >= 80 ? 'Maintain +5%' : overallScore >= 50 ? '+15%' : '+30%'}</div>
                    </div>
                    <div className="bg-purple-50 border border-purple-200 rounded-lg p-3 text-center">
                      <div className="text-purple-600 font-semibold text-sm">Conversion Rate</div>
                      <div className="text-xs text-gray-600 mt-1">Target: {overallScore >= 80 ? 'Maintain +5%' : overallScore >= 50 ? '+20%' : '+40%'}</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Personalized Action Plan */}
          <div className="card">
            <div className="flex items-center mb-6">
              <div className="flex items-center justify-center w-10 h-10 bg-gradient-to-r from-green-500 to-teal-600 rounded-lg mr-3">
                <Target className="w-5 h-5 text-white" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900">Personalized Action Plan</h3>
            </div>
            
            <div className="bg-gradient-to-r from-green-50 to-teal-50 border border-green-200 rounded-lg p-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Priority Actions */}
                <div>
                  <h4 className="font-semibold text-gray-900 mb-4 flex items-center">
                    <div className="w-2 h-2 bg-red-500 rounded-full mr-2"></div>
                    Priority Actions
                  </h4>
                  <div className="space-y-3">
                    {(() => {
                      const lowScoringCriteria = chartData.filter(c => c.score < 5);
                      const improvements = review.improvements || review.videoAnalysis?.improvements || [];
                      
                      if (lowScoringCriteria.length > 0) {
                        return lowScoringCriteria.slice(0, 3).map((criteria, index) => (
                          <div key={index} className="bg-white rounded-lg p-3 border border-red-100">
                            <div className="flex items-center justify-between mb-2">
                              <span className="font-medium text-red-800">{criteria.name}</span>
                              <span className="text-sm text-red-600">Score: {criteria.score}/10</span>
                            </div>
                            <p className="text-sm text-gray-700">
                              {criteria.recommendations || improvements.find(i => 
                                i.toLowerCase().includes(criteria.name.toLowerCase())
                              ) || `Focus on improving ${criteria.name.toLowerCase()} through targeted optimization.`}
                            </p>
                          </div>
                        ));
                      } else {
                        return (
                          <div className="bg-white rounded-lg p-3 border border-green-100">
                            <div className="flex items-center mb-2">
                              <div className="w-2 h-2 bg-green-500 rounded-full mr-2"></div>
                              <span className="font-medium text-green-800">Strong Performance</span>
                            </div>
                            <p className="text-sm text-gray-700">
                              Your video performs well across all criteria. Focus on optimization and experimentation to maximize results.
                            </p>
                          </div>
                        );
                      }
                    })()}
                  </div>
                </div>

                {/* Success Strategies */}
                <div>
                  <h4 className="font-semibold text-gray-900 mb-4 flex items-center">
                    <div className="w-2 h-2 bg-green-500 rounded-full mr-2"></div>
                    Success Strategies
                  </h4>
                  <div className="space-y-3">
                    {(() => {
                      const highScoringCriteria = chartData.filter(c => c.score >= 8);
                      const strengths = review.strengths || review.videoAnalysis?.strengths || [];
                      
                      if (highScoringCriteria.length > 0) {
                        return highScoringCriteria.slice(0, 3).map((criteria, index) => (
                          <div key={index} className="bg-white rounded-lg p-3 border border-green-100">
                            <div className="flex items-center justify-between mb-2">
                              <span className="font-medium text-green-800">{criteria.name}</span>
                              <span className="text-sm text-green-600">Score: {criteria.score}/10</span>
                            </div>
                            <p className="text-sm text-gray-700">
                              {criteria.explanation || strengths.find(s => 
                                s.toLowerCase().includes(criteria.name.toLowerCase())
                              ) || `Maintain your strong performance in ${criteria.name.toLowerCase()} while building on this success.`}
                            </p>
                          </div>
                        ));
                      } else {
                        return (
                          <div className="bg-white rounded-lg p-3 border border-amber-100">
                            <div className="flex items-center mb-2">
                              <div className="w-2 h-2 bg-amber-500 rounded-full mr-2"></div>
                              <span className="font-medium text-amber-800">Growth Opportunity</span>
                            </div>
                            <p className="text-sm text-gray-700">
                              Focus on building strengths across all areas. Consider A/B testing different approaches to identify winning strategies.
                            </p>
                          </div>
                        );
                      }
                    })()}
                  </div>
                </div>
              </div>

            </div>
          </div>
        </div>
      )}

      {activeTab === 'aida' && (
        <div className="space-y-8">
          {/* AIDA Overview */}
          <div className="mb-8">
            <div className="bg-gradient-to-br from-gray-50 via-blue-50 to-indigo-50 rounded-2xl p-8 border border-gray-200 shadow-lg">
              <div className="text-center mb-6">
                <div>
                  <h2 className="text-2xl font-bold text-gray-900">AIDA Analysis</h2>
                  <p className="text-gray-600">Marketing Framework Evaluation for Your Video Ad</p>
                  {(() => {
                    const aidaAnalysis = getAIDAAnalysis();
                    const { attention, interest, desire, action, overall } = aidaAnalysis;
                    
                    // Determine overall status
                    const getStatusInfo = (score) => {
                      if (score >= 8) return { label: 'Acceptable', color: 'green', bgColor: 'bg-green-100', textColor: 'text-green-800' };
                      if (score >= 5) return { label: 'Needs Improvement', color: 'amber', bgColor: 'bg-amber-100', textColor: 'text-amber-800' };
                      return { label: 'Critical', color: 'red', bgColor: 'bg-red-100', textColor: 'text-red-800' };
                    };
                    
                    const statusInfo = getStatusInfo(overall);
                    
                    return (
                      <div className="mt-4 space-y-3">
                        {/* Overall AIDA Score */}
                        <div className={`inline-flex items-center px-4 py-2 rounded-full ${statusInfo.bgColor} ${statusInfo.textColor}`}>
                          <div className={`w-3 h-3 rounded-full mr-2 ${
                            statusInfo.color === 'green' ? 'bg-green-500' : 
                            statusInfo.color === 'amber' ? 'bg-amber-500' : 'bg-red-500'
                          }`}></div>
                          <span className="font-semibold text-sm">
                            AIDA Score: {overall}/10 - {statusInfo.label}
                          </span>
                        </div>
                        
                        {/* Individual Element Scores */}
                        <div className="flex flex-wrap justify-center gap-3 text-xs">
                          <div className="flex items-center space-x-1">
                            <span className="font-medium text-gray-600">Attention:</span>
                            <span className={`font-semibold ${
                              attention.score >= 8 ? 'text-green-600' : 
                              attention.score >= 5 ? 'text-amber-600' : 'text-red-600'
                            }`}>{attention.score}/10</span>
                          </div>
                          <div className="flex items-center space-x-1">
                            <span className="font-medium text-gray-600">Interest:</span>
                            <span className={`font-semibold ${
                              interest.score >= 8 ? 'text-green-600' : 
                              interest.score >= 5 ? 'text-amber-600' : 'text-red-600'
                            }`}>{interest.score}/10</span>
                          </div>
                          <div className="flex items-center space-x-1">
                            <span className="font-medium text-gray-600">Desire:</span>
                            <span className={`font-semibold ${
                              desire.score >= 8 ? 'text-green-600' : 
                              desire.score >= 5 ? 'text-amber-600' : 'text-red-600'
                            }`}>{desire.score}/10</span>
                          </div>
                          <div className="flex items-center space-x-1">
                            <span className="font-medium text-gray-600">Action:</span>
                            <span className={`font-semibold ${
                              action.score >= 8 ? 'text-green-600' : 
                              action.score >= 5 ? 'text-amber-600' : 'text-red-600'
                            }`}>{action.score}/10</span>
                          </div>
                        </div>
                      </div>
                    );
                  })()}
                </div>
              </div>

              {/* AIDA Framework Breakdown */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                {/* Attention */}
                {(() => {
                  const aidaAnalysis = getAIDAAnalysis();
                  const { attention } = aidaAnalysis;
                  
                  return (
                    <div className="bg-white rounded-lg p-4 border border-orange-200">
                      <div className="flex items-center mb-3">
                        <div className="w-8 h-8 bg-orange-500 text-white rounded-full flex items-center justify-center text-sm font-bold mr-3">A</div>
                        <div>
                          <h5 className="font-semibold text-gray-900">Attention</h5>
                          <p className="text-xs text-gray-600">Hook the audience</p>
                        </div>
                      </div>
                      <div className="space-y-2">
                        <div className="flex items-center justify-between">
                          <span className="text-sm font-medium text-gray-700">Score:</span>
                          <span className={`font-bold text-lg ${
                            attention.score >= 8 ? 'text-green-600' : 
                            attention.score >= 5 ? 'text-amber-600' : 'text-red-600'
                          }`}>{attention.score}/10</span>
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-2">
                          <div 
                            className={`h-2 rounded-full ${
                              attention.score >= 8 ? 'bg-green-500' : 
                              attention.score >= 5 ? 'bg-amber-500' : 'bg-red-500'
                            }`}
                            style={{ width: `${attention.score * 10}%` }}
                          ></div>
                        </div>
                        <p className="text-xs text-gray-600">
                          {attention.score >= 8 
                            ? 'Strong visual hook and opening'
                            : attention.score >= 5 
                            ? 'Decent attention-grabbing elements'
                            : 'Needs stronger opening hook'
                          }
                        </p>
                      </div>
                    </div>
                  );
                })()}

                {/* Interest */}
                {(() => {
                  const aidaAnalysis = getAIDAAnalysis();
                  const { interest } = aidaAnalysis;
                  
                  return (
                    <div className="bg-white rounded-lg p-4 border border-blue-200">
                      <div className="flex items-center mb-3">
                        <div className="w-8 h-8 bg-blue-500 text-white rounded-full flex items-center justify-center text-sm font-bold mr-3">I</div>
                        <div>
                          <h5 className="font-semibold text-gray-900">Interest</h5>
                          <p className="text-xs text-gray-600">Build curiosity</p>
                        </div>
                      </div>
                      <div className="space-y-2">
                        <div className="flex items-center justify-between">
                          <span className="text-sm font-medium text-gray-700">Score:</span>
                          <span className={`font-bold text-lg ${
                            interest.score >= 8 ? 'text-green-600' : 
                            interest.score >= 5 ? 'text-amber-600' : 'text-red-600'
                          }`}>{interest.score}/10</span>
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-2">
                          <div 
                            className={`h-2 rounded-full ${
                              interest.score >= 8 ? 'bg-green-500' : 
                              interest.score >= 5 ? 'bg-amber-500' : 'bg-red-500'
                            }`}
                            style={{ width: `${interest.score * 10}%` }}
                          ></div>
                        </div>
                        <p className="text-xs text-gray-600">
                          {interest.score >= 8 
                            ? 'Highly engaging and relevant content'
                            : interest.score >= 5 
                            ? 'Moderate audience connection'
                            : 'Needs stronger relevance and engagement'
                          }
                        </p>
                      </div>
                    </div>
                  );
                })()}

                {/* Desire */}
                {(() => {
                  const aidaAnalysis = getAIDAAnalysis();
                  const { desire } = aidaAnalysis;
                  
                  return (
                    <div className="bg-white rounded-lg p-4 border border-purple-200">
                      <div className="flex items-center mb-3">
                        <div className="w-8 h-8 bg-purple-500 text-white rounded-full flex items-center justify-center text-sm font-bold mr-3">D</div>
                        <div>
                          <h5 className="font-semibold text-gray-900">Desire</h5>
                          <p className="text-xs text-gray-600">Show benefits & proof</p>
                        </div>
                      </div>
                      <div className="space-y-2">
                        <div className="flex items-center justify-between">
                          <span className="text-sm font-medium text-gray-700">Score:</span>
                          <span className={`font-bold text-lg ${
                            desire.score >= 8 ? 'text-green-600' : 
                            desire.score >= 5 ? 'text-amber-600' : 'text-red-600'
                          }`}>{desire.score}/10</span>
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-2">
                          <div 
                            className={`h-2 rounded-full ${
                              desire.score >= 8 ? 'bg-green-500' : 
                              desire.score >= 5 ? 'bg-amber-500' : 'bg-red-500'
                            }`}
                            style={{ width: `${desire.score * 10}%` }}
                          ></div>
                        </div>
                        <p className="text-xs text-gray-600">
                          {desire.score >= 8 
                            ? 'Clear benefits and emotional appeal'
                            : desire.score >= 5 
                            ? 'Some value proposition shown'
                            : 'Needs stronger benefits and proof'
                          }
                        </p>
                      </div>
                    </div>
                  );
                })()}

                {/* Action */}
                {(() => {
                  const aidaAnalysis = getAIDAAnalysis();
                  const { action } = aidaAnalysis;
                  
                  return (
                    <div className="bg-white rounded-lg p-4 border border-green-200">
                      <div className="flex items-center mb-3">
                        <div className="w-8 h-8 bg-green-500 text-white rounded-full flex items-center justify-center text-sm font-bold mr-3">A</div>
                        <div>
                          <h5 className="font-semibold text-gray-900">Action</h5>
                          <p className="text-xs text-gray-600">Clear call-to-action</p>
                        </div>
                      </div>
                      <div className="space-y-2">
                        <div className="flex items-center justify-between">
                          <span className="text-sm font-medium text-gray-700">Score:</span>
                          <span className={`font-bold text-lg ${
                            action.score >= 8 ? 'text-green-600' : 
                            action.score >= 5 ? 'text-amber-600' : 'text-red-600'
                          }`}>{action.score}/10</span>
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-2">
                          <div 
                            className={`h-2 rounded-full ${
                              action.score >= 8 ? 'bg-green-500' : 
                              action.score >= 5 ? 'bg-amber-500' : 'bg-red-500'
                            }`}
                            style={{ width: `${action.score * 10}%` }}
                          ></div>
                        </div>
                        <p className="text-xs text-gray-600">
                          {action.score >= 8 
                            ? 'Strong, clear call-to-action'
                            : action.score >= 5 
                            ? 'Decent action guidance'
                            : 'Needs clearer call-to-action'
                          }
                        </p>
                      </div>
                    </div>
                  );
                })()}
              </div>
            </div>
          </div>

          {/* AIDA Recommendations */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Strengths */}
            <div className="card">
              <div className="flex items-center mb-4">
                <div className="w-8 h-8 bg-green-100 rounded-lg flex items-center justify-center mr-3">
                  <span className="text-green-600 font-bold">✓</span>
                </div>
                <h3 className="text-lg font-semibold text-gray-900">AIDA Strengths</h3>
              </div>
              <div className="space-y-3">
                {(() => {
                  const aidaAnalysis = getAIDAAnalysis();
                  const { attention, interest, desire, action } = aidaAnalysis;
                  const strengths = [];
                  
                  if (attention.score >= 7) {
                    strengths.push({
                      element: 'Attention',
                      description: 'Strong visual hook and opening that captures viewer attention effectively',
                      color: 'text-green-700',
                      bgColor: 'bg-green-50',
                      borderColor: 'border-green-200'
                    });
                  }
                  
                  if (interest.score >= 7) {
                    strengths.push({
                      element: 'Interest',
                      description: 'Content builds curiosity and maintains audience engagement throughout',
                      color: 'text-blue-700',
                      bgColor: 'bg-blue-50',
                      borderColor: 'border-blue-200'
                    });
                  }
                  
                  if (desire.score >= 7) {
                    strengths.push({
                      element: 'Desire',
                      description: 'Clear benefits and emotional appeal that drives viewer desire',
                      color: 'text-purple-700',
                      bgColor: 'bg-purple-50',
                      borderColor: 'border-purple-200'
                    });
                  }
                  
                  if (action.score >= 7) {
                    strengths.push({
                      element: 'Action',
                      description: 'Effective call-to-action that guides viewers toward the desired action',
                      color: 'text-green-700',
                      bgColor: 'bg-green-50',
                      borderColor: 'border-green-200'
                    });
                  }
                  
                  if (strengths.length === 0) {
                    return (
                      <div className="text-center py-4">
                        <p className="text-gray-500 text-sm">Focus on improving all AIDA elements to build stronger marketing effectiveness.</p>
                      </div>
                    );
                  }
                  
                  return strengths.map((strength, index) => (
                    <div key={index} className={`p-3 rounded-lg border ${strength.bgColor} ${strength.borderColor}`}>
                      <div className="flex items-center mb-1">
                        <span className={`font-semibold text-sm ${strength.color}`}>{strength.element}</span>
                      </div>
                      <p className="text-sm text-gray-600">{strength.description}</p>
                    </div>
                  ));
                })()}
              </div>
            </div>

            {/* Improvements */}
            <div className="card">
              <div className="flex items-center mb-4">
                <div className="w-8 h-8 bg-amber-100 rounded-lg flex items-center justify-center mr-3">
                  <span className="text-amber-600 font-bold">!</span>
                </div>
                <h3 className="text-lg font-semibold text-gray-900">AIDA Improvements</h3>
              </div>
              <div className="space-y-3">
                {(() => {
                  const aidaAnalysis = getAIDAAnalysis();
                  const { attention, interest, desire, action } = aidaAnalysis;
                  const improvements = [];
                  
                  if (attention.score < 7) {
                    improvements.push({
                      element: 'Attention',
                      description: 'Strengthen the opening hook with compelling visuals, bold headlines, or unexpected elements',
                      recommendation: 'Use the first 3 seconds to create immediate visual impact and curiosity',
                      color: 'text-orange-700',
                      bgColor: 'bg-orange-50',
                      borderColor: 'border-orange-200'
                    });
                  }
                  
                  if (interest.score < 7) {
                    improvements.push({
                      element: 'Interest',
                      description: 'Build stronger audience connection through relatable scenarios and relevant content',
                      recommendation: 'Show how your product/service solves real problems your audience faces',
                      color: 'text-blue-700',
                      bgColor: 'bg-blue-50',
                      borderColor: 'border-blue-200'
                    });
                  }
                  
                  if (desire.score < 7) {
                    improvements.push({
                      element: 'Desire',
                      description: 'Enhance emotional appeal and social proof to drive stronger desire',
                      recommendation: 'Include testimonials, before/after results, or emotional transformation stories',
                      color: 'text-purple-700',
                      bgColor: 'bg-purple-50',
                      borderColor: 'border-purple-200'
                    });
                  }
                  
                  if (action.score < 7) {
                    improvements.push({
                      element: 'Action',
                      description: 'Create clearer, more compelling call-to-action with urgency',
                      recommendation: 'Use specific action words like "Buy Now", "Sign Up Today", or "Get Started"',
                      color: 'text-green-700',
                      bgColor: 'bg-green-50',
                      borderColor: 'border-green-200'
                    });
                  }
                  
                  if (improvements.length === 0) {
                    return (
                      <div className="text-center py-4">
                        <p className="text-gray-500 text-sm">Great job! All AIDA elements are performing well.</p>
                      </div>
                    );
                  }
                  
                  return improvements.map((improvement, index) => (
                    <div key={index} className={`p-3 rounded-lg border ${improvement.bgColor} ${improvement.borderColor}`}>
                      <div className="flex items-center mb-1">
                        <span className={`font-semibold text-sm ${improvement.color}`}>{improvement.element}</span>
                      </div>
                      <p className="text-sm text-gray-600 mb-2">{improvement.description}</p>
                      <div className="bg-white rounded p-2">
                        <p className="text-xs text-gray-700 font-medium">💡 {improvement.recommendation}</p>
                      </div>
                    </div>
                  ));
                })()}
              </div>
            </div>
          </div>
        </div>
      )}

      {activeTab === 'pas' && (
        <div className="space-y-8">
          {/* PAS Overview */}
          <div className="mb-8">
            <div className="bg-gradient-to-br from-gray-50 via-blue-50 to-indigo-50 rounded-2xl p-8 border border-gray-200 shadow-lg">
              <div className="text-center mb-6">
                <div>
                  <h2 className="text-2xl font-bold text-gray-900">PAS Analysis</h2>
                  <p className="text-gray-600">Pain Point Persuasion Framework for Your Video Ad</p>
                  {(() => {
                    const pasAnalysis = getPASAnalysis();
                    const { problem, agitate, solution, overall } = pasAnalysis;
                    
                    // Determine overall status
                    const getStatusInfo = (score) => {
                      if (score >= 8) return { label: 'Acceptable', color: 'green', bgColor: 'bg-green-100', textColor: 'text-green-800' };
                      if (score >= 5) return { label: 'Needs Improvement', color: 'amber', bgColor: 'bg-amber-100', textColor: 'text-amber-800' };
                      return { label: 'Critical', color: 'red', bgColor: 'bg-red-100', textColor: 'text-red-800' };
                    };
                    
                    const statusInfo = getStatusInfo(overall);
                    
                    return (
                      <div className="mt-4 space-y-3">
                        {/* Overall PAS Score */}
                        <div className={`inline-flex items-center px-4 py-2 rounded-full ${statusInfo.bgColor} ${statusInfo.textColor}`}>
                          <div className={`w-3 h-3 rounded-full mr-2 ${
                            statusInfo.color === 'green' ? 'bg-green-500' : 
                            statusInfo.color === 'amber' ? 'bg-amber-500' : 'bg-red-500'
                          }`}></div>
                          <span className="font-semibold text-sm">
                            PAS Score: {overall}/10 - {statusInfo.label}
                          </span>
                        </div>
                        
                        {/* Individual Element Scores */}
                        <div className="flex flex-wrap justify-center gap-3 text-xs">
                          <div className="flex items-center space-x-1">
                            <span className="font-medium text-gray-600">Problem:</span>
                            <span className={`font-semibold ${
                              problem.score >= 8 ? 'text-green-600' : 
                              problem.score >= 5 ? 'text-amber-600' : 'text-red-600'
                            }`}>{problem.score.toFixed(1)}/10</span>
                          </div>
                          <div className="flex items-center space-x-1">
                            <span className="font-medium text-gray-600">Agitate:</span>
                            <span className={`font-semibold ${
                              agitate.score >= 8 ? 'text-green-600' : 
                              agitate.score >= 5 ? 'text-amber-600' : 'text-red-600'
                            }`}>{agitate.score.toFixed(1)}/10</span>
                          </div>
                          <div className="flex items-center space-x-1">
                            <span className="font-medium text-gray-600">Solution:</span>
                            <span className={`font-semibold ${
                              solution.score >= 8 ? 'text-green-600' : 
                              solution.score >= 5 ? 'text-amber-600' : 'text-red-600'
                            }`}>{solution.score.toFixed(1)}/10</span>
                          </div>
                        </div>
                      </div>
                    );
                  })()}
                </div>
              </div>

              {/* PAS Framework Breakdown */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {/* Problem */}
                {(() => {
                  const pasAnalysis = getPASAnalysis();
                  const { problem } = pasAnalysis;
                  
                  return (
                    <div className="bg-white rounded-lg p-4 border border-red-200">
                      <div className="flex items-center mb-3">
                        <div className="w-8 h-8 bg-red-500 text-white rounded-full flex items-center justify-center text-sm font-bold mr-3">P</div>
                        <div>
                          <h5 className="font-semibold text-gray-900">Problem</h5>
                          <p className="text-xs text-gray-600">Identify pain points</p>
                        </div>
                      </div>
                      <div className="space-y-2">
                        <div className="flex items-center justify-between">
                          <span className="text-sm font-medium text-gray-700">Score:</span>
                          <span className={`font-bold text-lg ${
                            problem.score >= 8 ? 'text-green-600' : 
                            problem.score >= 5 ? 'text-amber-600' : 'text-red-600'
                          }`}>{problem.score.toFixed(1)}/10</span>
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-2">
                          <div 
                            className={`h-2 rounded-full ${
                              problem.score >= 8 ? 'bg-green-500' : 
                              problem.score >= 5 ? 'bg-amber-500' : 'bg-red-500'
                            }`}
                            style={{ width: `${problem.score * 10}%` }}
                          ></div>
                        </div>
                        <p className="text-xs text-gray-600">
                          {problem.description}
                        </p>
                        <div className="mt-2 text-xs">
                          <span className="text-gray-500">Strength: </span>
                          <span className={`font-medium ${
                            problem.strength === 'strong' ? 'text-green-600' : 
                            problem.strength === 'moderate' ? 'text-amber-600' : 'text-red-600'
                          }`}>
                            {problem.strength.charAt(0).toUpperCase() + problem.strength.slice(1)}
                          </span>
                          <span className="text-gray-500 ml-2">
                            ({problem.criteria.length} criteria)
                          </span>
                        </div>
                      </div>
                    </div>
                  );
                })()}

                {/* Agitate */}
                {(() => {
                  const pasAnalysis = getPASAnalysis();
                  const { agitate } = pasAnalysis;
                  
                  return (
                    <div className="bg-white rounded-lg p-4 border border-orange-200">
                      <div className="flex items-center mb-3">
                        <div className="w-8 h-8 bg-orange-500 text-white rounded-full flex items-center justify-center text-sm font-bold mr-3">A</div>
                        <div>
                          <h5 className="font-semibold text-gray-900">Agitate</h5>
                          <p className="text-xs text-gray-600">Make it urgent</p>
                        </div>
                      </div>
                      <div className="space-y-2">
                        <div className="flex items-center justify-between">
                          <span className="text-sm font-medium text-gray-700">Score:</span>
                          <span className={`font-bold text-lg ${
                            agitate.score >= 8 ? 'text-green-600' : 
                            agitate.score >= 5 ? 'text-amber-600' : 'text-red-600'
                          }`}>{agitate.score.toFixed(1)}/10</span>
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-2">
                          <div 
                            className={`h-2 rounded-full ${
                              agitate.score >= 8 ? 'bg-green-500' : 
                              agitate.score >= 5 ? 'bg-amber-500' : 'bg-red-500'
                            }`}
                            style={{ width: `${agitate.score * 10}%` }}
                          ></div>
                        </div>
                        <p className="text-xs text-gray-600">
                          {agitate.description}
                        </p>
                        <div className="mt-2 text-xs">
                          <span className="text-gray-500">Strength: </span>
                          <span className={`font-medium ${
                            agitate.strength === 'strong' ? 'text-green-600' : 
                            agitate.strength === 'moderate' ? 'text-amber-600' : 'text-red-600'
                          }`}>
                            {agitate.strength.charAt(0).toUpperCase() + agitate.strength.slice(1)}
                          </span>
                          <span className="text-gray-500 ml-2">
                            ({agitate.criteria.length} criteria)
                          </span>
                        </div>
                      </div>
                    </div>
                  );
                })()}

                {/* Solution */}
                {(() => {
                  const pasAnalysis = getPASAnalysis();
                  const { solution } = pasAnalysis;
                  
                  return (
                    <div className="bg-white rounded-lg p-4 border border-green-200">
                      <div className="flex items-center mb-3">
                        <div className="w-8 h-8 bg-green-500 text-white rounded-full flex items-center justify-center text-sm font-bold mr-3">S</div>
                        <div>
                          <h5 className="font-semibold text-gray-900">Solution</h5>
                          <p className="text-xs text-gray-600">Present the fix</p>
                        </div>
                      </div>
                      <div className="space-y-2">
                        <div className="flex items-center justify-between">
                          <span className="text-sm font-medium text-gray-700">Score:</span>
                          <span className={`font-bold text-lg ${
                            solution.score >= 8 ? 'text-green-600' : 
                            solution.score >= 5 ? 'text-amber-600' : 'text-red-600'
                          }`}>{solution.score.toFixed(1)}/10</span>
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-2">
                          <div 
                            className={`h-2 rounded-full ${
                              solution.score >= 8 ? 'bg-green-500' : 
                              solution.score >= 5 ? 'bg-amber-500' : 'bg-red-500'
                            }`}
                            style={{ width: `${solution.score * 10}%` }}
                          ></div>
                        </div>
                        <p className="text-xs text-gray-600">
                          {solution.description}
                        </p>
                        <div className="mt-2 text-xs">
                          <span className="text-gray-500">Strength: </span>
                          <span className={`font-medium ${
                            solution.strength === 'strong' ? 'text-green-600' : 
                            solution.strength === 'moderate' ? 'text-amber-600' : 'text-red-600'
                          }`}>
                            {solution.strength.charAt(0).toUpperCase() + solution.strength.slice(1)}
                          </span>
                          <span className="text-gray-500 ml-2">
                            ({solution.criteria.length} criteria)
                          </span>
                        </div>
                      </div>
                    </div>
                  );
                })()}
              </div>
            </div>
          </div>

          {/* Enhanced PAS Framework Insights */}
          <div className="mb-8">
            <div className="bg-gradient-to-br from-red-50 via-pink-50 to-purple-50 rounded-2xl p-6 border border-red-200 shadow-lg">
              <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center">
                <div className="w-8 h-8 bg-gradient-to-r from-red-500 to-pink-500 rounded-lg flex items-center justify-center mr-3">
                  <span className="text-white text-sm font-bold">⚡</span>
                </div>
                Persuasion Analysis & Flow
              </h3>
              {(() => {
                const pasAnalysis = getPASAnalysis();
                const { isBalanced, persuasionFlow, hasStrongEscalation, hasClearSolution, totalCriteria } = pasAnalysis;
                
                return (
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    {/* Persuasion Flow */}
                    <div className="bg-white rounded-lg p-4 border border-red-200">
                      <div className="flex items-center mb-2">
                        <div className={`w-3 h-3 rounded-full mr-2 ${
                          persuasionFlow === 'excellent' ? 'bg-green-500' : 
                          persuasionFlow === 'good' ? 'bg-blue-500' : 'bg-amber-500'
                        }`}></div>
                        <h4 className="font-semibold text-gray-900 text-sm">Persuasion Flow</h4>
                      </div>
                      <p className="text-xs text-gray-600">
                        {persuasionFlow === 'excellent' 
                          ? 'Excellent progression from problem through agitation to solution'
                          : persuasionFlow === 'good' 
                          ? 'Good persuasion flow with minor improvements needed'
                          : 'Persuasion flow needs improvement - consider restructuring'
                        }
                      </p>
                    </div>

                    {/* Emotional Escalation */}
                    <div className="bg-white rounded-lg p-4 border border-red-200">
                      <div className="flex items-center mb-2">
                        <div className={`w-3 h-3 rounded-full mr-2 ${
                          hasStrongEscalation ? 'bg-green-500' : 'bg-amber-500'
                        }`}></div>
                        <h4 className="font-semibold text-gray-900 text-sm">Emotional Escalation</h4>
                      </div>
                      <p className="text-xs text-gray-600">
                        {hasStrongEscalation 
                          ? 'Strong emotional escalation from problem to agitation'
                          : 'Escalation needs strengthening - intensify agitation beyond problem'
                        }
                      </p>
                    </div>

                    {/* Solution Clarity */}
                    <div className="bg-white rounded-lg p-4 border border-red-200">
                      <div className="flex items-center mb-2">
                        <div className={`w-3 h-3 rounded-full mr-2 ${
                          hasClearSolution ? 'bg-green-500' : 'bg-amber-500'
                        }`}></div>
                        <h4 className="font-semibold text-gray-900 text-sm">Solution Clarity</h4>
                      </div>
                      <p className="text-xs text-gray-600">
                        {hasClearSolution 
                          ? 'Clear product/service positioning as the solution'
                          : 'Solution positioning needs strengthening - clarify product/service role'
                        }
                        <br />
                        <span className="font-medium text-red-600">{totalCriteria} criteria analyzed</span>
                      </p>
                    </div>
                  </div>
                );
              })()}
            </div>
          </div>

          {/* Enhanced PAS Framework Recommendations */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Strengths */}
            <div className="card">
              <div className="flex items-center mb-4">
                <div className="w-8 h-8 bg-green-100 rounded-lg flex items-center justify-center mr-3">
                  <span className="text-green-600 font-bold">✓</span>
                </div>
                <h3 className="text-lg font-semibold text-gray-900">PAS Strengths</h3>
              </div>
              <div className="space-y-3">
                {(() => {
                  const pasAnalysis = getPASAnalysis();
                  const { problem, agitate, solution } = pasAnalysis;
                  const strengths = [];
                  
                  if (problem.score >= 7) {
                    strengths.push({
                      element: 'Problem',
                      description: 'Effectively identifies and communicates the viewer\'s pain points and challenges',
                      color: 'text-red-700',
                      bgColor: 'bg-red-50',
                      borderColor: 'border-red-200'
                    });
                  }
                  
                  if (agitate.score >= 7) {
                    strengths.push({
                      element: 'Agitate',
                      description: 'Creates urgency and shows the real cost of ignoring the problem',
                      color: 'text-orange-700',
                      bgColor: 'bg-orange-50',
                      borderColor: 'border-orange-200'
                    });
                  }
                  
                  if (solution.score >= 7) {
                    strengths.push({
                      element: 'Solution',
                      description: 'Clearly presents your product/service as the ideal fix for the problem',
                      color: 'text-green-700',
                      bgColor: 'bg-green-50',
                      borderColor: 'border-green-200'
                    });
                  }
                  
                  if (strengths.length === 0) {
                    return (
                      <div className="text-center py-4">
                        <p className="text-gray-500 text-sm">Focus on improving all PAS elements to build stronger persuasion effectiveness.</p>
                      </div>
                    );
                  }
                  
                  return strengths.map((strength, index) => (
                    <div key={index} className={`p-3 rounded-lg border ${strength.bgColor} ${strength.borderColor}`}>
                      <div className="flex items-center mb-1">
                        <span className={`font-semibold text-sm ${strength.color}`}>{strength.element}</span>
                      </div>
                      <p className="text-sm text-gray-600">{strength.description}</p>
                    </div>
                  ));
                })()}
              </div>
            </div>

            {/* Enhanced Actionable Recommendations */}
            <div className="card">
              <div className="flex items-center mb-4">
                <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center mr-3">
                  <span className="text-blue-600 font-bold">💡</span>
                </div>
                <h3 className="text-lg font-semibold text-gray-900">Actionable Recommendations</h3>
              </div>
              <div className="space-y-4">
                {(() => {
                  const pasAnalysis = getPASAnalysis();
                  const { recommendations } = pasAnalysis;
                  
                  if (recommendations.length === 0) {
                    return (
                      <div className="text-center py-4">
                        <p className="text-green-600 text-sm font-medium">Excellent PAS structure! All elements are well-developed.</p>
                      </div>
                    );
                  }
                  
                  return recommendations.map((rec, index) => (
                    <div key={index} className={`p-4 rounded-lg border-l-4 ${
                      rec.priority === 'high' ? 'border-red-500 bg-red-50' : 'border-amber-500 bg-amber-50'
                    }`}>
                      <div className="flex items-start justify-between mb-2">
                        <h4 className="font-semibold text-gray-900 text-sm">{rec.title}</h4>
                        <span className={`text-xs font-medium px-2 py-1 rounded-full ${
                          rec.priority === 'high' ? 'bg-red-100 text-red-700' : 'bg-amber-100 text-amber-700'
                        }`}>
                          {rec.priority === 'high' ? 'High Priority' : 'Medium Priority'}
                        </span>
                      </div>
                      <p className="text-sm text-gray-600 mb-3">{rec.description}</p>
                      <div className="space-y-1">
                        <p className="text-xs font-medium text-gray-700">Action Items:</p>
                        {rec.tips.map((tip, tipIndex) => (
                          <div key={tipIndex} className="flex items-start space-x-2">
                            <span className="text-blue-500 text-xs mt-1">•</span>
                            <span className="text-xs text-gray-600">{tip}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  ));
                })()}
              </div>
            </div>

            {/* Improvements */}
            <div className="card">
              <div className="flex items-center mb-4">
                <div className="w-8 h-8 bg-amber-100 rounded-lg flex items-center justify-center mr-3">
                  <span className="text-amber-600 font-bold">!</span>
                </div>
                <h3 className="text-lg font-semibold text-gray-900">PAS Improvements</h3>
              </div>
              <div className="space-y-3">
                {(() => {
                  const pasAnalysis = getPASAnalysis();
                  const { problem, agitate, solution } = pasAnalysis;
                  const improvements = [];
                  
                  if (problem.score < 7) {
                    improvements.push({
                      element: 'Problem',
                      description: 'Strengthen problem identification with specific pain points your audience faces',
                      recommendation: 'Start with relatable scenarios that make viewers think "That\'s me!" or "I have that problem too"',
                      color: 'text-red-700',
                      bgColor: 'bg-red-50',
                      borderColor: 'border-red-200'
                    });
                  }
                  
                  if (agitate.score < 7) {
                    improvements.push({
                      element: 'Agitate',
                      description: 'Increase urgency by showing the consequences and cost of not solving the problem',
                      recommendation: 'Use phrases like "Every day you wait..." or "The longer you delay..." to create urgency',
                      color: 'text-orange-700',
                      bgColor: 'bg-orange-50',
                      borderColor: 'border-orange-200'
                    });
                  }
                  
                  if (solution.score < 7) {
                    improvements.push({
                      element: 'Solution',
                      description: 'Present your product/service more clearly as the ideal solution to their problem',
                      recommendation: 'Show specific benefits and results that directly address the pain points identified',
                      color: 'text-green-700',
                      bgColor: 'bg-green-50',
                      borderColor: 'border-green-200'
                    });
                  }
                  
                  if (improvements.length === 0) {
                    return (
                      <div className="text-center py-4">
                        <p className="text-gray-500 text-sm">Great job! All PAS elements are performing well.</p>
                      </div>
                    );
                  }
                  
                  return improvements.map((improvement, index) => (
                    <div key={index} className={`p-3 rounded-lg border ${improvement.bgColor} ${improvement.borderColor}`}>
                      <div className="flex items-center mb-1">
                        <span className={`font-semibold text-sm ${improvement.color}`}>{improvement.element}</span>
                      </div>
                      <p className="text-sm text-gray-600 mb-2">{improvement.description}</p>
                      <div className="bg-white rounded p-2">
                        <p className="text-xs text-gray-700 font-medium">💡 {improvement.recommendation}</p>
                      </div>
                    </div>
                  ));
                })()}
              </div>
            </div>
          </div>
        </div>
      )}

      {activeTab === 'bab' && (
        <div className="space-y-8">
          {/* BAB Overview */}
          <div className="mb-8">
            <div className="bg-gradient-to-br from-gray-50 via-blue-50 to-indigo-50 rounded-2xl p-8 border border-gray-200 shadow-lg">
              <div className="text-center mb-6">
                <div>
                  <h2 className="text-2xl font-bold text-gray-900">BAB Analysis</h2>
                  <p className="text-gray-600">Transformation-Driven Storytelling Framework for Your Video Ad</p>
                  {(() => {
                    const babAnalysis = getBABAnalysis();
                    const { before, after, bridge, overall } = babAnalysis;
                    
                    // Determine overall status
                    const getStatusInfo = (score) => {
                      if (score >= 8) return { label: 'Acceptable', color: 'green', bgColor: 'bg-green-100', textColor: 'text-green-800' };
                      if (score >= 5) return { label: 'Needs Improvement', color: 'amber', bgColor: 'bg-amber-100', textColor: 'text-amber-800' };
                      return { label: 'Critical', color: 'red', bgColor: 'bg-red-100', textColor: 'text-red-800' };
                    };
                    
                    const statusInfo = getStatusInfo(overall);
                    
                    return (
                      <div className="mt-4 space-y-3">
                        {/* Overall BAB Score */}
                        <div className={`inline-flex items-center px-4 py-2 rounded-full ${statusInfo.bgColor} ${statusInfo.textColor}`}>
                          <div className={`w-3 h-3 rounded-full mr-2 ${
                            statusInfo.color === 'green' ? 'bg-green-500' : 
                            statusInfo.color === 'amber' ? 'bg-amber-500' : 'bg-red-500'
                          }`}></div>
                          <span className="font-semibold text-sm">
                            BAB Score: {overall}/10 - {statusInfo.label}
                          </span>
                        </div>
                        
                        {/* Individual Element Scores */}
                        <div className="flex flex-wrap justify-center gap-3 text-xs">
                          <div className="flex items-center space-x-1">
                            <span className="font-medium text-gray-600">Before:</span>
                            <span className={`font-semibold ${
                              before.score >= 8 ? 'text-green-600' : 
                              before.score >= 5 ? 'text-amber-600' : 'text-red-600'
                            }`}>{before.score.toFixed(1)}/10</span>
                          </div>
                          <div className="flex items-center space-x-1">
                            <span className="font-medium text-gray-600">After:</span>
                            <span className={`font-semibold ${
                              after.score >= 8 ? 'text-green-600' : 
                              after.score >= 5 ? 'text-amber-600' : 'text-red-600'
                            }`}>{after.score.toFixed(1)}/10</span>
                          </div>
                          <div className="flex items-center space-x-1">
                            <span className="font-medium text-gray-600">Bridge:</span>
                            <span className={`font-semibold ${
                              bridge.score >= 8 ? 'text-green-600' : 
                              bridge.score >= 5 ? 'text-amber-600' : 'text-red-600'
                            }`}>{bridge.score.toFixed(1)}/10</span>
                          </div>
                        </div>
                      </div>
                    );
                  })()}
                </div>
              </div>

              {/* BAB Framework Breakdown */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {/* Before */}
                {(() => {
                  const babAnalysis = getBABAnalysis();
                  const { before } = babAnalysis;
                  
                  return (
                    <div className="bg-white rounded-lg p-4 border border-blue-200">
                      <div className="flex items-center mb-3">
                        <div className="w-8 h-8 bg-blue-500 text-white rounded-full flex items-center justify-center text-sm font-bold mr-3">B</div>
                        <div>
                          <h5 className="font-semibold text-gray-900">Before</h5>
                          <p className="text-xs text-gray-600">Describe current struggles</p>
                        </div>
                      </div>
                      <div className="space-y-2">
                        <div className="flex items-center justify-between">
                          <span className="text-sm font-medium text-gray-700">Score:</span>
                          <span className={`font-bold text-lg ${
                            before.score >= 8 ? 'text-green-600' : 
                            before.score >= 5 ? 'text-amber-600' : 'text-red-600'
                          }`}>{before.score.toFixed(1)}/10</span>
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-2">
                          <div 
                            className={`h-2 rounded-full ${
                              before.score >= 8 ? 'bg-green-500' : 
                              before.score >= 5 ? 'bg-amber-500' : 'bg-red-500'
                            }`}
                            style={{ width: `${before.score * 10}%` }}
                          ></div>
                        </div>
                        <p className="text-xs text-gray-600">
                          {before.description}
                        </p>
                        <div className="mt-2 text-xs">
                          <span className="text-gray-500">Strength: </span>
                          <span className={`font-medium ${
                            before.strength === 'strong' ? 'text-green-600' : 
                            before.strength === 'moderate' ? 'text-amber-600' : 'text-red-600'
                          }`}>
                            {before.strength.charAt(0).toUpperCase() + before.strength.slice(1)}
                          </span>
                          <span className="text-gray-500 ml-2">
                            ({before.criteria.length} criteria)
                          </span>
                        </div>
                      </div>
                    </div>
                  );
                })()}

                {/* After */}
                {(() => {
                  const babAnalysis = getBABAnalysis();
                  const { after } = babAnalysis;
                  
                  return (
                    <div className="bg-white rounded-lg p-4 border border-green-200">
                      <div className="flex items-center mb-3">
                        <div className="w-8 h-8 bg-green-500 text-white rounded-full flex items-center justify-center text-sm font-bold mr-3">A</div>
                        <div>
                          <h5 className="font-semibold text-gray-900">After</h5>
                          <p className="text-xs text-gray-600">Paint the desired future</p>
                        </div>
                      </div>
                      <div className="space-y-2">
                        <div className="flex items-center justify-between">
                          <span className="text-sm font-medium text-gray-700">Score:</span>
                          <span className={`font-bold text-lg ${
                            after.score >= 8 ? 'text-green-600' : 
                            after.score >= 5 ? 'text-amber-600' : 'text-red-600'
                          }`}>{after.score.toFixed(1)}/10</span>
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-2">
                          <div 
                            className={`h-2 rounded-full ${
                              after.score >= 8 ? 'bg-green-500' : 
                              after.score >= 5 ? 'bg-amber-500' : 'bg-red-500'
                            }`}
                            style={{ width: `${after.score * 10}%` }}
                          ></div>
                        </div>
                        <p className="text-xs text-gray-600">
                          {after.description}
                        </p>
                        <div className="mt-2 text-xs">
                          <span className="text-gray-500">Strength: </span>
                          <span className={`font-medium ${
                            after.strength === 'strong' ? 'text-green-600' : 
                            after.strength === 'moderate' ? 'text-amber-600' : 'text-red-600'
                          }`}>
                            {after.strength.charAt(0).toUpperCase() + after.strength.slice(1)}
                          </span>
                          <span className="text-gray-500 ml-2">
                            ({after.criteria.length} criteria)
                          </span>
                        </div>
                      </div>
                    </div>
                  );
                })()}

                {/* Bridge */}
                {(() => {
                  const babAnalysis = getBABAnalysis();
                  const { bridge } = babAnalysis;
                  
                  return (
                    <div className="bg-white rounded-lg p-4 border border-purple-200">
                      <div className="flex items-center mb-3">
                        <div className="w-8 h-8 bg-purple-500 text-white rounded-full flex items-center justify-center text-sm font-bold mr-3">B</div>
                        <div>
                          <h5 className="font-semibold text-gray-900">Bridge</h5>
                          <p className="text-xs text-gray-600">Show the connection</p>
                        </div>
                      </div>
                      <div className="space-y-2">
                        <div className="flex items-center justify-between">
                          <span className="text-sm font-medium text-gray-700">Score:</span>
                          <span className={`font-bold text-lg ${
                            bridge.score >= 8 ? 'text-green-600' : 
                            bridge.score >= 5 ? 'text-amber-600' : 'text-red-600'
                          }`}>{bridge.score.toFixed(1)}/10</span>
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-2">
                          <div 
                            className={`h-2 rounded-full ${
                              bridge.score >= 8 ? 'bg-green-500' : 
                              bridge.score >= 5 ? 'bg-amber-500' : 'bg-red-500'
                            }`}
                            style={{ width: `${bridge.score * 10}%` }}
                          ></div>
                        </div>
                        <p className="text-xs text-gray-600">
                          {bridge.description}
                        </p>
                        <div className="mt-2 text-xs">
                          <span className="text-gray-500">Strength: </span>
                          <span className={`font-medium ${
                            bridge.strength === 'strong' ? 'text-green-600' : 
                            bridge.strength === 'moderate' ? 'text-amber-600' : 'text-red-600'
                          }`}>
                            {bridge.strength.charAt(0).toUpperCase() + bridge.strength.slice(1)}
                          </span>
                          <span className="text-gray-500 ml-2">
                            ({bridge.criteria.length} criteria)
                          </span>
                        </div>
                      </div>
                    </div>
                  );
                })()}
              </div>
            </div>
          </div>

          {/* Enhanced BAB Framework Insights */}
          <div className="mb-8">
            <div className="bg-gradient-to-br from-teal-50 via-cyan-50 to-blue-50 rounded-2xl p-6 border border-teal-200 shadow-lg">
              <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center">
                <div className="w-8 h-8 bg-gradient-to-r from-teal-500 to-cyan-500 rounded-lg flex items-center justify-center mr-3">
                  <span className="text-white text-sm font-bold">🔄</span>
                </div>
                Transformation Analysis & Flow
              </h3>
              {(() => {
                const babAnalysis = getBABAnalysis();
                const { isBalanced, transformationFlow, hasStrongContrast, totalCriteria } = babAnalysis;
                
                return (
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    {/* Transformation Flow */}
                    <div className="bg-white rounded-lg p-4 border border-teal-200">
                      <div className="flex items-center mb-2">
                        <div className={`w-3 h-3 rounded-full mr-2 ${
                          transformationFlow === 'excellent' ? 'bg-green-500' : 
                          transformationFlow === 'good' ? 'bg-blue-500' : 'bg-amber-500'
                        }`}></div>
                        <h4 className="font-semibold text-gray-900 text-sm">Transformation Flow</h4>
                      </div>
                      <p className="text-xs text-gray-600">
                        {transformationFlow === 'excellent' 
                          ? 'Excellent progression from problems through solution to outcomes'
                          : transformationFlow === 'good' 
                          ? 'Good transformation flow with minor improvements needed'
                          : 'Transformation flow needs improvement - consider restructuring'
                        }
                      </p>
                    </div>

                    {/* Contrast Analysis */}
                    <div className="bg-white rounded-lg p-4 border border-teal-200">
                      <div className="flex items-center mb-2">
                        <div className={`w-3 h-3 rounded-full mr-2 ${
                          hasStrongContrast ? 'bg-green-500' : 'bg-amber-500'
                        }`}></div>
                        <h4 className="font-semibold text-gray-900 text-sm">Before-After Contrast</h4>
                      </div>
                      <p className="text-xs text-gray-600">
                        {hasStrongContrast 
                          ? 'Strong contrast between current problems and desired outcomes'
                          : 'Contrast needs strengthening - amplify differences between before and after'
                        }
                      </p>
                    </div>

                    {/* Element Balance */}
                    <div className="bg-white rounded-lg p-4 border border-teal-200">
                      <div className="flex items-center mb-2">
                        <div className={`w-3 h-3 rounded-full mr-2 ${
                          isBalanced ? 'bg-green-500' : 'bg-amber-500'
                        }`}></div>
                        <h4 className="font-semibold text-gray-900 text-sm">Element Balance</h4>
                      </div>
                      <p className="text-xs text-gray-600">
                        {isBalanced 
                          ? 'All BAB elements are well-balanced and work together'
                          : 'Elements have varying strengths - consider balancing weaker parts'
                        }
                        <br />
                        <span className="font-medium text-teal-600">{totalCriteria} criteria analyzed</span>
                      </p>
                    </div>
                  </div>
                );
              })()}
            </div>
          </div>

          {/* Enhanced BAB Framework Recommendations */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Strengths */}
            <div className="card">
              <div className="flex items-center mb-4">
                <div className="w-8 h-8 bg-green-100 rounded-lg flex items-center justify-center mr-3">
                  <span className="text-green-600 font-bold">✓</span>
                </div>
                <h3 className="text-lg font-semibold text-gray-900">BAB Strengths</h3>
              </div>
              <div className="space-y-3">
                {(() => {
                  const babAnalysis = getBABAnalysis();
                  const { before, after, bridge } = babAnalysis;
                  const strengths = [];
                  
                  if (before.score >= 7) {
                    strengths.push({
                      element: 'Before',
                      description: 'Effectively describes the current struggles and inefficiencies your audience faces',
                      color: 'text-blue-700',
                      bgColor: 'bg-blue-50',
                      borderColor: 'border-blue-200'
                    });
                  }
                  
                  if (after.score >= 7) {
                    strengths.push({
                      element: 'After',
                      description: 'Paints a compelling picture of the benefits and transformation possible',
                      color: 'text-green-700',
                      bgColor: 'bg-green-50',
                      borderColor: 'border-green-200'
                    });
                  }
                  
                  if (bridge.score >= 7) {
                    strengths.push({
                      element: 'Bridge',
                      description: 'Clearly shows how your product/service connects the before and after states',
                      color: 'text-purple-700',
                      bgColor: 'bg-purple-50',
                      borderColor: 'border-purple-200'
                    });
                  }
                  
                  if (strengths.length === 0) {
                    return (
                      <div className="text-center py-4">
                        <p className="text-gray-500 text-sm">Focus on improving all BAB elements to build stronger transformation storytelling.</p>
                      </div>
                    );
                  }
                  
                  return strengths.map((strength, index) => (
                    <div key={index} className={`p-3 rounded-lg border ${strength.bgColor} ${strength.borderColor}`}>
                      <div className="flex items-center mb-1">
                        <span className={`font-semibold text-sm ${strength.color}`}>{strength.element}</span>
                      </div>
                      <p className="text-sm text-gray-600">{strength.description}</p>
                    </div>
                  ));
                })()}
              </div>
            </div>

            {/* Enhanced Actionable Recommendations */}
            <div className="card">
              <div className="flex items-center mb-4">
                <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center mr-3">
                  <span className="text-blue-600 font-bold">💡</span>
                </div>
                <h3 className="text-lg font-semibold text-gray-900">Actionable Recommendations</h3>
              </div>
              <div className="space-y-4">
                {(() => {
                  const babAnalysis = getBABAnalysis();
                  const { recommendations } = babAnalysis;
                  
                  if (recommendations.length === 0) {
                    return (
                      <div className="text-center py-4">
                        <p className="text-green-600 text-sm font-medium">Excellent BAB structure! All elements are well-developed.</p>
                      </div>
                    );
                  }
                  
                  return recommendations.map((rec, index) => (
                    <div key={index} className={`p-4 rounded-lg border-l-4 ${
                      rec.priority === 'high' ? 'border-red-500 bg-red-50' : 'border-amber-500 bg-amber-50'
                    }`}>
                      <div className="flex items-start justify-between mb-2">
                        <h4 className="font-semibold text-gray-900 text-sm">{rec.title}</h4>
                        <span className={`text-xs font-medium px-2 py-1 rounded-full ${
                          rec.priority === 'high' ? 'bg-red-100 text-red-700' : 'bg-amber-100 text-amber-700'
                        }`}>
                          {rec.priority === 'high' ? 'High Priority' : 'Medium Priority'}
                        </span>
                      </div>
                      <p className="text-sm text-gray-600 mb-3">{rec.description}</p>
                      <div className="space-y-1">
                        <p className="text-xs font-medium text-gray-700">Action Items:</p>
                        {rec.tips.map((tip, tipIndex) => (
                          <div key={tipIndex} className="flex items-start space-x-2">
                            <span className="text-blue-500 text-xs mt-1">•</span>
                            <span className="text-xs text-gray-600">{tip}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  ));
                })()}
              </div>
            </div>

            {/* Improvements */}
            <div className="card">
              <div className="flex items-center mb-4">
                <div className="w-8 h-8 bg-amber-100 rounded-lg flex items-center justify-center mr-3">
                  <span className="text-amber-600 font-bold">!</span>
                </div>
                <h3 className="text-lg font-semibold text-gray-900">BAB Improvements</h3>
              </div>
              <div className="space-y-3">
                {(() => {
                  const babAnalysis = getBABAnalysis();
                  const { before, after, bridge } = babAnalysis;
                  const improvements = [];
                  
                  if (before.score < 7) {
                    improvements.push({
                      element: 'Before',
                      description: 'Strengthen the "before" state with specific struggles and inefficiencies',
                      recommendation: 'Use relatable scenarios that show the pain points your audience experiences daily',
                      color: 'text-blue-700',
                      bgColor: 'bg-blue-50',
                      borderColor: 'border-blue-200'
                    });
                  }
                  
                  if (after.score < 7) {
                    improvements.push({
                      element: 'After',
                      description: 'Create a more compelling vision of the "after" state with clear benefits',
                      recommendation: 'Show specific outcomes, emotions, and improvements that result from using your solution',
                      color: 'text-green-700',
                      bgColor: 'bg-green-50',
                      borderColor: 'border-green-200'
                    });
                  }
                  
                  if (bridge.score < 7) {
                    improvements.push({
                      element: 'Bridge',
                      description: 'Strengthen the connection between before and after states',
                      recommendation: 'Clearly explain how your product/service specifically takes people from the before state to the after state',
                      color: 'text-purple-700',
                      bgColor: 'bg-purple-50',
                      borderColor: 'border-purple-200'
                    });
                  }
                  
                  if (improvements.length === 0) {
                    return (
                      <div className="text-center py-4">
                        <p className="text-gray-500 text-sm">Great job! All BAB elements are performing well.</p>
                      </div>
                    );
                  }
                  
                  return improvements.map((improvement, index) => (
                    <div key={index} className={`p-3 rounded-lg border ${improvement.bgColor} ${improvement.borderColor}`}>
                      <div className="flex items-center mb-1">
                        <span className={`font-semibold text-sm ${improvement.color}`}>{improvement.element}</span>
                      </div>
                      <p className="text-sm text-gray-600 mb-2">{improvement.description}</p>
                      <div className="bg-white rounded p-2">
                        <p className="text-xs text-gray-700 font-medium">💡 {improvement.recommendation}</p>
                      </div>
                    </div>
                  ));
                })()}
              </div>
            </div>
          </div>
        </div>
      )}

      {activeTab === 'hrr' && (
        <div className="space-y-8">
          {/* HRR Overview */}
          <div className="mb-8">
            <div className="bg-gradient-to-br from-gray-50 via-blue-50 to-indigo-50 rounded-2xl p-8 border border-gray-200 shadow-lg">
              <div className="text-center mb-6">
                <div>
                  <h2 className="text-2xl font-bold text-gray-900">HRR Analysis</h2>
                  <p className="text-gray-600">Short-Form Storytelling Arc for Your Video Ad</p>
                  {(() => {
                    const hrrAnalysis = getHRRAnalysis();
                    const { hook, relate, resolution, overall } = hrrAnalysis;
                    
                    // Determine overall status
                    const getStatusInfo = (score) => {
                      if (score >= 8) return { label: 'Acceptable', color: 'green', bgColor: 'bg-green-100', textColor: 'text-green-800' };
                      if (score >= 5) return { label: 'Needs Improvement', color: 'amber', bgColor: 'bg-amber-100', textColor: 'text-amber-800' };
                      return { label: 'Critical', color: 'red', bgColor: 'bg-red-100', textColor: 'text-red-800' };
                    };
                    
                    const statusInfo = getStatusInfo(overall);
                    
                    return (
                      <div className="mt-4 space-y-3">
                        {/* Overall HRR Score */}
                        <div className={`inline-flex items-center px-4 py-2 rounded-full ${statusInfo.bgColor} ${statusInfo.textColor}`}>
                          <div className={`w-3 h-3 rounded-full mr-2 ${
                            statusInfo.color === 'green' ? 'bg-green-500' : 
                            statusInfo.color === 'amber' ? 'bg-amber-500' : 'bg-red-500'
                          }`}></div>
                          <span className="font-semibold text-sm">
                            HRR Score: {overall}/10 - {statusInfo.label}
                          </span>
                        </div>
                        
                        {/* Individual Element Scores */}
                        <div className="flex flex-wrap justify-center gap-3 text-xs">
                          <div className="flex items-center space-x-1">
                            <span className="font-medium text-gray-600">Hook:</span>
                            <span className={`font-semibold ${
                              hook.score >= 8 ? 'text-green-600' : 
                              hook.score >= 5 ? 'text-amber-600' : 'text-red-600'
                            }`}>{hook.score.toFixed(1)}/10</span>
                          </div>
                          <div className="flex items-center space-x-1">
                            <span className="font-medium text-gray-600">Relate:</span>
                            <span className={`font-semibold ${
                              relate.score >= 8 ? 'text-green-600' : 
                              relate.score >= 5 ? 'text-amber-600' : 'text-red-600'
                            }`}>{relate.score.toFixed(1)}/10</span>
                          </div>
                          <div className="flex items-center space-x-1">
                            <span className="font-medium text-gray-600">Resolution:</span>
                            <span className={`font-semibold ${
                              resolution.score >= 8 ? 'text-green-600' : 
                              resolution.score >= 5 ? 'text-amber-600' : 'text-red-600'
                            }`}>{resolution.score.toFixed(1)}/10</span>
                          </div>
                        </div>
                      </div>
                    );
                  })()}
                </div>
              </div>

              {/* HRR Framework Breakdown */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {/* Hook */}
                {(() => {
                  const hrrAnalysis = getHRRAnalysis();
                  const { hook } = hrrAnalysis;
                  
                  return (
                    <div className="bg-white rounded-lg p-4 border border-cyan-200">
                      <div className="flex items-center mb-3">
                        <div className="w-8 h-8 bg-cyan-500 text-white rounded-full flex items-center justify-center text-sm font-bold mr-3">H</div>
                        <div>
                          <h5 className="font-semibold text-gray-900">Hook</h5>
                          <p className="text-xs text-gray-600">Grab attention (3-5s)</p>
                        </div>
                      </div>
                      <div className="space-y-2">
                        <div className="flex items-center justify-between">
                          <span className="text-sm font-medium text-gray-700">Score:</span>
                          <span className={`font-bold text-lg ${
                            hook.score >= 8 ? 'text-green-600' : 
                            hook.score >= 5 ? 'text-amber-600' : 'text-red-600'
                          }`}>{hook.score.toFixed(1)}/10</span>
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-2">
                          <div 
                            className={`h-2 rounded-full ${
                              hook.score >= 8 ? 'bg-green-500' : 
                              hook.score >= 5 ? 'bg-amber-500' : 'bg-red-500'
                            }`}
                            style={{ width: `${hook.score * 10}%` }}
                          ></div>
                        </div>
                        <p className="text-xs text-gray-600">
                          {hook.score >= 8 
                            ? 'Strong attention-grabbing opening'
                            : hook.score >= 5 
                            ? 'Decent opening hook'
                            : 'Needs stronger opening hook'
                          }
                        </p>
                      </div>
                    </div>
                  );
                })()}

                {/* Relate */}
                {(() => {
                  const hrrAnalysis = getHRRAnalysis();
                  const { relate } = hrrAnalysis;
                  
                  return (
                    <div className="bg-white rounded-lg p-4 border border-teal-200">
                      <div className="flex items-center mb-3">
                        <div className="w-8 h-8 bg-teal-500 text-white rounded-full flex items-center justify-center text-sm font-bold mr-3">R</div>
                        <div>
                          <h5 className="font-semibold text-gray-900">Relate</h5>
                          <p className="text-xs text-gray-600">Show shared struggles</p>
                        </div>
                      </div>
                      <div className="space-y-2">
                        <div className="flex items-center justify-between">
                          <span className="text-sm font-medium text-gray-700">Score:</span>
                          <span className={`font-bold text-lg ${
                            relate.score >= 8 ? 'text-green-600' : 
                            relate.score >= 5 ? 'text-amber-600' : 'text-red-600'
                          }`}>{relate.score.toFixed(1)}/10</span>
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-2">
                          <div 
                            className={`h-2 rounded-full ${
                              relate.score >= 8 ? 'bg-green-500' : 
                              relate.score >= 5 ? 'bg-amber-500' : 'bg-red-500'
                            }`}
                            style={{ width: `${relate.score * 10}%` }}
                          ></div>
                        </div>
                        <p className="text-xs text-gray-600">
                          {relate.score >= 8 
                            ? 'Strong audience connection and relatability'
                            : relate.score >= 5 
                            ? 'Some relatable elements shown'
                            : 'Needs stronger audience connection'
                          }
                        </p>
                      </div>
                    </div>
                  );
                })()}

                {/* Resolution */}
                {(() => {
                  const hrrAnalysis = getHRRAnalysis();
                  const { resolution } = hrrAnalysis;
                  
                  return (
                    <div className="bg-white rounded-lg p-4 border border-emerald-200">
                      <div className="flex items-center mb-3">
                        <div className="w-8 h-8 bg-emerald-500 text-white rounded-full flex items-center justify-center text-sm font-bold mr-3">R</div>
                        <div>
                          <h5 className="font-semibold text-gray-900">Resolution</h5>
                          <p className="text-xs text-gray-600">Deliver the payoff</p>
                        </div>
                      </div>
                      <div className="space-y-2">
                        <div className="flex items-center justify-between">
                          <span className="text-sm font-medium text-gray-700">Score:</span>
                          <span className={`font-bold text-lg ${
                            resolution.score >= 8 ? 'text-green-600' : 
                            resolution.score >= 5 ? 'text-amber-600' : 'text-red-600'
                          }`}>{resolution.score.toFixed(1)}/10</span>
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-2">
                          <div 
                            className={`h-2 rounded-full ${
                              resolution.score >= 8 ? 'bg-green-500' : 
                              resolution.score >= 5 ? 'bg-amber-500' : 'bg-red-500'
                            }`}
                            style={{ width: `${resolution.score * 10}%` }}
                          ></div>
                        </div>
                        <p className="text-xs text-gray-600">
                          {resolution.score >= 8 
                            ? 'Clear solution and satisfying payoff'
                            : resolution.score >= 5 
                            ? 'Some resolution elements shown'
                            : 'Needs stronger resolution and payoff'
                          }
                        </p>
                      </div>
                    </div>
                  );
                })()}
              </div>
            </div>
          </div>

          {/* HRR Recommendations */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Strengths */}
            <div className="card">
              <div className="flex items-center mb-4">
                <div className="w-8 h-8 bg-green-100 rounded-lg flex items-center justify-center mr-3">
                  <span className="text-green-600 font-bold">✓</span>
                </div>
                <h3 className="text-lg font-semibold text-gray-900">HRR Strengths</h3>
              </div>
              <div className="space-y-3">
                {(() => {
                  const hrrAnalysis = getHRRAnalysis();
                  const { hook, relate, resolution } = hrrAnalysis;
                  const strengths = [];
                  
                  if (hook.score >= 7) {
                    strengths.push({
                      element: 'Hook',
                      description: 'Effectively grabs attention in the first 3-5 seconds with compelling opening',
                      color: 'text-cyan-700',
                      bgColor: 'bg-cyan-50',
                      borderColor: 'border-cyan-200'
                    });
                  }
                  
                  if (relate.score >= 7) {
                    strengths.push({
                      element: 'Relate',
                      description: 'Creates strong audience connection through familiar scenarios and shared struggles',
                      color: 'text-teal-700',
                      bgColor: 'bg-teal-50',
                      borderColor: 'border-teal-200'
                    });
                  }
                  
                  if (resolution.score >= 7) {
                    strengths.push({
                      element: 'Resolution',
                      description: 'Delivers a clear solution and satisfying payoff that completes the story',
                      color: 'text-emerald-700',
                      bgColor: 'bg-emerald-50',
                      borderColor: 'border-emerald-200'
                    });
                  }
                  
                  if (strengths.length === 0) {
                    return (
                      <div className="text-center py-4">
                        <p className="text-gray-500 text-sm">Focus on improving all HRR elements to build stronger short-form storytelling.</p>
                      </div>
                    );
                  }
                  
                  return strengths.map((strength, index) => (
                    <div key={index} className={`p-3 rounded-lg border ${strength.bgColor} ${strength.borderColor}`}>
                      <div className="flex items-center mb-1">
                        <span className={`font-semibold text-sm ${strength.color}`}>{strength.element}</span>
                      </div>
                      <p className="text-sm text-gray-600">{strength.description}</p>
                    </div>
                  ));
                })()}
              </div>
            </div>

            {/* Improvements */}
            <div className="card">
              <div className="flex items-center mb-4">
                <div className="w-8 h-8 bg-amber-100 rounded-lg flex items-center justify-center mr-3">
                  <span className="text-amber-600 font-bold">!</span>
                </div>
                <h3 className="text-lg font-semibold text-gray-900">HRR Improvements</h3>
              </div>
              <div className="space-y-3">
                {(() => {
                  const hrrAnalysis = getHRRAnalysis();
                  const { hook, relate, resolution } = hrrAnalysis;
                  const improvements = [];
                  
                  if (hook.score < 7) {
                    improvements.push({
                      element: 'Hook',
                      description: 'Strengthen the opening hook to grab attention in the first 3-5 seconds',
                      recommendation: 'Start with a surprising fact, bold statement, or compelling question that immediately captures attention',
                      color: 'text-cyan-700',
                      bgColor: 'bg-cyan-50',
                      borderColor: 'border-cyan-200'
                    });
                  }
                  
                  if (relate.score < 7) {
                    improvements.push({
                      element: 'Relate',
                      description: 'Build stronger audience connection through relatable scenarios and shared experiences',
                      recommendation: 'Show common struggles, everyday situations, or universal experiences that your audience can identify with',
                      color: 'text-teal-700',
                      bgColor: 'bg-teal-50',
                      borderColor: 'border-teal-200'
                    });
                  }
                  
                  if (resolution.score < 7) {
                    improvements.push({
                      element: 'Resolution',
                      description: 'Create a more satisfying resolution with clear solution and payoff',
                      recommendation: 'Provide a specific answer, solution, or outcome that directly addresses the hook and relates to the audience\'s needs',
                      color: 'text-emerald-700',
                      bgColor: 'bg-emerald-50',
                      borderColor: 'border-emerald-200'
                    });
                  }
                  
                  if (improvements.length === 0) {
                    return (
                      <div className="text-center py-4">
                        <p className="text-gray-500 text-sm">Great job! All HRR elements are performing well.</p>
                      </div>
                    );
                  }
                  
                  return improvements.map((improvement, index) => (
                    <div key={index} className={`p-3 rounded-lg border ${improvement.bgColor} ${improvement.borderColor}`}>
                      <div className="flex items-center mb-1">
                        <span className={`font-semibold text-sm ${improvement.color}`}>{improvement.element}</span>
                      </div>
                      <p className="text-sm text-gray-600 mb-2">{improvement.description}</p>
                      <div className="bg-white rounded p-2">
                        <p className="text-xs text-gray-700 font-medium">💡 {improvement.recommendation}</p>
                      </div>
                    </div>
                  ));
                })()}
              </div>
            </div>
          </div>
        </div>
      )}

      {activeTab === '3act' && (
        <div className="space-y-8">
          {/* 3-Act Structure Overview */}
          <div className="mb-8">
            <div className="bg-gradient-to-br from-gray-50 via-blue-50 to-indigo-50 rounded-2xl p-8 border border-gray-200 shadow-lg">
              <div className="text-center mb-6">
                <div>
                  <h2 className="text-2xl font-bold text-gray-900">3-Act Structure Analysis</h2>
                  <p className="text-gray-600">Classic Film Narrative Model for Your Video Ad</p>
                  {(() => {
                    const act3Analysis = get3ActAnalysis();
                    const { setup, confrontation, resolution, overall } = act3Analysis;
                    
                    // Determine overall status
                    const getStatusInfo = (score) => {
                      if (score >= 8) return { label: 'Acceptable', color: 'green', bgColor: 'bg-green-100', textColor: 'text-green-800' };
                      if (score >= 5) return { label: 'Needs Improvement', color: 'amber', bgColor: 'bg-amber-100', textColor: 'text-amber-800' };
                      return { label: 'Critical', color: 'red', bgColor: 'bg-red-100', textColor: 'text-red-800' };
                    };
                    
                    const statusInfo = getStatusInfo(overall);
                    
                    return (
                      <div className="mt-4 space-y-3">
                        {/* Overall 3-Act Score */}
                        <div className={`inline-flex items-center px-4 py-2 rounded-full ${statusInfo.bgColor} ${statusInfo.textColor}`}>
                          <div className={`w-3 h-3 rounded-full mr-2 ${
                            statusInfo.color === 'green' ? 'bg-green-500' : 
                            statusInfo.color === 'amber' ? 'bg-amber-500' : 'bg-red-500'
                          }`}></div>
                          <span className="font-semibold text-sm">
                            3-Act Score: {overall}/10 - {statusInfo.label}
                          </span>
                        </div>
                        
                        {/* Individual Act Scores */}
                        <div className="flex flex-wrap justify-center gap-3 text-xs">
                          <div className="flex items-center space-x-1">
                            <span className="font-medium text-gray-600">Act 1 (Setup):</span>
                            <span className={`font-semibold ${
                              setup.score >= 8 ? 'text-green-600' : 
                              setup.score >= 5 ? 'text-amber-600' : 'text-red-600'
                            }`}>{setup.score.toFixed(1)}/10</span>
                          </div>
                          <div className="flex items-center space-x-1">
                            <span className="font-medium text-gray-600">Act 2 (Confrontation):</span>
                            <span className={`font-semibold ${
                              confrontation.score >= 8 ? 'text-green-600' : 
                              confrontation.score >= 5 ? 'text-amber-600' : 'text-red-600'
                            }`}>{confrontation.score.toFixed(1)}/10</span>
                          </div>
                          <div className="flex items-center space-x-1">
                            <span className="font-medium text-gray-600">Act 3 (Resolution):</span>
                            <span className={`font-semibold ${
                              resolution.score >= 8 ? 'text-green-600' : 
                              resolution.score >= 5 ? 'text-amber-600' : 'text-red-600'
                            }`}>{resolution.score.toFixed(1)}/10</span>
                          </div>
                        </div>
                      </div>
                    );
                  })()}
                </div>
              </div>

              {/* 3-Act Structure Breakdown */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {/* Act 1 - Setup */}
                {(() => {
                  const act3Analysis = get3ActAnalysis();
                  const { setup } = act3Analysis;
                  
                  return (
                    <div className="bg-white rounded-lg p-4 border border-violet-200">
                      <div className="flex items-center mb-3">
                        <div className="w-8 h-8 bg-violet-500 text-white rounded-full flex items-center justify-center text-sm font-bold mr-3">1</div>
                        <div>
                          <h5 className="font-semibold text-gray-900">Act 1: Setup</h5>
                          <p className="text-xs text-gray-600">Introduce character/context</p>
                        </div>
                      </div>
                      <div className="space-y-2">
                        <div className="flex items-center justify-between">
                          <span className="text-sm font-medium text-gray-700">Score:</span>
                          <span className={`font-bold text-lg ${
                            setup.score >= 8 ? 'text-green-600' : 
                            setup.score >= 5 ? 'text-amber-600' : 'text-red-600'
                          }`}>{setup.score.toFixed(1)}/10</span>
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-2">
                          <div 
                            className={`h-2 rounded-full ${
                              setup.score >= 8 ? 'bg-green-500' : 
                              setup.score >= 5 ? 'bg-amber-500' : 'bg-red-500'
                            }`}
                            style={{ width: `${setup.score * 10}%` }}
                          ></div>
                        </div>
                        <p className="text-xs text-gray-600">
                          {setup.description}
                        </p>
                        <div className="mt-2 text-xs">
                          <span className="text-gray-500">Strength: </span>
                          <span className={`font-medium ${
                            setup.strength === 'strong' ? 'text-green-600' : 
                            setup.strength === 'moderate' ? 'text-amber-600' : 'text-red-600'
                          }`}>
                            {setup.strength.charAt(0).toUpperCase() + setup.strength.slice(1)}
                          </span>
                          <span className="text-gray-500 ml-2">
                            ({setup.criteria.length} criteria)
                          </span>
                        </div>
                      </div>
                    </div>
                  );
                })()}

                {/* Act 2 - Confrontation */}
                {(() => {
                  const act3Analysis = get3ActAnalysis();
                  const { confrontation } = act3Analysis;
                  
                  return (
                    <div className="bg-white rounded-lg p-4 border border-purple-200">
                      <div className="flex items-center mb-3">
                        <div className="w-8 h-8 bg-purple-500 text-white rounded-full flex items-center justify-center text-sm font-bold mr-3">2</div>
                        <div>
                          <h5 className="font-semibold text-gray-900">Act 2: Confrontation</h5>
                          <p className="text-xs text-gray-600">Show problem/struggle</p>
                        </div>
                      </div>
                      <div className="space-y-2">
                        <div className="flex items-center justify-between">
                          <span className="text-sm font-medium text-gray-700">Score:</span>
                          <span className={`font-bold text-lg ${
                            confrontation.score >= 8 ? 'text-green-600' : 
                            confrontation.score >= 5 ? 'text-amber-600' : 'text-red-600'
                          }`}>{confrontation.score.toFixed(1)}/10</span>
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-2">
                          <div 
                            className={`h-2 rounded-full ${
                              confrontation.score >= 8 ? 'bg-green-500' : 
                              confrontation.score >= 5 ? 'bg-amber-500' : 'bg-red-500'
                            }`}
                            style={{ width: `${confrontation.score * 10}%` }}
                          ></div>
                        </div>
                        <p className="text-xs text-gray-600">
                          {confrontation.description}
                        </p>
                        <div className="mt-2 text-xs">
                          <span className="text-gray-500">Strength: </span>
                          <span className={`font-medium ${
                            confrontation.strength === 'strong' ? 'text-green-600' : 
                            confrontation.strength === 'moderate' ? 'text-amber-600' : 'text-red-600'
                          }`}>
                            {confrontation.strength.charAt(0).toUpperCase() + confrontation.strength.slice(1)}
                          </span>
                          <span className="text-gray-500 ml-2">
                            ({confrontation.criteria.length} criteria)
                          </span>
                        </div>
                      </div>
                    </div>
                  );
                })()}

                {/* Act 3 - Resolution */}
                {(() => {
                  const act3Analysis = get3ActAnalysis();
                  const { resolution } = act3Analysis;
                  
                  return (
                    <div className="bg-white rounded-lg p-4 border border-fuchsia-200">
                      <div className="flex items-center mb-3">
                        <div className="w-8 h-8 bg-fuchsia-500 text-white rounded-full flex items-center justify-center text-sm font-bold mr-3">3</div>
                        <div>
                          <h5 className="font-semibold text-gray-900">Act 3: Resolution</h5>
                          <p className="text-xs text-gray-600">Product/service as enabler</p>
                        </div>
                      </div>
                      <div className="space-y-2">
                        <div className="flex items-center justify-between">
                          <span className="text-sm font-medium text-gray-700">Score:</span>
                          <span className={`font-bold text-lg ${
                            resolution.score >= 8 ? 'text-green-600' : 
                            resolution.score >= 5 ? 'text-amber-600' : 'text-red-600'
                          }`}>{resolution.score.toFixed(1)}/10</span>
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-2">
                          <div 
                            className={`h-2 rounded-full ${
                              resolution.score >= 8 ? 'bg-green-500' : 
                              resolution.score >= 5 ? 'bg-amber-500' : 'bg-red-500'
                            }`}
                            style={{ width: `${resolution.score * 10}%` }}
                          ></div>
                        </div>
                        <p className="text-xs text-gray-600">
                          {resolution.description}
                        </p>
                        <div className="mt-2 text-xs">
                          <span className="text-gray-500">Strength: </span>
                          <span className={`font-medium ${
                            resolution.strength === 'strong' ? 'text-green-600' : 
                            resolution.strength === 'moderate' ? 'text-amber-600' : 'text-red-600'
                          }`}>
                            {resolution.strength.charAt(0).toUpperCase() + resolution.strength.slice(1)}
                          </span>
                          <span className="text-gray-500 ml-2">
                            ({resolution.criteria.length} criteria)
                          </span>
                        </div>
                      </div>
                    </div>
                  );
                })()}
              </div>
            </div>
          </div>

          {/* Enhanced 3-Act Structure Insights */}
          <div className="mb-8">
            <div className="bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 rounded-2xl p-6 border border-blue-200 shadow-lg">
              <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center">
                <div className="w-8 h-8 bg-gradient-to-r from-blue-500 to-indigo-500 rounded-lg flex items-center justify-center mr-3">
                  <span className="text-white text-sm font-bold">📊</span>
                </div>
                Structure Analysis & Flow
              </h3>
              {(() => {
                const act3Analysis = get3ActAnalysis();
                const { balance, flow, totalCriteria } = act3Analysis;
                
                return (
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    {/* Balance Analysis */}
                    <div className="bg-white rounded-lg p-4 border border-blue-200">
                      <div className="flex items-center mb-2">
                        <div className={`w-3 h-3 rounded-full mr-2 ${
                          balance ? 'bg-green-500' : 'bg-amber-500'
                        }`}></div>
                        <h4 className="font-semibold text-gray-900 text-sm">Act Balance</h4>
                      </div>
                      <p className="text-xs text-gray-600">
                        {balance 
                          ? 'All acts are well-balanced and flow together smoothly'
                          : 'Acts have varying strengths - consider balancing the weaker elements'
                        }
                      </p>
                    </div>

                    {/* Narrative Flow */}
                    <div className="bg-white rounded-lg p-4 border border-blue-200">
                      <div className="flex items-center mb-2">
                        <div className={`w-3 h-3 rounded-full mr-2 ${
                          flow === 'excellent' ? 'bg-green-500' : 
                          flow === 'good' ? 'bg-blue-500' : 'bg-amber-500'
                        }`}></div>
                        <h4 className="font-semibold text-gray-900 text-sm">Narrative Flow</h4>
                      </div>
                      <p className="text-xs text-gray-600">
                        {flow === 'excellent' 
                          ? 'Excellent progression from setup through conflict to resolution'
                          : flow === 'good' 
                          ? 'Good narrative progression with minor improvements needed'
                          : 'Narrative flow needs improvement - consider restructuring'
                        }
                      </p>
                    </div>

                    {/* Criteria Coverage */}
                    <div className="bg-white rounded-lg p-4 border border-blue-200">
                      <div className="flex items-center mb-2">
                        <div className="w-3 h-3 bg-blue-500 rounded-full mr-2"></div>
                        <h4 className="font-semibold text-gray-900 text-sm">Coverage</h4>
                      </div>
                      <p className="text-xs text-gray-600">
                        {totalCriteria >= 10 
                          ? 'Comprehensive coverage of 3-Act elements'
                          : totalCriteria >= 6 
                          ? 'Good coverage with room for more elements'
                          : 'Limited coverage - consider adding more 3-Act elements'
                        }
                        <br />
                        <span className="font-medium text-blue-600">{totalCriteria} criteria analyzed</span>
                      </p>
                    </div>
                  </div>
                );
              })()}
            </div>
          </div>

          {/* Enhanced 3-Act Structure Recommendations */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Strengths */}
            <div className="card">
              <div className="flex items-center mb-4">
                <div className="w-8 h-8 bg-green-100 rounded-lg flex items-center justify-center mr-3">
                  <span className="text-green-600 font-bold">✓</span>
                </div>
                <h3 className="text-lg font-semibold text-gray-900">3-Act Structure Strengths</h3>
              </div>
              <div className="space-y-3">
                {(() => {
                  const act3Analysis = get3ActAnalysis();
                  const { setup, confrontation, resolution } = act3Analysis;
                  const strengths = [];
                  
                  if (setup.score >= 7) {
                    strengths.push({
                      element: 'Act 1: Setup',
                      description: 'Effectively introduces characters and establishes the context for the story',
                      color: 'text-violet-700',
                      bgColor: 'bg-violet-50',
                      borderColor: 'border-violet-200',
                      score: setup.score,
                      strength: setup.strength
                    });
                  }
                  
                  if (confrontation.score >= 7) {
                    strengths.push({
                      element: 'Act 2: Confrontation',
                      description: 'Clearly presents the problem, struggle, or obstacle that drives the narrative',
                      color: 'text-purple-700',
                      bgColor: 'bg-purple-50',
                      borderColor: 'border-purple-200',
                      score: confrontation.score,
                      strength: confrontation.strength
                    });
                  }
                  
                  if (resolution.score >= 7) {
                    strengths.push({
                      element: 'Act 3: Resolution',
                      description: 'Successfully positions your product/service as the enabler of the solution',
                      color: 'text-fuchsia-700',
                      bgColor: 'bg-fuchsia-50',
                      borderColor: 'border-fuchsia-200',
                      score: resolution.score,
                      strength: resolution.strength
                    });
                  }
                  
                  if (strengths.length === 0) {
                    return (
                      <div className="text-center py-4">
                        <p className="text-gray-500 text-sm">Focus on improving all 3-Act elements to build stronger narrative structure.</p>
                      </div>
                    );
                  }
                  
                  return strengths.map((strength, index) => (
                    <div key={index} className={`p-3 rounded-lg border ${strength.bgColor} ${strength.borderColor}`}>
                      <div className="flex items-center mb-1">
                        <span className={`font-semibold text-sm ${strength.color}`}>{strength.element}</span>
                      </div>
                      <p className="text-sm text-gray-600">{strength.description}</p>
                    </div>
                  ));
                })()}
              </div>
            </div>

            {/* Enhanced Actionable Recommendations */}
            <div className="card">
              <div className="flex items-center mb-4">
                <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center mr-3">
                  <span className="text-blue-600 font-bold">💡</span>
                </div>
                <h3 className="text-lg font-semibold text-gray-900">Actionable Recommendations</h3>
              </div>
              <div className="space-y-4">
                {(() => {
                  const act3Analysis = get3ActAnalysis();
                  const { recommendations } = act3Analysis;
                  
                  if (recommendations.length === 0) {
                    return (
                      <div className="text-center py-4">
                        <p className="text-green-600 text-sm font-medium">Excellent 3-Act structure! All elements are well-developed.</p>
                      </div>
                    );
                  }
                  
                  return recommendations.map((rec, index) => (
                    <div key={index} className={`p-4 rounded-lg border-l-4 ${
                      rec.priority === 'high' ? 'border-red-500 bg-red-50' : 'border-amber-500 bg-amber-50'
                    }`}>
                      <div className="flex items-start justify-between mb-2">
                        <h4 className="font-semibold text-gray-900 text-sm">{rec.title}</h4>
                        <span className={`text-xs font-medium px-2 py-1 rounded-full ${
                          rec.priority === 'high' ? 'bg-red-100 text-red-700' : 'bg-amber-100 text-amber-700'
                        }`}>
                          {rec.priority === 'high' ? 'High Priority' : 'Medium Priority'}
                        </span>
                      </div>
                      <p className="text-sm text-gray-600 mb-3">{rec.description}</p>
                      <div className="space-y-1">
                        <p className="text-xs font-medium text-gray-700">Action Items:</p>
                        {rec.tips.map((tip, tipIndex) => (
                          <div key={tipIndex} className="flex items-start space-x-2">
                            <span className="text-blue-500 text-xs mt-1">•</span>
                            <span className="text-xs text-gray-600">{tip}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  ));
                })()}
              </div>
            </div>

            {/* Improvements */}
            <div className="card">
              <div className="flex items-center mb-4">
                <div className="w-8 h-8 bg-amber-100 rounded-lg flex items-center justify-center mr-3">
                  <span className="text-amber-600 font-bold">!</span>
                </div>
                <h3 className="text-lg font-semibold text-gray-900">3-Act Structure Improvements</h3>
              </div>
              <div className="space-y-3">
                {(() => {
                  const act3Analysis = get3ActAnalysis();
                  const { setup, confrontation, resolution } = act3Analysis;
                  const improvements = [];
                  
                  if (setup.score < 7) {
                    improvements.push({
                      element: 'Act 1: Setup',
                      description: 'Strengthen the character and context introduction in the opening',
                      recommendation: 'Clearly establish who the character is, what their world looks like, and what their normal situation is before the problem arises',
                      color: 'text-violet-700',
                      bgColor: 'bg-violet-50',
                      borderColor: 'border-violet-200'
                    });
                  }
                  
                  if (confrontation.score < 7) {
                    improvements.push({
                      element: 'Act 2: Confrontation',
                      description: 'Build stronger conflict and confrontation elements',
                      recommendation: 'Show the specific problem, obstacle, or struggle that creates tension and drives the need for a solution',
                      color: 'text-purple-700',
                      bgColor: 'bg-purple-50',
                      borderColor: 'border-purple-200'
                    });
                  }
                  
                  if (resolution.score < 7) {
                    improvements.push({
                      element: 'Act 3: Resolution',
                      description: 'Strengthen the resolution with clear product/service positioning',
                      recommendation: 'Show how your product/service specifically enables the character to overcome the problem and achieve their goal',
                      color: 'text-fuchsia-700',
                      bgColor: 'bg-fuchsia-50',
                      borderColor: 'border-fuchsia-200'
                    });
                  }
                  
                  if (improvements.length === 0) {
                    return (
                      <div className="text-center py-4">
                        <p className="text-gray-500 text-sm">Great job! All 3-Act elements are performing well.</p>
                      </div>
                    );
                  }
                  
                  return improvements.map((improvement, index) => (
                    <div key={index} className={`p-3 rounded-lg border ${improvement.bgColor} ${improvement.borderColor}`}>
                      <div className="flex items-center mb-1">
                        <span className={`font-semibold text-sm ${improvement.color}`}>{improvement.element}</span>
                      </div>
                      <p className="text-sm text-gray-600 mb-2">{improvement.description}</p>
                      <div className="bg-white rounded p-2">
                        <p className="text-xs text-gray-700 font-medium">💡 {improvement.recommendation}</p>
                      </div>
                    </div>
                  ));
                })()}
              </div>
            </div>
          </div>
        </div>
      )}

      {activeTab === 'hero' && (
        <div className="space-y-8">
          {/* Hero's Journey Overview */}
          <div className="mb-8">
            <div className="bg-gradient-to-br from-gray-50 via-blue-50 to-indigo-50 rounded-2xl p-8 border border-gray-200 shadow-lg">
              <div className="text-center mb-6">
                <div>
                  <h2 className="text-2xl font-bold text-gray-900">Hero's Journey Analysis</h2>
                  <p className="text-gray-600">Brand Storytelling Structure for Your Video Ad</p>
                  {(() => {
                    const heroAnalysis = getHeroJourneyAnalysis();
                    const { hero, callToAdventure, mentor, transformation, returnReward, overall } = heroAnalysis;
                    
                    // Determine overall status
                    const getStatusInfo = (score) => {
                      if (score >= 8) return { label: 'Acceptable', color: 'green', bgColor: 'bg-green-100', textColor: 'text-green-800' };
                      if (score >= 5) return { label: 'Needs Improvement', color: 'amber', bgColor: 'bg-amber-100', textColor: 'text-amber-800' };
                      return { label: 'Critical', color: 'red', bgColor: 'bg-red-100', textColor: 'text-red-800' };
                    };
                    
                    const statusInfo = getStatusInfo(overall);
                    
                    return (
                      <div className="mt-4 space-y-3">
                        {/* Overall Hero's Journey Score */}
                        <div className={`inline-flex items-center px-4 py-2 rounded-full ${statusInfo.bgColor} ${statusInfo.textColor}`}>
                          <div className={`w-3 h-3 rounded-full mr-2 ${
                            statusInfo.color === 'green' ? 'bg-green-500' : 
                            statusInfo.color === 'amber' ? 'bg-amber-500' : 'bg-red-500'
                          }`}></div>
                          <span className="font-semibold text-sm">
                            Hero's Journey Score: {overall}/10 - {statusInfo.label}
                          </span>
                        </div>
                        
                        {/* Individual Element Scores */}
                        <div className="flex flex-wrap justify-center gap-2 text-xs">
                          <div className="flex items-center space-x-1">
                            <span className="font-medium text-gray-600">Hero:</span>
                            <span className={`font-semibold ${
                              hero.score >= 8 ? 'text-green-600' : 
                              hero.score >= 5 ? 'text-amber-600' : 'text-red-600'
                            }`}>{hero.score.toFixed(1)}/10</span>
                          </div>
                          <div className="flex items-center space-x-1">
                            <span className="font-medium text-gray-600">Call:</span>
                            <span className={`font-semibold ${
                              callToAdventure.score >= 8 ? 'text-green-600' : 
                              callToAdventure.score >= 5 ? 'text-amber-600' : 'text-red-600'
                            }`}>{callToAdventure.score.toFixed(1)}/10</span>
                          </div>
                          <div className="flex items-center space-x-1">
                            <span className="font-medium text-gray-600">Mentor:</span>
                            <span className={`font-semibold ${
                              mentor.score >= 8 ? 'text-green-600' : 
                              mentor.score >= 5 ? 'text-amber-600' : 'text-red-600'
                            }`}>{mentor.score.toFixed(1)}/10</span>
                          </div>
                          <div className="flex items-center space-x-1">
                            <span className="font-medium text-gray-600">Transform:</span>
                            <span className={`font-semibold ${
                              transformation.score >= 8 ? 'text-green-600' : 
                              transformation.score >= 5 ? 'text-amber-600' : 'text-red-600'
                            }`}>{transformation.score.toFixed(1)}/10</span>
                          </div>
                          <div className="flex items-center space-x-1">
                            <span className="font-medium text-gray-600">Return:</span>
                            <span className={`font-semibold ${
                              returnReward.score >= 8 ? 'text-green-600' : 
                              returnReward.score >= 5 ? 'text-amber-600' : 'text-red-600'
                            }`}>{returnReward.score.toFixed(1)}/10</span>
                          </div>
                        </div>
                      </div>
                    );
                  })()}
                </div>
              </div>

              {/* Hero's Journey Framework Breakdown */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
                {/* Hero */}
                {(() => {
                  const heroAnalysis = getHeroJourneyAnalysis();
                  const { hero } = heroAnalysis;
                  
                  return (
                    <div className="bg-white rounded-lg p-4 border border-amber-200">
                      <div className="flex items-center mb-3">
                        <div className="w-8 h-8 bg-amber-500 text-white rounded-full flex items-center justify-center text-sm font-bold mr-3">👤</div>
                        <div>
                          <h5 className="font-semibold text-gray-900">Hero</h5>
                          <p className="text-xs text-gray-600">Customer as hero</p>
                        </div>
                      </div>
                      <div className="space-y-2">
                        <div className="flex items-center justify-between">
                          <span className="text-sm font-medium text-gray-700">Score:</span>
                          <span className={`font-bold text-lg ${
                            hero.score >= 8 ? 'text-green-600' : 
                            hero.score >= 5 ? 'text-amber-600' : 'text-red-600'
                          }`}>{hero.score.toFixed(1)}/10</span>
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-2">
                          <div 
                            className={`h-2 rounded-full ${
                              hero.score >= 8 ? 'bg-green-500' : 
                              hero.score >= 5 ? 'bg-amber-500' : 'bg-red-500'
                            }`}
                            style={{ width: `${hero.score * 10}%` }}
                          ></div>
                        </div>
                        <p className="text-xs text-gray-600">
                          {hero.score >= 8 
                            ? 'Customer clearly positioned as hero'
                            : hero.score >= 5 
                            ? 'Some hero elements present'
                            : 'Needs stronger customer-as-hero focus'
                          }
                        </p>
                      </div>
                    </div>
                  );
                })()}

                {/* Call to Adventure */}
                {(() => {
                  const heroAnalysis = getHeroJourneyAnalysis();
                  const { callToAdventure } = heroAnalysis;
                  
                  return (
                    <div className="bg-white rounded-lg p-4 border border-yellow-200">
                      <div className="flex items-center mb-3">
                        <div className="w-8 h-8 bg-yellow-500 text-white rounded-full flex items-center justify-center text-sm font-bold mr-3">📢</div>
                        <div>
                          <h5 className="font-semibold text-gray-900">Call to Adventure</h5>
                          <p className="text-xs text-gray-600">Challenge they face</p>
                        </div>
                      </div>
                      <div className="space-y-2">
                        <div className="flex items-center justify-between">
                          <span className="text-sm font-medium text-gray-700">Score:</span>
                          <span className={`font-bold text-lg ${
                            callToAdventure.score >= 8 ? 'text-green-600' : 
                            callToAdventure.score >= 5 ? 'text-amber-600' : 'text-red-600'
                          }`}>{callToAdventure.score.toFixed(1)}/10</span>
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-2">
                          <div 
                            className={`h-2 rounded-full ${
                              callToAdventure.score >= 8 ? 'bg-green-500' : 
                              callToAdventure.score >= 5 ? 'bg-amber-500' : 'bg-red-500'
                            }`}
                            style={{ width: `${callToAdventure.score * 10}%` }}
                          ></div>
                        </div>
                        <p className="text-xs text-gray-600">
                          {callToAdventure.score >= 8 
                            ? 'Clear challenge or need presented'
                            : callToAdventure.score >= 5 
                            ? 'Some challenge elements shown'
                            : 'Needs stronger call to adventure'
                          }
                        </p>
                      </div>
                    </div>
                  );
                })()}

                {/* Mentor/Guide */}
                {(() => {
                  const heroAnalysis = getHeroJourneyAnalysis();
                  const { mentor } = heroAnalysis;
                  
                  return (
                    <div className="bg-white rounded-lg p-4 border border-orange-200">
                      <div className="flex items-center mb-3">
                        <div className="w-8 h-8 bg-orange-500 text-white rounded-full flex items-center justify-center text-sm font-bold mr-3">🧙</div>
                        <div>
                          <h5 className="font-semibold text-gray-900">Mentor/Guide</h5>
                          <p className="text-xs text-gray-600">Brand as helper</p>
                        </div>
                      </div>
                      <div className="space-y-2">
                        <div className="flex items-center justify-between">
                          <span className="text-sm font-medium text-gray-700">Score:</span>
                          <span className={`font-bold text-lg ${
                            mentor.score >= 8 ? 'text-green-600' : 
                            mentor.score >= 5 ? 'text-amber-600' : 'text-red-600'
                          }`}>{mentor.score.toFixed(1)}/10</span>
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-2">
                          <div 
                            className={`h-2 rounded-full ${
                              mentor.score >= 8 ? 'bg-green-500' : 
                              mentor.score >= 5 ? 'bg-amber-500' : 'bg-red-500'
                            }`}
                            style={{ width: `${mentor.score * 10}%` }}
                          ></div>
                        </div>
                        <p className="text-xs text-gray-600">
                          {mentor.score >= 8 
                            ? 'Brand clearly positioned as guide'
                            : mentor.score >= 5 
                            ? 'Some mentor elements present'
                            : 'Needs stronger brand-as-mentor positioning'
                          }
                        </p>
                      </div>
                    </div>
                  );
                })()}

                {/* Transformation */}
                {(() => {
                  const heroAnalysis = getHeroJourneyAnalysis();
                  const { transformation } = heroAnalysis;
                  
                  return (
                    <div className="bg-white rounded-lg p-4 border border-red-200">
                      <div className="flex items-center mb-3">
                        <div className="w-8 h-8 bg-red-500 text-white rounded-full flex items-center justify-center text-sm font-bold mr-3">✨</div>
                        <div>
                          <h5 className="font-semibold text-gray-900">Transformation</h5>
                          <p className="text-xs text-gray-600">Overcome challenge</p>
                        </div>
                      </div>
                      <div className="space-y-2">
                        <div className="flex items-center justify-between">
                          <span className="text-sm font-medium text-gray-700">Score:</span>
                          <span className={`font-bold text-lg ${
                            transformation.score >= 8 ? 'text-green-600' : 
                            transformation.score >= 5 ? 'text-amber-600' : 'text-red-600'
                          }`}>{transformation.score.toFixed(1)}/10</span>
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-2">
                          <div 
                            className={`h-2 rounded-full ${
                              transformation.score >= 8 ? 'bg-green-500' : 
                              transformation.score >= 5 ? 'bg-amber-500' : 'bg-red-500'
                            }`}
                            style={{ width: `${transformation.score * 10}%` }}
                          ></div>
                        </div>
                        <p className="text-xs text-gray-600">
                          {transformation.score >= 8 
                            ? 'Clear transformation shown'
                            : transformation.score >= 5 
                            ? 'Some transformation elements'
                            : 'Needs stronger transformation story'
                          }
                        </p>
                      </div>
                    </div>
                  );
                })()}

                {/* Return with Reward */}
                {(() => {
                  const heroAnalysis = getHeroJourneyAnalysis();
                  const { returnReward } = heroAnalysis;
                  
                  return (
                    <div className="bg-white rounded-lg p-4 border border-green-200">
                      <div className="flex items-center mb-3">
                        <div className="w-8 h-8 bg-green-500 text-white rounded-full flex items-center justify-center text-sm font-bold mr-3">🏆</div>
                        <div>
                          <h5 className="font-semibold text-gray-900">Return with Reward</h5>
                          <p className="text-xs text-gray-600">Better life shown</p>
                        </div>
                      </div>
                      <div className="space-y-2">
                        <div className="flex items-center justify-between">
                          <span className="text-sm font-medium text-gray-700">Score:</span>
                          <span className={`font-bold text-lg ${
                            returnReward.score >= 8 ? 'text-green-600' : 
                            returnReward.score >= 5 ? 'text-amber-600' : 'text-red-600'
                          }`}>{returnReward.score.toFixed(1)}/10</span>
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-2">
                          <div 
                            className={`h-2 rounded-full ${
                              returnReward.score >= 8 ? 'bg-green-500' : 
                              returnReward.score >= 5 ? 'bg-amber-500' : 'bg-red-500'
                            }`}
                            style={{ width: `${returnReward.score * 10}%` }}
                          ></div>
                        </div>
                        <p className="text-xs text-gray-600">
                          {returnReward.score >= 8 
                            ? 'Clear reward and better life shown'
                            : returnReward.score >= 5 
                            ? 'Some reward elements present'
                            : 'Needs stronger return with reward'
                          }
                        </p>
                      </div>
                    </div>
                  );
                })()}
              </div>
            </div>
          </div>

          {/* Hero's Journey Recommendations */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Strengths */}
            <div className="card">
              <div className="flex items-center mb-4">
                <div className="w-8 h-8 bg-green-100 rounded-lg flex items-center justify-center mr-3">
                  <span className="text-green-600 font-bold">✓</span>
                </div>
                <h3 className="text-lg font-semibold text-gray-900">Hero's Journey Strengths</h3>
              </div>
              <div className="space-y-3">
                {(() => {
                  const heroAnalysis = getHeroJourneyAnalysis();
                  const { hero, callToAdventure, mentor, transformation, returnReward } = heroAnalysis;
                  const strengths = [];
                  
                  if (hero.score >= 7) {
                    strengths.push({
                      element: 'Hero',
                      description: 'Effectively positions the customer as the hero of the story, not the brand',
                      color: 'text-amber-700',
                      bgColor: 'bg-amber-50',
                      borderColor: 'border-amber-200'
                    });
                  }
                  
                  if (callToAdventure.score >= 7) {
                    strengths.push({
                      element: 'Call to Adventure',
                      description: 'Clearly presents the challenge or need that calls the hero to action',
                      color: 'text-yellow-700',
                      bgColor: 'bg-yellow-50',
                      borderColor: 'border-yellow-200'
                    });
                  }
                  
                  if (mentor.score >= 7) {
                    strengths.push({
                      element: 'Mentor/Guide',
                      description: 'Successfully positions the brand as the guide or helper in the hero\'s journey',
                      color: 'text-orange-700',
                      bgColor: 'bg-orange-50',
                      borderColor: 'border-orange-200'
                    });
                  }
                  
                  if (transformation.score >= 7) {
                    strengths.push({
                      element: 'Transformation',
                      description: 'Shows how the customer overcomes the challenge with the product/service',
                      color: 'text-red-700',
                      bgColor: 'bg-red-50',
                      borderColor: 'border-red-200'
                    });
                  }
                  
                  if (returnReward.score >= 7) {
                    strengths.push({
                      element: 'Return with Reward',
                      description: 'Presents a clear vision of the better life the hero achieves',
                      color: 'text-green-700',
                      bgColor: 'bg-green-50',
                      borderColor: 'border-green-200'
                    });
                  }
                  
                  if (strengths.length === 0) {
                    return (
                      <div className="text-center py-4">
                        <p className="text-gray-500 text-sm">Focus on improving all Hero's Journey elements to build stronger mythic storytelling.</p>
                      </div>
                    );
                  }
                  
                  return strengths.map((strength, index) => (
                    <div key={index} className={`p-3 rounded-lg border ${strength.bgColor} ${strength.borderColor}`}>
                      <div className="flex items-center mb-1">
                        <span className={`font-semibold text-sm ${strength.color}`}>{strength.element}</span>
                      </div>
                      <p className="text-sm text-gray-600">{strength.description}</p>
                    </div>
                  ));
                })()}
              </div>
            </div>

            {/* Improvements */}
            <div className="card">
              <div className="flex items-center mb-4">
                <div className="w-8 h-8 bg-amber-100 rounded-lg flex items-center justify-center mr-3">
                  <span className="text-amber-600 font-bold">!</span>
                </div>
                <h3 className="text-lg font-semibold text-gray-900">Hero's Journey Improvements</h3>
              </div>
              <div className="space-y-3">
                {(() => {
                  const heroAnalysis = getHeroJourneyAnalysis();
                  const { hero, callToAdventure, mentor, transformation, returnReward } = heroAnalysis;
                  const improvements = [];
                  
                  if (hero.score < 7) {
                    improvements.push({
                      element: 'Hero',
                      description: 'Strengthen the customer-as-hero positioning',
                      recommendation: 'Make the customer the protagonist of the story, not your brand. Show their perspective, struggles, and journey',
                      color: 'text-amber-700',
                      bgColor: 'bg-amber-50',
                      borderColor: 'border-amber-200'
                    });
                  }
                  
                  if (callToAdventure.score < 7) {
                    improvements.push({
                      element: 'Call to Adventure',
                      description: 'Create a stronger call to adventure or challenge',
                      recommendation: 'Present a clear challenge, need, or opportunity that calls the customer to take action',
                      color: 'text-yellow-700',
                      bgColor: 'bg-yellow-50',
                      borderColor: 'border-yellow-200'
                    });
                  }
                  
                  if (mentor.score < 7) {
                    improvements.push({
                      element: 'Mentor/Guide',
                      description: 'Position your brand more clearly as the guide or mentor',
                      recommendation: 'Show how your brand/product appears as the helpful guide that assists the hero on their journey',
                      color: 'text-orange-700',
                      bgColor: 'bg-orange-50',
                      borderColor: 'border-orange-200'
                    });
                  }
                  
                  if (transformation.score < 7) {
                    improvements.push({
                      element: 'Transformation',
                      description: 'Show a clearer transformation process',
                      recommendation: 'Demonstrate how the customer overcomes their challenge using your product/service',
                      color: 'text-red-700',
                      bgColor: 'bg-red-50',
                      borderColor: 'border-red-200'
                    });
                  }
                  
                  if (returnReward.score < 7) {
                    improvements.push({
                      element: 'Return with Reward',
                      description: 'Present a more compelling reward and better life outcome',
                      recommendation: 'Show the specific benefits and improved life the customer achieves after their transformation',
                      color: 'text-green-700',
                      bgColor: 'bg-green-50',
                      borderColor: 'border-green-200'
                    });
                  }
                  
                  if (improvements.length === 0) {
                    return (
                      <div className="text-center py-4">
                        <p className="text-gray-500 text-sm">Great job! All Hero's Journey elements are performing well.</p>
                      </div>
                    );
                  }
                  
                  return improvements.map((improvement, index) => (
                    <div key={index} className={`p-3 rounded-lg border ${improvement.bgColor} ${improvement.borderColor}`}>
                      <div className="flex items-center mb-1">
                        <span className={`font-semibold text-sm ${improvement.color}`}>{improvement.element}</span>
                      </div>
                      <p className="text-sm text-gray-600 mb-2">{improvement.description}</p>
                      <div className="bg-white rounded p-2">
                        <p className="text-xs text-gray-700 font-medium">💡 {improvement.recommendation}</p>
                      </div>
                    </div>
                  ));
                })()}
              </div>
            </div>
          </div>
        </div>
      )}

      {activeTab === 'advanced' && isAdvancedAnalysis && (
        <AdvancedAnalysisResults 
          analysis={review} 
          platform={platform} 
        />
      )}


    </div>
  );
};

export default ReviewResults;
